<template>
  <div
    class="frame-renderer"
    ref="container"
    :style="containerStyle"
    @mousemove="onMouseMove"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @mouseleave="onMouseLeave"
  >
    <transition name="canvas-fade" @after-enter="canvasTransitionComplete = true">
      <div
        v-show="isCanvasReady"
        class="frame-renderer__canvas-wrapper"
        :class="{ 'frame-renderer__canvas-wrapper--hidden': hideCanvas }"
      >
        <canvas ref="canvas" width="400" height="300"></canvas>
      </div>
    </transition>

    <div v-if="debugLighting" class="frame-renderer__render-stat">
      <span :class="renderActive ? 'is-drawing' : 'is-idle'">●</span>
      {{ renderActive ? 'drawing' : 'idle' }} · {{ renderFps }} fps
    </div>

    <div
      v-if="scrollSnapLayout"
      ref="scrollSnapOverlay"
      class="frame-renderer__scroll-snap-overlay"
      :class="{
        'frame-renderer__scroll-snap-overlay--debug': showScrollSnapDebug,
        'frame-renderer__scroll-snap-overlay--inert': turntablePhase !== 'idle',
      }"
      @scroll.passive="onScrollSnapOverlayScroll"
      @wheel.passive="onScrollSnapOverlayWheel"
      @pointerdown.capture="onScrollSnapOverlayPointerDown"
      @scrollsnapchange="onScrollSnapOverlaySnapChange"
    >
      <div
        class="frame-renderer__scroll-snap-track"
        :style="{ width: `${scrollSnapLayout.trackWidth}px`, height: `${scrollSnapLayout.trackHeight}px` }"
      >
        <div
          v-if="showScrollSnapDebug"
          class="frame-renderer__scroll-snap-grid"
        ></div>
        <div
          v-for="cell in scrollSnapLayout.cells"
          :key="cell.key"
          class="frame-renderer__scroll-snap-cell"
          :data-snap-item-key="cell.itemKey"
          :data-snap-mode="cell.mode"
          :class="{
            'frame-renderer__scroll-snap-cell--selected': String(cell.itemId) === String(selectedItemId || ''),
            'frame-renderer__scroll-snap-cell--active-mode': cell.mode === normalizedScrollSnapCameraMode,
          }"
          :style="{ left: `${cell.left}px`, top: `${cell.top}px`, width: `${cell.width}px`, height: `${cell.height}px` }"
        >
          <div
            v-if="showScrollSnapDebug"
            class="frame-renderer__scroll-snap-label"
          >
            {{ cell.label }}
          </div>
        </div>
      </div>
    </div>

    <!-- Rubber-band selection rect -->
    <div
      v-if="lassoRect"
      class="frame-renderer__lasso"
      :style="lassoRectStyle"
    ></div>

    <!-- Dimension / hover tooltip overlay -->
    <div
      v-if="tooltipVisible"
      class="frame-renderer__tooltip"
      :style="tooltipStyle"
    >{{ tooltipText }}</div>

    <div
      v-if="freeLookActive"
      class="frame-renderer__crosshair"
      aria-hidden="true"
    ></div>

    <!-- Loading overlay -->
    <div v-if="loading" class="frame-renderer__loading">
      <div class="spinner-border spinner-border-sm" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Thumbnail overlay for seamless loading -->
    <div
      v-if="shouldRenderThumbnailElement"
      class="centered"
      :class="{ 'frame-renderer__thumbnail-overlay--covering': keepThumbnailOverlay }"
      :style="thumbnailOverlayStyle"
    >
      <img
        :key="thumbnailUrl"
        :src="thumbnailUrl"
        :data-vt-item-visual="transitionItemId"
        alt="Loading preview..."
        class="frame-renderer__thumbnail"
        :style="thumbnailImageStyle"
      />
    </div>

  </div>
</template>

<script>
import * as THREE from 'three'
import { debounce } from 'lodash'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import {
  createResizeHandles,
  createDimensionLines,
  createDimensionLineMaterial,
  createResizeHandleMaterial,
  createTurntableHandleMaterial,
  OVERLAY_HANDLE_RADIUS,
  createSourceHandleMaterials,
  isSharedOverlayMaterial,
  createSizeSnapOverlay,
  createArtworkUvTransformTexture,
  resolveArtworkUvTransformTexture,
  getCropFromScale,
  inches,
} from '@/utils/frameGeometry.js'
import {
  setupShadowRenderer,
  setupPCSS,
  setupSceneEnvironment,
  createWallPlane,
  createRoomGeometry,
  setupSkyEnvironment,
  createSingleFrameLighting,
  createTelephotoCamera,
  computeFitDistance,
  WALL_WIDTH,
  WALL_HEIGHT,
  EYE_LEVEL,
  CEILING_HEIGHT,
  ROOM_HEIGHT,
} from '@/utils/sceneLighting.js'
import {
  CAMERA_MODE_STANDARD,
  CAMERA_MODE_WALL,
  CAMERA_MODE_FREE,
  AUTO_FRAMING_PIECE_PADDING,
  FRAMING_CROP_GUARD_PADDING,
  AUTO_FRAMING_PRESETS,
  selectAutoFramingMode,
  shouldPromoteCameraMode,
  FRAMING_PRESET_BY_MODE,
  getCameraModeRank,
  presetCameraDistance,
  screenSpaceScaleForDepth,
  scaleFovForViewport,
} from '@/utils/designStudioScale.js'
import {
  RESIZE_MODE_SNAP,
  RESIZE_MODE_ASPECT,
  RESIZE_MODE_FREE,
  RESIZE_MODE_ASPECT_FREE,
  clampResizeDimensionsToConstraints,
  resolveResizeDimensions,
} from '@/utils/sizingPolicy.js'
import {
  buildDesignStudioItemObject,
  disposeDesignStudioItemObject,
  updateDesignStudioItemObject,
  applyDesignStudioItemResizeVisual,
  applyDesignStudioItemSourceVisual,
  applyDesignStudioItemMatWidthVisual,
  resolveDesignStudioItemSceneMetrics,
  resolveDesignStudioItemFrontZ,
} from '@/utils/designStudioItemScene.js'
import {
  PROJECT_WALL_GAP_INCHES,
  getProjectWallLayoutFromSceneXs,
  resolveProjectWallRenderItems,
} from '@/utils/designStudioRenderItems.js'
import { resolveWallCollisions } from '@/utils/designStudioCollision.js'

// Draw-rate ceiling for the render loop. 60fps is the smoothness ceiling anyone
// perceives here; above it each extra frame is a whole scene draw competing with
// interaction work on the main thread.
const RENDER_FRAME_BUDGET_MS = 1000 / 60 - 1

// How long to keep drawing after the last thing that could have changed the
// picture. Long enough to cover the scene's own animations (the camera ease and
// the flip are the longest) without each of them having to announce itself.
const RENDER_LINGER_MS = 2500

// Any of these on the renderer's own subtree means the user is doing something,
// which is the largest source of scene changes and the one least worth asking
// each call site to declare. Captured, so `scroll` — which does not bubble —
// still reaches us from the scroll-snap overlay inside.
const RENDER_WAKE_EVENTS = ['pointerdown', 'pointermove', 'pointerup', 'wheel', 'scroll', 'keydown']

const STANDARD_REFERENCE_VIEWPORT_HEIGHT = 900
// Saved thumbnails keep a fixed shape so galleries stay uniform. The live canvas
// no longer has to match it: perspective comes from the camera distance, which
// both share, so a thumbnail and a render of the same item agree regardless of
// how the canvas is shaped.
const THUMBNAIL_EXPORT_ASPECT = 16 / 10
const SNAP_SWITCH_HYSTERESIS = 1.25
const SNAP_ENGAGEMENT_THRESHOLD = 4.0
const RESIZE_CONSTRAINT_VISIBILITY_THRESHOLD = 4.0
const WALL_Z_OFFSET = 2
const SCROLL_SNAP_SETTLE_MS = 220
// Grace before a disabled canvas actually stops drawing. Long enough to outlast
// a host that raises and clears its loading flag inside one task, short enough
// that a real load still parks the loop promptly.
const CANVAS_DISABLE_GRACE_MS = 250
const SCROLL_SNAP_MIN_PX_PER_UNIT = 2
const SCROLL_SNAP_MAX_PX_PER_UNIT = 18
const SCROLL_SNAP_EDGE_OVERSCROLL_PX = 2048
const CAMERA_CONTROLS_SCROLL = 'scroll'
const CAMERA_CONTROLS_ORBIT = 'orbit'
// Orbit bounds. The scene is a piece on a wall in a room, so the interesting
// viewpoints are all in front of that wall and inside that room: swinging
// behind it shows the back of a plane, and sinking below the floor or rising
// through the ceiling reads as a broken camera rather than a camera move.
// Distances are relative to whatever the framing preset fitted, so the same
// bounds hold for a postcard and for a mural.
const ORBIT_MAX_AZIMUTH_DEG = 72
const ORBIT_MIN_POLAR_DEG = 52   // ~38 degrees above eye level
const ORBIT_MAX_POLAR_DEG = 112  // ~22 degrees below eye level
// The height band the camera is kept in, in scene inches off the floor. Ankle
// height to a head's clearance under the ceiling: the room is a floor, a
// ceiling and a wall backdrop, and outside this band the view is either
// underfoot or poking through the ceiling into dead space above it.
const ORBIT_MIN_CAMERA_Y = 10
const ORBIT_MAX_CAMERA_Y = ROOM_HEIGHT - 12
// Dolly range, as multiples of two fixed anchors rather than of wherever the
// camera happens to be: the distance at which the piece exactly fills the
// viewport (the near anchor) and the loosest framing preset (the far one). Tied
// to the current preset instead, zooming would mean something different at
// standard than at wide — you could inspect a mat from standard but not from
// wide, on the same piece.
const ORBIT_MIN_DISTANCE_SCALE = 0.25
const ORBIT_MAX_DISTANCE_SCALE = 1
const ORBIT_MIN_DISTANCE_INCHES = 8
// How far the target may be panned away from the fitted centre, as a multiple
// of the piece's largest dimension. Bounded so panning cannot strand the orbit
// pivot somewhere the piece is no longer on screen.
const ORBIT_PAN_EXTENT_SCALE = 1.5
const ORBIT_DAMPING_FACTOR = 0.08
// How long the orbit has to hold still before the framing-preset selector is
// re-read from it. Matched to the scroll overlay's settle for the same reason:
// crossing a band boundary mid-gesture is not a choice, stopping past it is.
const ORBIT_MODE_SETTLE_MS = 220
const WALL_DRAG_SNAP_THRESHOLD_INCHES = 4
// Per-frame fraction neighbors glide toward their collision target. < 1 makes them flow smoothly
// (and arc through axis changes) instead of snapping, for a softer "items pushing past" feel.
const WALL_COLLISION_DISPLAY_EASE = 0.15
// --- Turntable ------------------------------------------------------------
//
// The piece comes off the wall and becomes an object you can turn over in your
// hands. Rotation is the object's, not the camera's: the room stays put and
// dims, so what moves is the one thing being looked at.
//
// Horizontal travel is a full turn and vertical travel is half of one, which is
// exactly the sphere OrbitControls describes by default — hence no azimuth or
// polar limits anywhere below.
//
// How far the room is knocked back behind the scrim. Blending happens on the
// encoded framebuffer, so this reads roughly as "the room keeps 45% of its
// lightness" — dark enough that the piece is plainly the subject, light enough
// that the room is still a room and you can see where the piece hangs.
const TURNTABLE_DIM_OPACITY = 0.55
const TURNTABLE_DIM_FADE_MS = 260
// Pose transitions are priced per half turn of travel, not per move, so a
// reversal costs what it has left to undo. The way back to the wall is brisker
// than a flip: it is the end of an interaction, not part of one.
const TURNTABLE_TURN_MS_PER_PI = 1500
const TURNTABLE_RETURN_MS_PER_PI = 900
// How fast the piece travels back onto the wall, when that is the slower half
// of the return — a piece already near square still has its whole stand-off to
// cross, and rotation time alone would not pay for it.
const TURNTABLE_RETURN_INCHES_PER_SEC = 20
// The click-spin's velocity profile, in radians of travel: leave the wall at a
// definite pace, wind up to full speed as the piece comes side-on, then shed it
// into a steady drift that carries on until someone takes hold. The launch speed
// is not the drift speed — a spin that starts at the rate it ends at spends its
// first half-second looking like nothing has happened.
const TURNTABLE_SPIN_LAUNCH_RAD_PER_S = 1.4
const TURNTABLE_SPIN_PEAK_RAD_PER_S = 5
const TURNTABLE_SPIN_IDLE_RAD_PER_S = 0.35
const TURNTABLE_SPIN_PEAK_AT = THREE.MathUtils.degToRad(90)
const TURNTABLE_SPIN_SETTLE_AT = THREE.MathUtils.degToRad(165)
// Drag sensitivity: about a full turn across 700px of travel, matched to
// TURNTABLE_ROTATE_SPEED so the gesture does not change gear when OrbitControls
// takes over on release.
const TURNTABLE_DRAG_RADIANS_PER_PX = (Math.PI * 2) / 700
// Pointer travel below which a press is a click (spin once) rather than a drag.
const TURNTABLE_CLICK_SLOP_PX = 4
// How far the handle's edge lifts under a hover, in inches. A distance rather
// than an angle: the same few degrees is a shrug on a postcard and a heave on a
// mural, and what should read the same across pieces is how far the edge comes
// away from the wall.
const TURNTABLE_PEEK_INCHES = 0.25
const TURNTABLE_ROTATE_SPEED = 1
const TURNTABLE_DAMPING_FACTOR = 0.12
// The rig camera's distance from its target. Arbitrary — only the direction is
// read back — but it has to be far enough that OrbitControls' own pan/zoom
// guards never fire.
const TURNTABLE_RIG_RADIUS = 100
// Daylight held between the piece and the wall once it is off it, so a turn
// never looks like it is grazing the plaster.
const TURNTABLE_AIR_GAP_INCHES = 1
// How close the wheel may bring the camera to the piece's front face.
const TURNTABLE_MIN_DOLLY_INCHES = 12
const THREE_UP = new THREE.Vector3(0, 1, 0)
const THREE_RIGHT = new THREE.Vector3(1, 0, 0)
const PLAYER_SHADOW_MIN_HALF_EXTENT = 80
const PLAYER_SHADOW_MAX_HALF_EXTENT = 880
const PLAYER_SHADOW_REFERENCE_DISTANCE = 96
const PLAYER_SHADOW_OFFSET_XY_SCALE = 1.5
const PLAYER_SHADOW_CAMERA_FAR = 260
/**
 * When exporting a thumbnail, fit the full outer frame in the frustum with minimal extra slack.
 * Keep this at ~1 so we pull back only as far as required—larger values increase Z and exaggerate
 * perspective change vs the live camera.
 */
const THUMBNAIL_EXPORT_OUTER_PADDING = 1.01

/** `framing` prop values — see the prop's doc comment. */
const FRAMING_AUTO = 'auto'
const FRAMING_PIECE = 'piece'

/**
 * Breathing room around the piece under `framing="piece"`, as a multiple of the
 * outer frame. Thumbnails crop flush to the frame and lose the drop shadow;
 * a live render has room to show it, so this sits well above the ~1.01 the
 * thumbnail export uses.
 */
const PIECE_FRAMING_PADDING = 1.2

/**
 * The window over which changes coalesce into one rebuild, on the trailing
 * edge: a held arrow key builds the item it lands on rather than each one it
 * passes, and an edit that trips several prop watchers in a tick costs one
 * rebuild rather than one per watcher.
 *
 * Exported because it also defines what counts as switching faster than the
 * scene can follow — see DsItemRenderer, which shows the thumbnail for a switch
 * arriving within this of the last one. That is the same question as whether
 * the rebuild has had time to run, so it should stay the same number.
 */
export const REBUILD_DEBOUNCE_MS = 150

export default {
  name: 'FrameRenderer',

  props: {
    /** Substrate name (e.g. 'Print', 'Canvas') - affects backing paper, stretchers */
    substrate: {
      type: String,
      default: '',
    },
    /** Artwork width in inches */
    artWidth: {
      type: [Number, String],
      default: 16,
    },
    /** Artwork height in inches */
    artHeight: {
      type: [Number, String],
      default: 20,
    },
    artworkScaleX: {
      type: [Number, String],
      default: 1,
    },
    artworkScaleY: {
      type: [Number, String],
      default: 1,
    },
    artworkRotation: {
      type: [Number, String],
      default: 0,
    },
    artworkOffsetLeft: {
      type: [Number, String],
      default: 0,
    },
    artworkOffsetTop: {
      type: [Number, String],
      default: 0,
    },
    /** Deterministic target finished width in inches for fit/sizing. */
    targetWidth: {
      type: [Number, String],
      default: null,
    },
    /** Deterministic target finished height in inches for fit/sizing. */
    targetHeight: {
      type: [Number, String],
      default: null,
    },
    /** When true, render a built-in 1:1 checker texture for debugging aspect/UVs. */
    debugChecker: {
      type: Boolean,
      default: false,
    },
    /** When true, show 3D lighting debug helpers overlaid on the scene. */
    debugLighting: {
      type: Boolean,
      default: false,
    },
    /** When true, show scroll snap cells/centers over the renderer. */
    debugScroll: {
      type: Boolean,
      default: false,
    },
    /** URL of the artwork image */
    artworkImageUrl: {
      type: String,
      default: null,
    },
    artworkFilter: {
      type: Object,
      default: null,
    },
    /**
     * Frame option extra_data from the available-options API.
     * Uses top_image for texturing. Expected shape: { top_image, width, rabbet, ... }
     * Pass null for no frame.
     */
    frameData: {
      type: Object,
      default: null,
    },
    /**
     * Mat option extra_data.
     * Expected shape: { color, ... }
     * Pass null for no mat.
     */
    matData: {
      type: Object,
      default: null,
    },
    /** Multi-mat: array of { color } for Mat 1, Mat 2, Mat 3 (outer to inner) */
    matDataArray: {
      type: Array,
      default: null,
    },
    /** Mat border width in inches. Object { top, bottom, left, right } or single number. */
    matWidth: {
      type: [Number, String, Object],
      default: 2,
    },
    /** Multi-mat: per-mat widths [{ top, bottom, left, right }, ...] */
    matWidthsArray: {
      type: Array,
      default: null,
    },
    /**
     * Linen liner width per side, in inches (Canvas only; 1 or 2, 0 = none).
     * A fabric-covered liner between the artwork and the frame — it enlarges
     * the finished piece by this width on all four sides, as legacy does.
     */
    linenLiner: {
      type: [Number, String],
      default: 0,
    },
    /** Container max width in CSS (e.g. '100%', '600px') */
    maxWidth: {
      type: String,
      default: '100%',
    },
    /** Container max height in CSS */
    maxHeight: {
      type: String,
      default: '500px',
    },
    /** Whether interactive features (hover, resize) are enabled */
    interactive: {
      type: Boolean,
      default: true,
    },
    wallDragEnabled: {
      type: Boolean,
      default: false,
    },
    /** When true, wall items are kept from overlapping (push-apart collision avoidance). */
    collisionEnabled: {
      type: Boolean,
      default: false,
    },
    resizeMode: {
      type: String,
      default: RESIZE_MODE_SNAP,
    },
    resizeAspectRatio: {
      type: Number,
      default: null,
    },
    resizeSnapCandidates: {
      type: Array,
      default: () => [],
    },
    resizeConstraints: {
      type: Array,
      default: () => [],
    },
    resizeOverlayConstraints: {
      type: Array,
      default: null,
    },
    resizeSnapOverlayVisible: {
      type: Boolean,
      default: false,
    },
    /** Background color of the scene */
    backgroundColor: {
      type: String,
      default: '#f0f0f0',
    },
    /**
     * Hang option data for back-of-frame hardware visualization.
     * Expected shape: { name: 'wire' | 'security hardware' | ... }
     */
    hangData: {
      type: Object,
      default: null,
    },
    /** Explicit stretcher depth in inches (Canvas only); the scene supplies a mount-appropriate default when absent. */
    stretcherDepth: {
      type: [Number, String],
      default: 0,
    },
    /** Canvas mount style: "Gallery Wrapped", "Frame", or "Float Frame" */
    canvasMount: {
      type: String,
      default: '',
    },
    /** Extra data from Float Frame option (has width for frame face) */
    floatFrameData: {
      type: Object,
      default: null,
    },
    /** Substrate back panel: { depth, color, roughness, metalness } for Aluminum, Acrylic, etc. */
    substrateBackData: {
      type: Object,
      default: null,
    },
    /** Acoustic panel option: print artwork on rear face with black edge band between (no plain back). */
    acousticTwoSidedChangeable: {
      type: Boolean,
      default: false,
    },
    /** Whether to show a wall backdrop behind the frame */
    wallEnabled: {
      type: Boolean,
      default: true,
    },
    /** Wall color (CSS string or hex number) */
    wallColor: {
      type: [String, Number],
      default: '#ffffff',
    },
    /** URL of a pre-rendered thumbnail to show while loading */
    thumbnailUrl: {
      type: String,
      default: null,
    },
    /** Stable key for the current preview item; changes reset thumbnail-to-canvas fade state. */
    thumbnailKey: {
      type: [String, Number],
      default: null,
    },
    /** Whether the parent is still fetching configuration or options data */
    awaitingConfig: {
      type: Boolean,
      default: false,
    },
    /** Item id exposed on the retained still for shared-item transitions. */
    transitionItemId: {
      type: [String, Number],
      default: null,
    },
    /** Whether Three.js canvas/rendering should be initialized */
    canvasEnabled: {
      type: Boolean,
      default: true,
    },
    /** Whether canvas layer should be visually hidden */
    hideCanvas: {
      type: Boolean,
      default: false,
    },
    /**
     * Hold the thumbnail overlay up even once the canvas is ready. Lets a
     * caller un-hide the canvas and wait out its fade before dropping the
     * still image underneath it, instead of cutting to a blank frame.
     */
    keepThumbnailOverlay: {
      type: Boolean,
      default: false,
    },
    /**
     * Keep a hidden, correctly-sized thumbnail in the DOM after the canvas is
     * ready. The route transition orchestrator can reveal it synchronously for
     * the old-state snapshot; Vue cannot mount a new source after that capture.
     */
    retainThumbnailElement: {
      type: Boolean,
      default: false,
    },
    /** When true, postpone heavy rebuild/texture work until resumed */
    suspendHeavyWork: {
      type: Boolean,
      default: false,
    },
    /** When true, suppress camera refit during rebuilds (option hover preview) */
    previewMode: {
      type: Boolean,
      default: false,
    },
    /** Camera mode: standard, wall, or free */
    cameraMode: {
      type: String,
    },
    /**
     * Which interaction model moves the camera while `interactive`.
     *
     * 'scroll' (default) — the scroll-snap overlay: horizontal scrolling walks
     * the project wall item by item and vertical scrolling walks the framing
     * presets, with the camera following the overlay's snap positions. Built
     * for the design studio, where the camera is a way of moving through a
     * wall of items.
     *
     * 'orbit' — a plain three.js OrbitControls: drag to orbit, wheel to dolly,
     * right-drag to pan, bounded to viewpoints in front of the wall. Built for
     * the browse item viewer, where there is one piece and the camera is just
     * a way of looking at it. Framing presets still choose where the camera
     * starts for each item (see autoFramingAllowed); after that the user owns
     * it.
     */
    cameraControls: {
      type: String,
      default: CAMERA_CONTROLS_SCROLL,
      validator: value => value === CAMERA_CONTROLS_SCROLL || value === CAMERA_CONTROLS_ORBIT,
    },
    /**
     * How the camera frames the scene.
     *
     * 'auto' (default) — room-scale framing presets: the camera sits at eye
     * level a fixed distance back, so a piece looks the size it would look in
     * a room. Every existing caller wants this.
     *
     * 'piece' — fill the viewport with the outer frame, centered on the piece
     * rather than on eye level. This is the framing `exportThumbnail` crops to,
     * so a live render matches the item's saved thumbnail and the two can be
     * cross-faded or morphed into each other.
     */
    framing: {
      type: String,
      default: FRAMING_AUTO,
      validator: value => value === FRAMING_AUTO || value === FRAMING_PIECE,
    },
    /** Room around the piece under `framing="piece"`, as a multiple of the outer frame. */
    piecePadding: {
      type: Number,
      default: PIECE_FRAMING_PADDING,
    },
    /**
     * Optional decor asset: a GLB you can place in the scene for scale/context
     * (plant, furniture, full room scene, etc).
     *
     * Scene units are inches; by default we auto-scale the GLB to the desired height (inches)
     * and drop it onto the floor (y=0).
     */
    decorEnabled: {
      type: Boolean,
      default: false,
    },
    decorUrl: {
      type: String,
      default: '/monstera_deliciosa_potted_mid-century_plant.glb',
    },
    decorHeightInches: {
      type: [Number, String],
      default: 48,
    },
    decorOffsetX: {
      type: [Number, String],
      default: -40,
    },
    /**
     * Distance in front of the wall (inches). Wall is behind z=0; positive z moves toward camera.
     */
    decorFromWall: {
      type: [Number, String],
      default: 14,
    },
    /** When true, add floor, ceiling, side walls, and a window to the scene. */
    roomEnabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Optional saved/base multi-item scene specs. Selection and live selected props
     * are layered on via selectedItemId/selectedRendererProps.
     */
    renderItems: {
      type: Array,
      default: null,
    },
    /** Current selected project item id; selection metadata is resolved separately from base render specs. */
    selectedItemId: {
      type: [Number, String],
      default: null,
    },
    /**
     * Multi-selection: 2+ ids highlight each matching item and suppress all
     * single-item interaction affordances. 0-1 ids defer to selectedItemId.
     */
    selectedItemIds: {
      type: Array,
      default: null,
    },
    /** Item id that selectedRendererProps belongs to; stale live props are ignored when ids differ. */
    selectedRendererItemId: {
      type: [Number, String],
      default: null,
    },
    /** Live renderer props for the selected item, applied only when selectedRendererItemId matches. */
    selectedRendererProps: {
      type: Object,
      default: null,
    },
    /** Monotonic rotate request for animating selected item orientation after dimensions swap. */
    rotateAnimationRequest: {
      type: Object,
      default: null,
    },
    /** Horizontal gap between project wall items, in inches. */
    renderItemGapInches: {
      type: [Number, String],
      default: PROJECT_WALL_GAP_INCHES,
    },
  },

  data() {
    return {
      loading: false,
      showThumbnail: true,
      // 3D debug readout only; see trackRenderRate.
      renderFps: 0,
      renderActive: false,
      hasInitialFit: false,
      // Bumped on every camera fit so size-derived computeds re-evaluate
      cameraFitVersion: 0,
      canvasTransitionComplete: false,
      tooltipVisible: false,
      tooltipText: '',
      tooltipX: 0,
      tooltipY: 0,
      // Rubber-band (lasso) selection: client-space rect + container origin
      lassoRect: null,
      lassoOrigin: null,
      isDragging: false,
      dragHandle: null,
      dragStartPos: null,
      dragStartDims: null,
      dragWidth: null,
      dragHeight: null,
      dragMode: null, // 'resize' | 'source-move' | 'source-scale'
      // 'idle' | 'peeling' | 'spinning' | 'engaged' | 'returning'. Reactive
      // because the scroll-snap overlay has to stop eating the pointer for the
      // duration — see the template.
      turntablePhase: 'idle',
      freeLookActive: false,
      scrollSnapLayout: null,
    }
  },

  /**
   * Three.js objects are stored directly on the instance (not in data())
   * to avoid Vue reactivity overhead and the _-prefix reservation rule.
   */
  created() {
    // Every prop that can change what the scene looks like should trigger a
    // draw. Watching each one individually (shallow, so it fires exactly when a
    // prop reference changes — the granularity props change at) means the
    // on-demand loop cannot miss a prop-driven change, and no future prop has to
    // remember to opt in.
    Object.keys(this.$options.props || {}).forEach((prop) => {
      this.$watch(prop, () => this.markNeedsRender())
    })
    this._renderUntil = 0
    this.threeRenderer = null
    this.threeScene = null
    this.threeCamera = null
    this.threeRaycaster = null
    this.threeMouse = null
    this.animFrameId = null
    this.handlesGroup = null
    this.dimGroup = null
    this.sizeSnapOverlayGroup = null
    this.wallSnapOverlayGroup = null
    this.interactionGroup = null
    this.wallMesh = null
    this.sceneLights = []
    this.builtDims = null
    this.hoveredObject = null
    this.pendingDragDims = null
    this.dragRafId = null
    this.cameraAnimRafId = null
    this._autoFramingRecheckPending = false
    this._interactionAimPoint = null
    this.lastDragEvent = null
    this.lastEmittedDims = null // { width, height } - avoid duplicate emits
    this._formResizePreviewActive = false
    this.pendingSourceTransform = null
    this.sourceDragRafId = null
    this.sourceGhostMesh = null
    this.sourceGhostFadeId = null
    // What the flip has been *asked* to show, which is not the same as what it
    // is showing mid-turn. Reversing a flip that is still running has to read
    // the request, or an open-then-close of the hang menu leaves the piece
    // facing whichever way the turn happened to reach.
    this._flipTargetBack = false
    // The piece's orientation away from the wall. Plain object rather than
    // data(): it is written every frame from the render loop, and nothing
    // renders off it in the template.
    this._pose = { yaw: 0, pitch: 0 }
    this._poseAnim = null
    this._poseOffWall = false
    this._posePeeking = false
    this._turntableHandleMesh = null
    // Per-piece share of the flip, recomputed each frame — see getPoseShares.
    this._poseShares = new Map()
    this._dimValue = 0
    this._dimTarget = 0
    this._turntableDrag = null
    this._turntablePress = null
    this._turntableControls = null
    this._turntableRig = null
    this._turntableViewControls = null
    this._turntableDimScene = null
    this._turntableDimQuad = null
    this._turntableDimCamera = null
    this._poseScratch = {
      quat: new THREE.Quaternion(),
      yawQuat: new THREE.Quaternion(),
      pitchQuat: new THREE.Quaternion(),
      spherical: new THREE.Spherical(),
      euler: new THREE.Euler(),
      matrix: new THREE.Matrix4(),
    }
    this._boundTurntableChange = () => this.markNeedsRender()
    this._boundTurntableInteract = () => this.onTurntableInteract()
    this._boundTurntableDragMove = (e) => this._onTurntableDragMove(e)
    this._boundTurntableDragEnd = () => this._onTurntableDragEnd()
    this._boundTurntablePressEnd = (e) => this._onTurntablePressEnd(e)
    this.itemRotationOffsetZ = 0
    this.itemRotationAnimation = null
    this.itemRotationAnimationRafId = null
    this.pendingItemRotationAnimation = null

    // Room geometry (floor, ceiling, side walls, window)
    this._roomMeshes = []
    this._roomLights = []
    this._skyMesh = null

    // Decor GLB (optional)
    this._decorLoader = null
    this._decorObject = null
    this._decorLoadPromise = null
    this._wallZOffset = WALL_Z_OFFSET
    this._pointerControls = null
    this._orbitControls = null
    this._orbitModeSettleTimer = null
    this._boundOrbitChange = () => this.onOrbitControlsChange()
    this._freeLookKeys = new Set()
    this._boundFreeLookKeyDown = this._onFreeLookKeyDown.bind(this)
    this._boundFreeLookKeyUp = this._onFreeLookKeyUp.bind(this)
    this._boundPointerUnlock = this._onPointerUnlock.bind(this)
    this._freeLookVelocity = { forward: 0, right: 0, vertical: 0 }
    this._freeLookOnGround = true
    this._freeLookFloorY = 0
    this._freeLookBobTime = 0
    this._freeLookBobAmplitude = 0
    this._freeLookJumpConsumed = false
    this._freeLookLandDip = 0
    this._freeLookLandDipVel = 0
    this._freeLookPhysicsY = 0
    this._freeLookAudioCtx = null
    this._grabbed = null
    this._shiftLockedAspect = null
    this.activeWallSnap = { x: false, y: false }
    this.sizeSnapOverlayOpacity = 0
    this.sizeSnapOverlayTargetOpacity = 0
    this.wallSnapOverlayOpacity = 0
    this.wallSnapOverlayTargetOpacity = 0
    this.resizeSnapAnimation = null
    this.activeResizeSnap = null
    this.activeResizeConstraint = null
    this._activeResizeConstraintMessageKeys = new Set()
    this.pendingResizeIntent = null
    this.hoveredIntersectionPoint = null
    this._renderItemInstances = new Map()
    this._renderItemsSyncVersion = 0
    this._selectedRenderKey = null
    this._dynamicWallLayout = null
    this._playerShadowState = null
    this._shadowCameraHelper = null
    this._shadowCameraHelperLight = null
    this._suppressFlipForThumbnail = false
    this._suppressNextCameraModeFit = false
    this._syncingScrollSnapOverlay = false
    this._scrollSnapSyncRafId = null
    this._scrollSnapSettleTimer = null
    this._scrollSnapSelectionFitPending = false
    this._scrollSnapOverlayAuthoritative = false
    this._scrollSnapLayoutSignature = null
    // Key of the item an instant jump put the overlay on, awaiting its fit,
    // and every jump target still awaiting reconciliation — a burst of jumps in
    // one task is reported by the browser a frame late, naming targets the
    // overlay has already left.
    this._scrollSnapInstantFitKey = null
    this._scrollSnapInstantJumpKeys = new Set()
    this._renderLoopStopTimer = null
  },

  computed: {
    containerStyle() {
      return {
        width: '100%',
        maxWidth: this.maxWidth,
        height: this.maxHeight,
        maxHeight: this.maxHeight,
        position: 'relative',
        overflow: 'hidden',
      }
    },
    tooltipStyle() {
      return {
        left: this.tooltipX + 'px',
        top: this.tooltipY + 'px',
      }
    },
    lassoRectStyle() {
      if (!this.lassoRect || !this.lassoOrigin) return {}
      return {
        left: `${this.lassoRect.x - this.lassoOrigin.x}px`,
        top: `${this.lassoRect.y - this.lassoOrigin.y}px`,
        width: `${this.lassoRect.w}px`,
        height: `${this.lassoRect.h}px`,
      }
    },
    hasRenderItemSpecs() {
      return this.getRenderSpecs().length > 0
    },
    /**
     * Orbit and the scroll-snap overlay are alternatives, not layers: the
     * overlay sits above the canvas and eats the pointer, so nothing else can
     * drive the camera while it exists. Everything orbit-specific keys off this
     * one flag, and everything scroll-specific falls away because
     * buildScrollSnapLayout returns null.
     */
    orbitEnabled() {
      return this.interactive && this.cameraControls === CAMERA_CONTROLS_ORBIT
    },
    showScrollSnapDebug() {
      return Boolean(this.debugScroll)
    },
    normalizedScrollSnapCameraMode() {
      return FRAMING_PRESET_BY_MODE.has(this.cameraMode) ? this.cameraMode : CAMERA_MODE_STANDARD
    },
    parsedArtWidth() {
      if (this.dragWidth != null) return this.dragWidth
      return parseFloat(this.artWidth) || 16
    },
    parsedArtHeight() {
      if (this.dragHeight != null) return this.dragHeight
      return parseFloat(this.artHeight) || 20
    },
    parsedArtworkScaleX() {
      const n = parseFloat(this.artworkScaleX)
      if (!Number.isFinite(n) || n === 0) return 1
      return Math.abs(n) >= 1 ? n : (n < 0 ? -1 : 1)
    },
    parsedArtworkScaleY() {
      const raw = this.artworkScaleY != null ? this.artworkScaleY : this.artworkScaleX
      const n = parseFloat(raw)
      if (!Number.isFinite(n) || n === 0) return 1
      return Math.abs(n) >= 1 ? n : (n < 0 ? -1 : 1)
    },
    parsedArtworkRotation() {
      const n = parseInt(this.artworkRotation, 10)
      if (![0, 90, 180, 270].includes(n)) return 0
      return n
    },
    parsedArtworkOffsetLeft() {
      const n = parseFloat(this.artworkOffsetLeft)
      return Number.isFinite(n) ? n : 0
    },
    parsedArtworkOffsetTop() {
      const n = parseFloat(this.artworkOffsetTop)
      return Number.isFinite(n) ? n : 0
    },
    frameWidth() {
      return this.frameData ? parseFloat(this.frameData.width) || 0 : 0
    },
    /** Frame depth: match frame width up to 2", default to 1.5" */
    frameDepth() {
      const w = this.frameWidth
      return w > 0 ? Math.min(2, w) : 1.5
    },
    frameRabbet() {
      return this.frameData ? parseFloat(this.frameData.rabbet) || 0 : 0
    },
    parsedMatWidth() {
      const hasMat = this.matData || (this.matDataArray && this.matDataArray.length > 0)
      if (!hasMat) return { top: 0, bottom: 0, left: 0, right: 0 }
      if (typeof this.matWidth === 'object' && this.matWidth !== null) {
        return {
          top: parseFloat(this.matWidth.top) || 0,
          bottom: parseFloat(this.matWidth.bottom) || 0,
          left: parseFloat(this.matWidth.left) || 0,
          right: parseFloat(this.matWidth.right) || 0,
        }
      }
      const w = parseFloat(this.matWidth) || 0
      return { top: w, bottom: w, left: w, right: w }
    },
    parsedMatDepthsArray() {
      const matArr = this.matDataArray || (this.matData ? [this.matData] : [])
      const widthsArr = this.matWidthsArray || (this.hasMatWidth ? [this.parsedMatWidth] : [])
      const count = Math.max(matArr.length, widthsArr.length)
      return Array.from({ length: count }, (_, idx) => {
        const matDepth = parseFloat(matArr[idx]?.depth)
        return Number.isFinite(matDepth) && matDepth > 0 ? matDepth : 0.0625
      })
    },
    totalMatDepth() {
      if (!this.parsedMatDepthsArray.length) return 0
      return this.parsedMatDepthsArray.reduce((sum, depth) => sum + (parseFloat(depth) || 0), 0)
    },
    /** Max mat width for any side — used for frame offset calculations */
    maxMatWidth() {
      const m = this.parsedMatWidth
      return Math.max(m.top, m.bottom, m.left, m.right)
    },
    hasMatWidth() {
      const m = this.parsedMatWidth
      return m.top > 0 || m.bottom > 0 || m.left > 0 || m.right > 0
    },
    /** Calculates the maximum Z coordinate (closest to camera) of the generated 3D geometry */
    /**
     * The piece's front face, derived from props rather than from built
     * geometry so it is known before a rebuild — including during a switch,
     * when the built value still belongs to the piece being replaced. Same
     * rule the builder hangs the group by.
     */
    frontZ() {
      return resolveDesignStudioItemFrontZ({
        rendererProps: this.buildScalarRenderSpec().rendererProps,
      })
    },
    isCanvasReady() {
      if (this.thumbnailUrl) {
        return !this.showThumbnail
      }
      return this.hasInitialFit && !this.awaitingConfig
    },
    showTransitionOverlay() {
      if (!this.thumbnailUrl) return false
      return !this.canvasTransitionComplete
        || this.keepThumbnailOverlay
    },
    /**
     * Whether a size change should rebuild the scene there and then, rather
     * than leaving it to the debounce.
     *
     * Resizing the piece on screen should follow the handles immediately. An
     * item swap arrives as a size change too, but there the thumbnail is
     * covering and the scene underneath is not being looked at — so building it
     * only to replace it on the next keypress is work nobody sees. Holding an
     * arrow key was building a scene per item that way, which is why the
     * shadows kept appearing; the debounce then builds the one landed on.
     */
    shouldSyncSizeImmediately() {
      return !this.selectedRendererProps && !this.isDragging && !this.keepThumbnailOverlay
    },
    shouldRenderThumbnailElement() {
      return this.showTransitionOverlay || Boolean(this.retainThumbnailElement && this.thumbnailUrl)
    },
    thumbnailOverlayStyle() {
      return {
        viewTransitionName: 'none',
      }
    },
    thumbnailImageStyle() {
      // The projected size is derived from the Three.js camera and the
      // container's measured box, neither of which Vue can track. Without this
      // read the computed caches whatever it saw first — typically null, before
      // the camera existed — and the overlay never picks up its real size.
      void this.cameraFitVersion

      const style = {}

      // Retained thumbnails sit behind the live canvas and remain available as
      // synchronous transition sources without affecting the rendered scene.
      if (!this.showTransitionOverlay) style.visibility = 'hidden'

      const fitSize = this.getThumbnailDisplaySize()
      if (fitSize) {
        style.width = `${fitSize.width}px`
        style.height = `${fitSize.height}px`
        // Fill projected size; if source ratio is slightly off, crop instead of scale-clamping.
        style.objectFit = 'cover'
      } else {
        // No camera yet, so no projected size. The image would lay itself out at
        // its intrinsic size — far larger than the piece — and flash before the
        // first fit corrects it.
        style.visibility = 'hidden'
      }

      return style
    },
  },

  watch: {
    isCanvasReady(val) {
      if (!val) {
        this.canvasTransitionComplete = false
      }
    },
    artWidth() {
      if (this._formResizePreviewActive) return
      if (this.shouldSyncSizeImmediately) this.syncRenderItems()
      if (!this.isDragging) this.debouncedRebuild()
    },
    artHeight() {
      if (this._formResizePreviewActive) return
      if (this.shouldSyncSizeImmediately) this.syncRenderItems()
      if (!this.isDragging) this.debouncedRebuild()
    },
    dragWidth(val) { if (val != null && !this.selectedRendererProps && !this.isDragging) this.syncRenderItems() },
    dragHeight(val) { if (val != null && !this.selectedRendererProps && !this.isDragging) this.syncRenderItems() },
    resizeMode() { if (this.isDragging && this.lastDragEvent) this.handleDrag(this.lastDragEvent) },
    resizeAspectRatio() { if (this.isDragging && this.lastDragEvent) this.handleDrag(this.lastDragEvent) },
    artworkImageUrl() { this.debouncedRebuild() },
    artworkFilter: { handler() { this.debouncedRebuild() }, deep: true },
    substrate() { this.debouncedRebuild() },
    canvasMount() { this.debouncedRebuild() },
    floatFrameData: { handler() { this.debouncedRebuild() }, deep: true },
    substrateBackData: { handler() { this.debouncedRebuild() }, deep: true },
    frameData: { handler() { this.debouncedRebuild() }, deep: true },
    matData: { handler() { this.syncMatVisualState() }, deep: true },
    matDataArray: { handler() { this.syncMatVisualState() }, deep: true },
    matWidthsArray: { handler() { this.debouncedRebuild() }, deep: true },
    matWidth() { this.debouncedRebuild() },
    linenLiner() { this.debouncedRebuild() },
    hangData: { handler() { this.debouncedRebuild() }, deep: true },
    stretcherDepth() { this.debouncedRebuild() },
    backgroundColor() { this.updateBackground() },
    wallEnabled() { this.updateWall({ updateRoom: true }) },
    wallColor() { this.updateWall() },
    roomEnabled() { this.updateRoom() },
    decorEnabled() { this.updateDecor() },
    decorUrl() { this.updateDecor(true) },
    decorHeightInches() { this.positionDecor() },
    decorOffsetX() { this.positionDecor() },
    decorFromWall() { this.positionDecor() },
    orbitEnabled(enabled) {
      if (enabled) {
        this.initOrbitControls()
      } else {
        this.destroyOrbitControls()
      }
      // The scroll overlay and orbit are mutually exclusive, so whichever one
      // just went away leaves the other to be (re)built.
      this.$nextTick(() => this.refreshScrollSnapOverlayLayout())
    },
    cameraMode(newMode) {
      if (!this.threeRenderer) return
      if (newMode === CAMERA_MODE_FREE) {
        // Enter free-look directly on the user gesture call stack (no nextTick),
        // so Pointer Lock can be granted immediately.
        this.enableFreeLook()
        return
      }
      this.disableFreeLook()
      this.syncOrbitInteraction()
      if (!this._suppressNextCameraModeFit) {
        this._scrollSnapOverlayAuthoritative = false
      }
      if (this._suppressNextCameraModeFit) {
        this._suppressNextCameraModeFit = false
        return
      }
      this.$nextTick(() => {
        if (!this.threeRenderer) return
        // Under the overlay the framing preset *is* a scroll position, so move
        // it there and let the scroll carry the camera. Animating the camera on
        // its own instead left the overlay showing the old row until the ease
        // finished, and anything reading it in between got the old answer.
        //
        // Only once the scene has been fitted, though: before that the overlay
        // has never been placed, so it is still at the far left, and the
        // settle this schedules would read the item sitting there as a choice
        // and navigate to it — the first item in the project, every load.
        if (this.hasInitialFit && this.scrollSnapOverlayToCameraMode(newMode, { behavior: 'smooth' })) return
        if (this.hasInitialFit) {
          this.animateFitCamera()
        } else {
          this.fitCamera()
        }
      })
    },
    awaitingConfig(newVal) {
      if (!newVal) {
        // As soon as the config finishes loading, trigger a rebuild to ensure we drop the overlay
        // and finish the layout with the new final options.
        if (this.debouncedRebuild) {
          this.debouncedRebuild()
        }
      }
    },
    artworkScaleX() { if (!this.isDragging) this.debouncedRebuild() },
    artworkScaleY() { if (!this.isDragging) this.debouncedRebuild() },
    artworkRotation() { if (!this.isDragging) this.debouncedRebuild() },
    artworkOffsetLeft() { if (!this.isDragging) this.debouncedRebuild() },
    artworkOffsetTop() { if (!this.isDragging) this.debouncedRebuild() },
    debugChecker() { this.debouncedRebuild() },
    debugLighting() { this.updateShadowCameraHelper() },
    canvasEnabled(newVal) {
      if (newVal) {
        this.clearPendingRenderLoopStop()
        this.$nextTick(() => {
          this.enableCanvasRendering()
        })
      } else {
        // Not stopped on the spot. A host switching between items it already
        // holds raises and clears its loading flag within a single task, so this
        // watcher sees a disable that is over before the next frame — and
        // stopping cancels the frame that was about to draw. Under a held arrow
        // key that lands once per repeat, and the canvas never draws at all: the
        // scene walks along the wall while the picture on screen sits still.
        this.scheduleRenderLoopStop()
      }
    },
    previewMode(val, old) {
      if (old && !val && this.hasInitialFit && this.autoFramingAllowed()) {
        this.$nextTick(() => this.applyAutoFramingForCurrentScene({ instant: false }))
      }
    },
    suspendHeavyWork(isSuspended) {
      if (isSuspended) {
        this._pendingHeavyRebuild = true
        return
      }
      if (this.cameraAnimRafId != null) {
        this._pendingHeavyRebuild = true
        return
      }
      this.syncRenderItems()
      if (!isSuspended && this._pendingHeavyRebuild) {
        this._pendingHeavyRebuild = false
        this.$nextTick(() => this.debouncedRebuild())
      }
    },
    resizeSnapCandidates: {
      handler() {
        this.rebuildSizeSnapOverlay()
      },
      deep: true,
    },
    resizeConstraints: {
      handler() {
        this.rebuildSizeSnapOverlay()
      },
      deep: true,
    },
    resizeOverlayConstraints: {
      handler() {
        this.rebuildSizeSnapOverlay()
      },
      deep: true,
    },
    resizeSnapOverlayVisible() {
      this.refreshSizeSnapOverlayVisibility()
      if (this.resizeSnapOverlayVisible) {
        this.rebuildSizeSnapOverlay()
      }
    },
    renderItems: {
      handler() {
        this.syncRenderItems()
      },
    },
    selectedItemId() {
      this.handleSelectedRenderItemChange()
    },
    selectedItemIds() {
      this.syncRenderItemsWhenCameraIdle()
      this.syncSelectionHighlights()
      this.updateHandlesVisibility(this.hoveredObject)
    },
    selectedRendererItemId() {
      this.syncRenderItemsWhenCameraIdle()
    },
    selectedRendererProps: {
      handler() {
        if (this._formResizePreviewActive) return
        if (this.isDragging && (
          this.dragMode === 'resize' ||
          this.dragMode === 'source-move' ||
          this.dragMode === 'source-scale'
        )) return
        this.syncRenderItemsWhenCameraIdle()
      },
      deep: true,
    },
    rotateAnimationRequest: {
      handler(request) {
        this.queueItemRotationAnimation(request)
      },
      deep: true,
    },
    renderItemGapInches() {
      this.syncRenderItems()
    },
  },

  mounted() {
    // Trailing only. Holding a key coalesces to the item landed on rather than
    // allocating and discarding a scene per step, and an edit that trips
    // several prop watchers in one tick still costs a single rebuild.
    this.debouncedRebuild = debounce(() => this.rebuildScene(), REBUILD_DEBOUNCE_MS)
    this.rebuildVersion = 0
    this._pendingHeavyRebuild = false

    // Wait for layout to settle before initializing Three.js
    this.$nextTick(() => {
      if (this.canvasEnabled) {
        this.enableCanvasRendering()
      }
    })

    // Listen for window resize
    this._boundVisibilityChange = () => this.onDocumentVisibilityChange()
    document.addEventListener('visibilitychange', this._boundVisibilityChange)
    // One place covers every input-driven change — hover, drags, scroll-snap,
    // free-look keys — so those paths never have to remember to mark themselves.
    this._boundWake = () => this.markNeedsRender()
    RENDER_WAKE_EVENTS.forEach(name => {
      this.$el.addEventListener(name, this._boundWake, { capture: true, passive: true })
    })
    this.boundOnResize = () => this.onResize()
    window.addEventListener('resize', this.boundOnResize)

    this.boundHandleDrag = (e) => this.handleDrag(e)
    this.boundEndResizeDrag = () => this.endResizeDrag()
    this._boundDropGrab = () => this._dropGrab()
  },

  beforeDestroy() {
    // Set before anything else tears down: markNeedsRender restarts the loop,
    // and a watcher or stray event firing during teardown would otherwise
    // resurrect it on a component that is going away.
    this._teardown = true
    this.clearAllResizeConstraintMessages()
    this.disposeOverlayMaterials()
    if (this._boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this._boundVisibilityChange)
    }
    if (this._boundWake) {
      RENDER_WAKE_EVENTS.forEach(name => {
        this.$el.removeEventListener(name, this._boundWake, { capture: true })
      })
    }
    if (this.boundHandleDrag) {
      document.removeEventListener('mousemove', this.boundHandleDrag)
      document.removeEventListener('mouseup', this.boundEndResizeDrag)
    }
    document.removeEventListener('mouseup', this._boundDropGrab)
    if (this.dragRafId != null) {
      cancelAnimationFrame(this.dragRafId)
      this.dragRafId = null
    }
    this.cancelItemRotationAnimation()
    this.cancelPoseAnimation()
    this._endTurntableDragListeners()
    document.removeEventListener('mouseup', this._boundTurntablePressEnd)
    this.destroyTurntableControls()
    this.disposeTurntableDimPass()
    this.destroyOrbitControls()
    this.clearOrbitModeSettleTimer()
    if (this._scrollSnapSyncRafId != null) {
      cancelAnimationFrame(this._scrollSnapSyncRafId)
      this._scrollSnapSyncRafId = null
    }
    this.restoreScrollSnapOverlayContextMenuBypass()
    this.clearScrollSnapSettleTimer()
    // Cancel pending debounced rebuilds
    if (this.debouncedRebuild && this.debouncedRebuild.cancel) {
      this.debouncedRebuild.cancel()
    }
    this.stopRenderLoop()
    if (this.boundOnResize) {
      window.removeEventListener('resize', this.boundOnResize)
    }
    if (this.wallMesh) {
      if (this.wallMesh.geometry) this.wallMesh.geometry.dispose()
      if (this.wallMesh.material) this.wallMesh.material.dispose()
      this.wallMesh = null
    }
    if (this.sizeSnapOverlayGroup) {
      this.disposeSizeSnapOverlay(this.sizeSnapOverlayGroup)
      this.sizeSnapOverlayGroup = null
    }
    if (this._multiSelectHighlightGroup) {
      this._multiSelectHighlightGroup.children.forEach(child => {
        child.geometry?.dispose?.()
        child.material?.dispose?.()
      })
      this._multiSelectHighlightGroup.parent?.remove(this._multiSelectHighlightGroup)
      this._multiSelectHighlightGroup = null
    }
    if (this.wallSnapOverlayGroup) {
      this.disposeWallSnapOverlay()
    }
    this.removeShadowCameraHelper()
    this._removeDecor()
    this.clearRenderItems()
    this._clearRoomGeometry()
    this.sceneLights = []
    if (this.threeRenderer) {
      this.threeRenderer.dispose()
      this.threeRenderer.forceContextLoss()
      this.threeRenderer = null
    }
    if (this._freeLookAudioCtx) {
      this._freeLookAudioCtx.close()
      this._freeLookAudioCtx = null
    }
  },

  methods: {
    getPrimaryShadowDirectionalLight() {
      return Array.isArray(this.sceneLights)
        ? this.sceneLights.find(light => light?.isDirectionalLight && light.castShadow && light.shadow?.camera)
        : null
    },
    removeShadowCameraHelper() {
      if (!this._shadowCameraHelper) return
      if (this._shadowCameraHelper.parent) {
        this._shadowCameraHelper.parent.remove(this._shadowCameraHelper)
      }
      this._shadowCameraHelper.geometry?.dispose?.()
      const materials = Array.isArray(this._shadowCameraHelper.material)
        ? this._shadowCameraHelper.material
        : [this._shadowCameraHelper.material]
      materials.forEach(material => material?.dispose?.())
      this._shadowCameraHelper = null
      this._shadowCameraHelperLight = null
    },
    updateShadowCameraHelper() {
      if (!this.debugLighting || !this.threeScene) {
        this.removeShadowCameraHelper()
        return
      }

      const light = this.getPrimaryShadowDirectionalLight()
      if (!light) {
        this.removeShadowCameraHelper()
        return
      }

      if (!this._shadowCameraHelper || this._shadowCameraHelperLight !== light) {
        this.removeShadowCameraHelper()
        this._shadowCameraHelper = new THREE.CameraHelper(light.shadow.camera)
        this._shadowCameraHelper.userData = { type: 'shadow-camera-helper' }
        this.threeScene.add(this._shadowCameraHelper)
        this._shadowCameraHelperLight = light
      }

      this._shadowCameraHelper.visible = true
      this._shadowCameraHelper.update()
    },
    resetCameraFitState() {
      this.hasInitialFit = false
      if (this.cameraAnimRafId != null) {
        cancelAnimationFrame(this.cameraAnimRafId)
        this.cameraAnimRafId = null
      }
    },
    _selectAutoFramingMode(sceneW, sceneH) {
      const containerMetrics = this.getContainerMetrics()
      if (!containerMetrics) return CAMERA_MODE_STANDARD

      return selectAutoFramingMode({
        contentW: sceneW,
        contentH: sceneH,
        viewportW: containerMetrics.containerW,
        viewportH: containerMetrics.containerH,
        piecePadding: AUTO_FRAMING_PIECE_PADDING,
        baselineViewportHeight: STANDARD_REFERENCE_VIEWPORT_HEIGHT,
      })
    },
    // ------------------------------------------------------------------
    // Optional decor asset (GLB)
    // ------------------------------------------------------------------

    _disposeObject3D(root) {
      if (!root) return
      root.traverse((obj) => {
        if (!obj) return
        if (obj.geometry) obj.geometry.dispose()
        const mats = obj.material ? (Array.isArray(obj.material) ? obj.material : [obj.material]) : []
        mats.forEach((m) => {
          if (!m) return
          // Shared overlay materials outlive the group they hang off; disposing
          // them here would delete their shader program and force a recompile
          // (and a main-thread stall) on the very next draw.
          if (isSharedOverlayMaterial(m)) return
          ;['map', 'emissiveMap', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'alphaMap'].forEach((k) => {
            const tex = m[k]
            if (tex && tex.dispose) tex.dispose()
          })
          if (m.dispose) m.dispose()
        })
      })
    },
    _disposeUnusedVisualObject(root) {
      if (!root) return
      root.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose()
        const mats = obj.material ? (Array.isArray(obj.material) ? obj.material : [obj.material]) : []
        mats.forEach((m) => m?.dispose?.())
      })
    },
    getRenderSpecs({ collisionEnabled = false, pinnedItemId = undefined } = {}) {
      const baseRenderItems = Array.isArray(this.renderItems)
        ? this.renderItems.filter(spec => spec && spec.rendererProps)
        : []
      let selectedRendererProps = this.selectedRendererProps
      if (collisionEnabled && (this._formResizePreviewActive || (this.isDragging && this.dragMode === 'resize'))) {
        const selectedInstance = this._renderItemInstances?.get(String(this._selectedRenderKey ?? ''))
        const live = selectedInstance?.liveFootprint
        if (live?.w > 0 && live?.h > 0 && selectedRendererProps) {
          selectedRendererProps = {
            ...selectedRendererProps,
            targetWidth: live.w,
            targetHeight: live.h,
          }
        }
      }
      const effectiveRenderItems = resolveProjectWallRenderItems({
        renderItems: baseRenderItems,
        selectedItemId: this.selectedItemId,
        selectedItemIds: this.selectedItemIds,
        selectedRendererItemId: this.selectedRendererItemId,
        selectedRendererProps,
        gapInches: this.renderItemGapInches,
        collisionEnabled: Boolean(collisionEnabled && this.collisionEnabled),
        pinnedItemId,
      })
      if (effectiveRenderItems.length) {
        return effectiveRenderItems
      }
      return [this.buildScalarRenderSpec()]
    },
    getDynamicWallLayout(specs = null) {
      const renderSpecs = Array.isArray(specs) ? specs : this.getRenderSpecs()
      if (!renderSpecs.length) {
        return getProjectWallLayoutFromSceneXs([], WALL_WIDTH)
      }

      // Use each item's natural (auto-layout) x so the wall stays fixed
      // regardless of which items have been pinned or moved
      const xOf = (s) => {
        const n = Number(s?.naturalX)
        return Number.isFinite(n) ? n : (Number(s?.position?.x) || 0)
      }
      return getProjectWallLayoutFromSceneXs(renderSpecs.map(xOf), WALL_WIDTH)
    },
    buildScalarRenderSpec() {
      return {
        key: `scalar-item:${this.thumbnailKey ?? this.artworkImageUrl ?? 'default'}`,
        itemId: this.thumbnailKey ?? null,
        selected: true,
        active: true,
        affordances: { resize: true, source: true, dimensions: true, snapOverlay: true },
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        rendererProps: {
          artWidth: this.parsedArtWidth,
          artHeight: this.parsedArtHeight,
          targetWidth: this.targetWidth,
          targetHeight: this.targetHeight,
          artworkImageUrl: this.artworkImageUrl,
          debugChecker: this.debugChecker,
          artworkScaleX: this.parsedArtworkScaleX,
          artworkScaleY: this.parsedArtworkScaleY,
          artworkRotation: this.parsedArtworkRotation,
          artworkOffsetLeft: this.parsedArtworkOffsetLeft,
          artworkOffsetTop: this.parsedArtworkOffsetTop,
          substrate: this.substrate,
          frameData: this.frameData,
          matData: this.matData,
          matDataArray: this.matDataArray,
          matWidth: this.matWidth,
          matWidthsArray: this.matWidthsArray,
          linenLiner: this.linenLiner,
          hangData: this.hangData,
          stretcherDepth: this.stretcherDepth,
          canvasMount: this.canvasMount,
          floatFrameData: this.floatFrameData,
          substrateBackData: this.substrateBackData,
          artworkFilter: this.artworkFilter,
          acousticTwoSidedChangeable: this.acousticTwoSidedChangeable,
        },
      }
    },
    normalizeRenderSpec(spec, wallLayout = null) {
      const position = spec.position || {}
      const selected = Boolean(spec.selected || spec.active)
      const x = Number(position.x) || 0
      const y = Number(position.y) || 0
      const layout = wallLayout || this.getDynamicWallLayout()
      const wallLeftX = Number.isFinite(Number(layout?.wallLeftX))
        ? Number(layout.wallLeftX)
        : ((Number(layout?.wallCenterX) || 0) - ((Number(layout?.wallWidth) || WALL_WIDTH) / 2))
      return {
        ...spec,
        selected,
        active: selected,
        rendererProps: {
          ...(spec.rendererProps || {}),
          debugChecker: this.debugChecker,
        },
        position: {
          x: spec.wallSpacePosition ? wallLeftX + x : x,
          y: spec.wallSpacePosition ? y : EYE_LEVEL + y,
          z: Number(position.z) || 0,
        },
      }
    },
    clearRenderItems() {
      if (!this._renderItemInstances) return
      this.cancelItemRotationAnimation()
      this._renderItemInstances.forEach(instance => disposeDesignStudioItemObject(instance))
      this._renderItemInstances.clear()
    },
    /**
     * Drop the built scene without building a replacement.
     *
     * For a host swapping items faster than it can render them: the piece in
     * the scene has been abandoned, and the thumbnail covering it only covers
     * the piece's own rect — its shadow falls outside and goes on showing
     * around the cover. Clearing leaves an empty room behind the thumbnail
     * until the debounced rebuild builds the item actually landed on.
     */
    dropRenderedItems() {
      if (!this._renderItemInstances?.size) return
      this.clearRenderItems()
      this.markNeedsRender()
    },

    clearInteractionOverlays() {
      const groups = [this.handlesGroup, this.dimGroup, this.sizeSnapOverlayGroup].filter(Boolean)
      groups.forEach((group) => {
        if (group.parent) group.parent.remove(group)
        this._disposeObject3D(group)
      })
      this.handlesGroup = null
      this.dimGroup = null
      this.sizeSnapOverlayGroup = null
      this.sizeSnapOverlayOpacity = 0
      this.sizeSnapOverlayTargetOpacity = (
        this.resizeSnapOverlayVisible || this.hasNearResizeOverlayConstraints()
      ) ? 1 : 0
    },
    getSelectedRenderSpec() {
      const specs = this.getRenderSpecs()
      // In multi-selection several specs are flagged selected; camera framing,
      // interaction overlays, and collision pinning stay anchored to the
      // active (route) item.
      if (this.selectedItemId != null) {
        const active = specs.find(spec => String(spec?.itemId) === String(this.selectedItemId))
        if (active) return active
      }
      return specs.find(spec => spec?.selected || spec?.active) || null
    },
    getActiveRenderSpec() {
      return this.getSelectedRenderSpec()
    },
    getRenderSpecByKey(key) {
      if (key == null) return null
      return this.getRenderSpecs().find(spec => String(spec?.key) === String(key)) || null
    },
    isSelectedRenderObject(obj) {
      const key = obj?.userData?.renderItemKey
      if (key == null) return true
      const spec = this.getRenderSpecByKey(key)
      return Boolean(spec?.selected || spec?.active)
    },
    getWallCenterX() {
      const currentCenter = Number(this._dynamicWallLayout?.wallCenterX)
      if (Number.isFinite(currentCenter)) return currentCenter

      const layout = this.getDynamicWallLayout()
      const layoutCenter = Number(layout?.wallCenterX)
      return Number.isFinite(layoutCenter) ? layoutCenter : 0
    },
    getActiveScenePosition() {
      const spec = this.getSelectedRenderSpec()
      if (!spec?.key) return { x: 0, y: EYE_LEVEL }
      const instance = this._renderItemInstances?.get(String(spec.key))
      if (instance?.group) {
        const offset = new THREE.Vector3(
          inches(Number(instance.builtDims?.matOffsetX) || 0),
          inches(Number(instance.builtDims?.matOffsetY) || 0),
          0,
        )
        if (this.getEffectivePose()) {
          // Off the wall the piece turns about its own origin, which swings
          // this point around with it. Callers want where the piece hangs —
          // the overlay anchors to it and the shadow is cast from it — not
          // where one corner of it has got to mid-spin.
          offset.applyEuler(instance.baseRotation || instance.group.rotation)
          return {
            x: instance.group.position.x + offset.x,
            y: instance.group.position.y + offset.y,
          }
        }
        const center = instance.group.localToWorld(offset)
        return { x: center.x, y: center.y }
      }
      const normalized = this.normalizeRenderSpec(spec, this._dynamicWallLayout || this.getDynamicWallLayout())
      return normalized.position || { x: 0, y: EYE_LEVEL }
    },
    handleSelectedRenderItemChange() {
      const selectedSpec = this.getSelectedRenderSpec()
      const keyChanged = String(selectedSpec?.key ?? '') !== String(this._selectedRenderKey ?? '')
      // The turntable is a pose held by one piece. Switching pieces mid-spin
      // would hand it to the new one, which nobody asked to take off the wall.
      if (keyChanged) this.exitTurntable({ instant: true })
      this._renderItemsSyncVersion += 1
      this._selectedRenderKey = selectedSpec?.key ?? null
      // Take the incoming piece's geometry now. It is already built and hanging
      // on the wall, so there is nothing to wait for — but the overlay only
      // adopted it at the end of the next render-items sync, which left the
      // handles a few hundred milliseconds sized to the piece just left, and
      // then jumping to the new one once that sync landed.
      if (keyChanged) this.syncSelectedInteractionOverlays()
      this.applySelectedItemPose()
      this.updateInteractionDimensionsForRenderSpec(selectedSpec)
      this.updateHandlesVisibility(this.hoveredObject)
      this.$nextTick(() => {
        if (!this.threeRenderer || !this.hasInitialFit) return
        // An instant jump has already put the overlay on the item, and the
        // framing is deliberately left alone: a run of jumps is someone
        // scanning, and reframing under each one turns the run into a stutter.
        // The host eases to the incoming piece's framing once the run stops —
        // see fitForItemSettled.
        if (this._scrollSnapInstantFitKey) {
          if (String(selectedSpec?.key ?? '') === this._scrollSnapInstantFitKey) {
            this._scrollSnapInstantFitKey = null
            this._scrollSnapInstantJumpKeys.clear()
            this.finishScrollSnapSelectionHandoff()
            this.runPendingHeavyRebuild()
          }
          // Otherwise this selection is a route lagging behind a jump the user
          // has already made past it. Scrolling the overlay to it would walk the
          // step back; the pending jump's own selection lands right behind this.
          return
        }
        if (
          !this._scrollSnapSelectionFitPending &&
          this.scrollSnapLayout?.enabled &&
          this.$refs.scrollSnapOverlay
        ) {
          const _overlay = this.$refs.scrollSnapOverlay
          const _layout = this.scrollSnapLayout
          const _item = this.getScrollSnapItemForSpec(selectedSpec)
          if (_item) {
            const _targetLeft = (_item.x - _layout.minX) * _layout.pxPerUnit
            if (Math.abs(_overlay.scrollLeft - _targetLeft) > 2) {
              // Overlay is not at the target item's snap position — smooth-scroll to it.
              // Camera follows via onScrollSnapOverlayScroll with no separate animation.
              // User scrolling cancels the browser's smooth scroll and takes over naturally.
              if (this.scrollSnapOverlayToSelectedSpec(selectedSpec, { behavior: 'smooth' })) {
                this._scrollSnapSelectionFitPending = true
                return
              }
            }
          }
        }
        if (this._scrollSnapSelectionFitPending) {
          this.finishScrollSnapSelectionHandoff()
          this.applyAutoFramingForCurrentScene({ instant: false, restart: true })
            ?.then?.(() => this.runPendingHeavyRebuild())
          return
        }
        if (!this.autoFramingAllowed()) return
        this.applyAutoFramingForCurrentScene({ instant: false, restart: true })
          ?.then?.(() => this.runPendingHeavyRebuild())
      })
    },
    updateInteractionDimensionsForRenderSpec(spec) {
      const props = spec?.rendererProps || {}
      const artW = Number(props.artWidth)
      const artH = Number(props.artHeight)
      if (!Number.isFinite(artW) || !Number.isFinite(artH) || artW <= 0 || artH <= 0) return
      this.updateSceneDimensions(artW, artH, {
        scaleX: Number(props.artworkScaleX) || 1,
        scaleY: Number(props.artworkScaleY ?? props.artworkScaleX) || 1,
        rotation: Number(props.artworkRotation) || 0,
        offsetLeft: Number(props.artworkOffsetLeft) || 0,
        offsetTop: Number(props.artworkOffsetTop) || 0,
      })
    },
    syncRenderItemsWhenCameraIdle() {
      if (this.cameraAnimRafId != null) {
        this._pendingHeavyRebuild = true
        return
      }
      this.syncRenderItems()
    },
    runPendingHeavyRebuild() {
      if (!this._pendingHeavyRebuild) return
      this._pendingHeavyRebuild = false
      this.$nextTick(() => this.debouncedRebuild())
    },
    syncMatVisualState() {
      if (this.isDragging && this.dragMode === 'resize') {
        this.syncRenderItems({ forceDuringResize: true })
      } else {
        this.debouncedRebuild()
      }
    },
    async syncRenderItems({ forceDuringResize = false } = {}) {
      if (!this.threeScene || !this._renderItemInstances) return
      if (this.suspendHeavyWork) return
      if (this._grabbed) return
      if (this.isDragging && this.dragMode === 'resize' && !forceDuringResize) return
      const version = ++this._renderItemsSyncVersion
      const rawSpecs = this.getRenderSpecs()
      const wallLayout = this.getDynamicWallLayout(rawSpecs)
      const specs = rawSpecs.map(spec => this.normalizeRenderSpec(spec, wallLayout))
      this.syncDynamicWallLayout(rawSpecs)
      const selectedKey = specs.find(spec => spec.selected)?.key ?? null
      this._selectedRenderKey = selectedKey
      const wantedKeys = new Set(specs.map(spec => String(spec.key)))

      this._renderItemInstances.forEach((instance, key) => {
        if (!wantedKeys.has(key)) {
          disposeDesignStudioItemObject(instance)
          this._renderItemInstances.delete(key)
        }
      })

      for (const spec of specs) {
        if (version !== this._renderItemsSyncVersion) return
        const key = String(spec.key)
        const existing = this._renderItemInstances.get(key)
        if (existing) {
          const next = await updateDesignStudioItemObject(existing, spec, { deferParentSwap: true })
          if (version !== this._renderItemsSyncVersion) {
            if (next !== existing) disposeDesignStudioItemObject(next)
            return
          }
          if (next !== existing) {
            this.threeScene.add(next.group)
            if (existing.group?.parent) existing.group.parent.remove(existing.group)
            disposeDesignStudioItemObject(existing)
          }
          this.captureRenderInstanceBaseTransform(next)
          this._renderItemInstances.set(key, next)
          continue
        }
        try {
          const instance = await buildDesignStudioItemObject(spec)
          if (version !== this._renderItemsSyncVersion) {
            disposeDesignStudioItemObject(instance)
            return
          }
          this.threeScene.add(instance.group)
          this.captureRenderInstanceBaseTransform(instance)
          this._renderItemInstances.set(key, instance)
        } catch (err) {
          console.warn('FrameRenderer: failed to build render item', err)
        }
      }
      this.syncSelectedInteractionOverlays()
      this.syncSelectionHighlights(specs)
      this.applySelectedItemPose()
      this.startPendingItemRotationAnimation()
      this.updatePlayerShadowLight()
      this.updateHandlesVisibility(this.hoveredObject)
      if (this._formResizePreviewActive && this.collisionEnabled) this._applyResizeCollision()
      this.$nextTick(() => this.refreshScrollSnapOverlayLayout())
    },
    captureRenderInstanceBaseTransform(instance) {
      if (!instance?.group) return
      instance.basePosition = instance.group.position.clone()
      instance.baseRotation = instance.group.rotation.clone()
      instance.baseScale = instance.group.scale.clone()
      instance.baseBuiltDims = instance.builtDims ? { ...instance.builtDims } : null
      // Cache the finished footprint for collision so it tracks the actual built geometry; the
      // resize path overrides this live while builtDims/specs lag behind.
      const ow = Number(instance.builtDims?.outerWidth)
      const oh = Number(instance.builtDims?.outerHeight)
      instance.liveFootprint = ow > 0 && oh > 0 ? { w: ow, h: oh } : null
    },
    syncSelectedInteractionOverlays() {
      if (this.isDragging) return
      const selectedInstance = this.getSelectedRenderInstance()
      this.builtDims = selectedInstance?.builtDims || null
      this.rebuildInteractionOverlays(this.builtDims)
      this.updateSceneDimensions(this.parsedArtWidth, this.parsedArtHeight)
    },
    // Outline every multi-selected wall item. The outlines are pure visuals:
    // they carry no userData.renderItemId, so hit-testing ignores them.
    syncSelectionHighlights(specs = null) {
      if (!this.threeScene) return
      if (!this._multiSelectHighlightGroup) {
        this._multiSelectHighlightGroup = new THREE.Group()
        this._multiSelectHighlightGroup.name = 'multi-select-highlights'
        this.threeScene.add(this._multiSelectHighlightGroup)
      }
      const group = this._multiSelectHighlightGroup
      while (group.children.length) {
        const child = group.children[0]
        group.remove(child)
        child.geometry?.dispose?.()
        child.material?.dispose?.()
      }
      if ((this.selectedItemIds?.length || 0) < 2) return

      let resolvedSpecs = specs
      if (!resolvedSpecs) {
        const rawSpecs = this.getRenderSpecs()
        const wallLayout = this.getDynamicWallLayout(rawSpecs)
        resolvedSpecs = rawSpecs.map(spec => this.normalizeRenderSpec(spec, wallLayout))
      }
      const pad = 1.25
      resolvedSpecs.forEach(spec => {
        if (!spec?.selected) return
        const instance = this._renderItemInstances?.get(String(spec.key))
        if (!instance?.group) return
        const w = Number(instance.builtDims?.outerWidth) || Number(spec.rendererProps?.targetWidth) || 0
        const h = Number(instance.builtDims?.outerHeight) || Number(spec.rendererProps?.targetHeight) || 0
        if (!(w > 0 && h > 0)) return
        const geometry = new THREE.EdgesGeometry(new THREE.PlaneGeometry(w + pad, h + pad))
        const material = new THREE.LineBasicMaterial({
          color: 0x0d6efd,
          transparent: true,
          opacity: 0.9,
          depthTest: false,
        })
        const outline = new THREE.LineSegments(geometry, material)
        outline.position.copy(instance.group.localToWorld(new THREE.Vector3(
          inches(Number(instance.builtDims?.matOffsetX) || 0),
          inches(Number(instance.builtDims?.matOffsetY) || 0),
          0,
        )))
        outline.position.z += 0.5
        outline.renderOrder = 999
        outline.userData.followKey = String(spec.key)
        group.add(outline)
      })
    },
    // Reposition the selection outlines from their instances' live positions —
    // called per frame during a multi-item wall drag (no geometry rebuild).
    _syncSelectionHighlightPositions() {
      const group = this._multiSelectHighlightGroup
      if (!group?.children?.length) return
      group.children.forEach((child) => {
        const key = child.userData?.followKey
        if (key == null) return
        const instance = this._renderItemInstances?.get(String(key))
        if (!instance?.group) return
        const center = instance.group.localToWorld(new THREE.Vector3(
          inches(Number(instance.builtDims?.matOffsetX) || 0),
          inches(Number(instance.builtDims?.matOffsetY) || 0),
          0,
        ))
        child.position.x = center.x
        child.position.y = center.y
        child.position.y = instance.group.position.y
      })
    },
    syncDynamicWallLayout(specs = null) {
      const nextLayout = this.getDynamicWallLayout(specs)
      const previous = this._dynamicWallLayout
      this._dynamicWallLayout = nextLayout

      const layoutChanged = !previous ||
        Math.abs((previous.wallWidth || 0) - (nextLayout.wallWidth || 0)) > 0.001 ||
        Math.abs((previous.wallCenterX || 0) - (nextLayout.wallCenterX || 0)) > 0.001
      if (layoutChanged) {
        this.updateWall({ updateRoom: true })
      }
      this.updatePlayerShadowLight()
    },
    updatePlayerShadowLight() {
      if (!Array.isArray(this.sceneLights) || !this.sceneLights.length) return

      const wallZ = -(this._wallZOffset || WALL_Z_OFFSET)
      const layout = this._dynamicWallLayout || this.getDynamicWallLayout()
      const wallCenterX = Number.isFinite(Number(layout?.wallCenterX)) ? Number(layout.wallCenterX) : 0
      const halfExtent = this.getPlayerShadowHalfExtent(wallZ)
      const target = this.getPlayerShadowTarget(wallZ, halfExtent) || new THREE.Vector3(wallCenterX, EYE_LEVEL, 0)

      const nextState = `${target.toArray().map(value => Number(value.toFixed(2))).join(',')}:${halfExtent.toFixed(2)}`
      const previous = this._playerShadowState
      if (
        previous &&
        previous === nextState
      ) {
        return
      }
      this._playerShadowState = nextState

      this.sceneLights.forEach((light) => {
        if (!light?.isDirectionalLight || !light.castShadow) return
        if (!light.userData.playerShadowOffset && light.target) {
          light.userData.playerShadowOffset = light.position.clone().sub(light.target.position)
        }
        const offset = (light.userData.playerShadowOffset || new THREE.Vector3(35, 35, 50)).clone()
        offset.x *= PLAYER_SHADOW_OFFSET_XY_SCALE
        offset.y *= PLAYER_SHADOW_OFFSET_XY_SCALE
        light.position.copy(target).add(offset)
        this.updatePlayerShadowCamera(light, halfExtent)
        if (light.target) {
          light.target.position.copy(target)
          light.target.updateMatrixWorld()
        }
        light.shadow.needsUpdate = true
      })
      this.updateShadowCameraHelper()
    },
    getPlayerShadowHalfExtent(wallZ) {
      if (!this.threeCamera) return PLAYER_SHADOW_MIN_HALF_EXTENT

      const distance = Math.max(0, Math.abs(this.threeCamera.position.z - wallZ))
      const scale = Math.max(1, distance / PLAYER_SHADOW_REFERENCE_DISTANCE)
      return Math.min(
        PLAYER_SHADOW_MAX_HALF_EXTENT,
        PLAYER_SHADOW_MIN_HALF_EXTENT * scale
      )
    },
    updatePlayerShadowCamera(light, halfExtent) {
      const camera = light?.shadow?.camera
      if (!camera) return

      camera.left = -halfExtent
      camera.right = halfExtent
      camera.top = halfExtent
      camera.bottom = -halfExtent
      camera.near = 1
      camera.far = PLAYER_SHADOW_CAMERA_FAR
      camera.updateProjectionMatrix()
    },
    getCameraWallCenterPoint(z = 0) {
      if (!this.threeCamera) return null

      this.threeCamera.updateMatrixWorld()
      const cameraPosition = this.threeCamera.position
      const point = new THREE.Vector3(0, 0, 1).unproject(this.threeCamera)
      const dir = point.sub(cameraPosition).normalize()
      if (Math.abs(dir.z) < 1e-6) return null

      const t = (z - cameraPosition.z) / dir.z
      if (!Number.isFinite(t) || t <= 0) return null

      return new THREE.Vector3(
        cameraPosition.x + dir.x * t,
        cameraPosition.y + dir.y * t,
        z
      )
    },
    getPlayerShadowTarget(z = 0, halfExtent = PLAYER_SHADOW_MIN_HALF_EXTENT) {
      const aimPoint = this.getCameraWallCenterPoint(z)
      if (!this.threeCamera || !(this.freeLookActive || this.cameraMode === CAMERA_MODE_FREE)) {
        return aimPoint
      }

      this.threeCamera.updateMatrixWorld()
      const cameraPosition = this.threeCamera.position
      const localAnchor = new THREE.Vector3(cameraPosition.x, cameraPosition.y, z)

      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.threeCamera.quaternion)
      forward.y = 0
      if (forward.lengthSq() < 1e-6) return localAnchor
      forward.normalize()

      return new THREE.Vector3(
        localAnchor.x + forward.x * Math.max(0, halfExtent),
        localAnchor.y,
        z
      )
    },
    resolveMatHoverSide(data, worldPoint, mesh = this.hoveredObject) {
      const widths = data?.widthsInches
      const inner = data?.innerBounds
      if (!widths || !inner || !worldPoint || !mesh) return null

      const local = mesh.worldToLocal(worldPoint.clone())
      if (!local) return null

      const deltas = {
        top: local.y - inner.top,
        bottom: inner.bottom - local.y,
        left: inner.left - local.x,
        right: local.x - inner.right,
      }

      const outside = Object.entries(deltas)
        .filter(([, delta]) => delta >= -1e-4)
        .map(([side, delta]) => ({
          side,
          ratio: delta / Math.max(widths[side] || 1e-4, 1e-4),
        }))

      if (outside.length) {
        outside.sort((a, b) => a.ratio - b.ratio)
        return outside[0].side
      }

      const nearest = Object.entries({
        top: Math.abs(local.y - inner.top),
        bottom: Math.abs(local.y - inner.bottom),
        left: Math.abs(local.x - inner.left),
        right: Math.abs(local.x - inner.right),
      }).sort((a, b) => a[1] - b[1])[0]

      return nearest ? nearest[0] : null
    },

    _removeDecor() {
      if (this._decorObject && this._decorObject.parent) {
        this._decorObject.parent.remove(this._decorObject)
      }
      this._disposeObject3D(this._decorObject)
      this._decorObject = null
      this._decorLoadPromise = null
    },

    _getDecorElements() {
      const url = String(this.decorUrl || '').trim()
      if (!url) return []

      const fromWall = parseFloat(this.decorFromWall) || 14
      return [
        {
          id: 'plant',
          url,
          heightInches: parseFloat(this.decorHeightInches) || 48,
          position: {
            x: (parseFloat(this.decorOffsetX) || 0) + 6,
            z: (-this._wallZOffset) + fromWall + 6,
          },
          animation: {
            duration: 2000,
            fromDeltaX: -24,
            fromDeltaZ: 76,
          },
        },
      ]
    },

    _getPrimaryDecorElement() {
      return this._getDecorElements()[0] || null
    },

    _startDecorEntranceAnimation() {
      const obj = this._decorObject
      const config = this._getPrimaryDecorElement()
      if (!obj || !config) return

      const animation = config.animation || {}
      const toX = config.position?.x || 0
      const toZ = config.position?.z || 0
      const fromX = toX + (animation.fromDeltaX || 0)
      const fromZ = toZ + (animation.fromDeltaZ || 0)

      obj.userData.decorAnimation = {
        startTime: performance.now(),
        duration: Math.max(1, animation.duration || 2000),
        fromX,
        fromZ,
        toX,
        toZ,
      }
      obj.position.x = fromX
      obj.position.z = fromZ
    },

    _updateDecorEntranceAnimation(now) {
      const obj = this._decorObject
      const anim = obj?.userData?.decorAnimation
      if (!obj || !anim) return

      const elapsed = now - anim.startTime
      const duration = Math.max(1, anim.duration)
      const t = Math.min(1, elapsed / duration)
      const eased = t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)

      obj.position.x = anim.fromX + ((anim.toX - anim.fromX) * eased)
      obj.position.z = anim.fromZ + ((anim.toZ - anim.fromZ) * eased)

      if (t >= 1) {
        obj.position.x = anim.toX
        obj.position.z = anim.toZ
        delete obj.userData.decorAnimation
      }
    },

    async _ensureDecorLoaded(forceReload = false) {
      if (!this.threeScene) return null
      if (!this.decorEnabled) return null

      if (forceReload) {
        this._removeDecor()
      }
      if (this._decorObject) return this._decorObject
      if (this._decorLoadPromise) return this._decorLoadPromise

      const decorConfig = this._getPrimaryDecorElement()
      if (!decorConfig) return null
      if (!this._decorLoader) this._decorLoader = new GLTFLoader()
      const url = decorConfig.url

      this._decorLoadPromise = new Promise((resolve) => {
        // Resolves long after the room prop that asked for it, so the load has
        // to ask for drawing again itself.
        this._decorLoader.load(
          url,
          (gltf) => { this.markNeedsRender(); resolve(gltf?.scene || null) },
          undefined,
          (err) => {
            console.warn('FrameRenderer: decor failed to load', err)
            resolve(null)
          }
        )
      })

      const obj = await this._decorLoadPromise
      this._decorLoadPromise = null
      if (!obj || !this.threeScene || !this.decorEnabled) return null

      // Normalize + scale to target height (inches).
      obj.userData = { ...(obj.userData || {}), type: 'decor', decorId: decorConfig.id }
      obj.traverse((n) => {
        if (n.isMesh) {
          n.castShadow = true
          n.receiveShadow = true
        }
      })

      const box = new THREE.Box3().setFromObject(obj)
      const size = box.getSize(new THREE.Vector3())
      const desiredH = inches(decorConfig.heightInches)
      if (size.y > 0 && desiredH > 0) {
        const s = desiredH / size.y
        obj.scale.multiplyScalar(s)
      }

      // Drop to floor (y=0)
      const box2 = new THREE.Box3().setFromObject(obj)
      obj.position.y += -box2.min.y

      this._decorObject = obj
      this.threeScene.add(obj)
      this.positionDecor()
      this._startDecorEntranceAnimation()
      return obj
    },

    async updateDecor(forceReload = false) {
      if (!this.threeScene) return
      if (!this.decorEnabled) {
        this._removeDecor()
        return
      }
      await this._ensureDecorLoaded(forceReload)
    },

    positionDecor() {
      const obj = this._decorObject
      const config = this._getPrimaryDecorElement()
      if (!this.threeScene || !obj || !config) return

      const x = config.position?.x || 0
      const z = config.position?.z || 0
      const anim = obj.userData?.decorAnimation
      if (anim) {
        anim.toX = x
        anim.toZ = z
      } else {
        obj.position.x = x
      }
      obj.position.z = z
    },
    // ------------------------------------------------------------------
    // Source image drag helpers (shared by move, scale, and resize)
    // ------------------------------------------------------------------

    _beginDrag(event, mode, cursor) {
      if (this.debouncedRebuild?.cancel) this.debouncedRebuild.cancel()
      this.isDragging = true
      this.dragMode = mode
      this.syncOrbitInteraction()
      if (mode === 'resize') {
        this.activeResizeSnap = null
        this.setActiveResizeConstraint(null)
        this.$emit('resize-drag-state', true)
      } else if (mode === 'source-move' || mode === 'source-scale') {
        this.$emit('source-transform-drag-state', true)
      }
      this.dragStartPos = { x: event.clientX, y: event.clientY }
      this._dragGrabAimPoint = null
      if (this.threeRaycaster && this.threeCamera) {
        const aimPoint = this.freeLookActive
          ? this.getViewportCenterAimPoint()
          : { clientX: event.clientX, clientY: event.clientY }
        if (aimPoint) {
          const ndc = this.getMouseNDC(aimPoint)
          this.threeRaycaster.setFromCamera(ndc, this.threeCamera)
          const { origin, direction } = this.threeRaycaster.ray
          if (direction.z < 0) {
            const t = -origin.z / direction.z
            this._dragGrabAimPoint = { x: origin.x + direction.x * t, y: origin.y + direction.y * t }
          }
        }
      }
      this.dragSourceStart = {
        scaleX: this.parsedArtworkScaleX,
        scaleY: this.parsedArtworkScaleY,
        rotation: this.parsedArtworkRotation,
        offsetLeft: this.parsedArtworkOffsetLeft,
        offsetTop: this.parsedArtworkOffsetTop,
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
      }
      if (mode === 'resize') {
        this.setTooltipPositionFromClientPoint(this.getTooltipAnchorPoint(event))
        this.tooltipText = this._resizeDimTooltip(this.parsedArtWidth, this.parsedArtHeight)
        this.tooltipVisible = true
      } else {
        this.tooltipVisible = false
      }
      document.addEventListener('mousemove', this.boundHandleDrag)
      document.addEventListener('mouseup', this.boundEndResizeDrag)
      document.body.style.cursor = cursor
      document.body.style.userSelect = 'none'
      event.preventDefault()
    },

    rebuildSizeSnapOverlay() {
      if (!this.interactionGroup) return

      if (this.sizeSnapOverlayGroup?.parent) {
        this.sizeSnapOverlayGroup.parent.remove(this.sizeSnapOverlayGroup)
      }
      if (this.sizeSnapOverlayGroup) {
        this.disposeSizeSnapOverlay(this.sizeSnapOverlayGroup)
      }

      this.sizeSnapOverlayGroup = createSizeSnapOverlay(
        this.resizeSnapCandidates,
        this.getResizeOverlayConstraints(),
      )
      this.interactionGroup.add(this.sizeSnapOverlayGroup)
      this.updateSizeSnapOverlayLineOpacities()
    },

    disposeSizeSnapOverlay(group) {
      if (!group) return
      group.traverse((obj) => {
        if (obj.geometry?.dispose) obj.geometry.dispose()
        if (obj.material) {
          const materials = Array.isArray(obj.material) ? obj.material : [obj.material]
          materials.forEach((material) => material?.dispose?.())
        }
      })
    },

    /**
     * The overlay's materials, created once per renderer and reused by every
     * rebuild. Held per instance rather than per module: the hover code animates
     * their opacity, and two renderers on screen at once must not share that.
     */
    overlayMaterials() {
      if (!this._overlayMaterials) {
        this._overlayMaterials = {
          ...createSourceHandleMaterials(),
          // One per corner — see createResizeHandles.
          corners: [0, 1, 2, 3].map(() => createResizeHandleMaterial()),
          turntable: createTurntableHandleMaterial(),
          dimension: createDimensionLineMaterial(),
        }
      }
      return this._overlayMaterials
    },
    disposeOverlayMaterials() {
      if (!this._overlayMaterials) return
      Object.values(this._overlayMaterials)
        .flat()
        .forEach(m => m?.dispose?.())
      this._overlayMaterials = null
    },
    /**
     * The tab on the left edge that lifts the piece off the wall.
     *
     * Centred on the edge itself and vertically halfway up it, so which edge it
     * belongs to is unambiguous once the piece is turning. Sized off the piece —
     * a fixed tab is a smudge on a mural and swallows a postcard — but floored
     * so a small piece still leaves something to aim at.
     */
    createTurntableHandle(metrics) {
      const outerH = inches(Number(metrics?.outerHeight) || Number(metrics?.artH) || 0)
      const outerW = inches(Number(metrics?.outerWidth) || Number(metrics?.artW) || 0)
      if (!(outerH > 0) || !(outerW > 0)) return null

      // Same girth as every other handle; only the length tracks the piece,
      // because that is what makes it read as an edge rather than a dot.
      const radius = OVERLAY_HANDLE_RADIUS
      const length = Math.max(inches(1.5), Math.min(outerH * 0.22, inches(9)))
      const geometry = new THREE.CapsuleGeometry(radius, length, 6, 14)
      const handle = new THREE.Mesh(geometry, this.overlayMaterials().turntable)
      handle.castShadow = false
      // Straddling the edge rather than floating outside it: this is the edge
      // you are about to pull, so it should read as attached to it.
      handle.position.set(-(outerW / 2), 0, 0.6)
      handle.userData = {
        type: 'turntable-handle',
        label: 'Spin',
        // Where it sits square — see getTurntableHandleRestMatrix.
        rest: handle.position.clone(),
      }

      // Grabbable where it is *and* where it started. The proxy shares this
      // mesh's geometry and material, so it is only ever a second placement of
      // the same shape, nothing to build or dispose. It takes the resting
      // scale, not this mesh's: handles shrink as they near the camera, so
      // lending it a travelling one crept its edge out from under the pointer.
      const restProxy = new THREE.Mesh(geometry, handle.material)
      handle.raycast = (raycaster, intersects) => {
        THREE.Mesh.prototype.raycast.call(handle, raycaster, intersects)
        const rest = this.getTurntableHandleRestMatrix()
        if (!rest) return
        restProxy.matrixWorld.copy(rest).scale(handle.userData.restScale || handle.scale)
        const hits = []
        THREE.Mesh.prototype.raycast.call(restProxy, raycaster, hits)
        hits.forEach((hit) => {
          hit.object = handle
          intersects.push(hit)
        })
      }
      return handle
    },

    createInteractionHandles(metrics) {
      const artW = metrics.artW
      const artH = metrics.artH
      const shared = this.overlayMaterials()
      const handlesGroup = createResizeHandles(artW, artH, shared.corners)
      const sourceGroup = new THREE.Group()
      sourceGroup.userData = { type: 'source-handles' }

      const aw = inches(artW)
      const handleSize = OVERLAY_HANDLE_RADIUS
      const centerGeo = new THREE.SphereGeometry(handleSize, 18, 18)
      const scaleGeo = new THREE.SphereGeometry(handleSize * 0.8, 16, 16)
      const centerMat = shared.center
      const scaleMat = shared.scale
      const linkMat = shared.link
      const crossMat = shared.cross

      const centerHandle = new THREE.Mesh(centerGeo, centerMat)
      centerHandle.position.set(0, 0, 0.6)
      centerHandle.userData = { type: 'source-handle-move', label: 'Move image' }
      sourceGroup.add(centerHandle)

      const scaleOffset = aw * 0.25
      const scaleHandle = new THREE.Mesh(scaleGeo, scaleMat)
      scaleHandle.position.set(scaleOffset, 0, 0.6)
      scaleHandle.userData = { type: 'source-handle-scale', label: 'Scale image' }
      sourceGroup.add(scaleHandle)

      const linkGeo = new THREE.BoxGeometry(Math.abs(scaleOffset), handleSize * 0.25, 0.1)
      const link = new THREE.Mesh(linkGeo, linkMat)
      link.position.set(scaleOffset / 2, 0, 0.55)
      link.userData = { type: 'source-handle-link' }
      sourceGroup.add(link)

      handlesGroup.add(sourceGroup)

      const crossSize = handleSize * 1.1
      const crossGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-crossSize, 0, 0.7),
        new THREE.Vector3(crossSize, 0, 0.7),
        new THREE.Vector3(0, -crossSize, 0.7),
        new THREE.Vector3(0, crossSize, 0.7),
      ])
      const crosshair = new THREE.LineSegments(crossGeo, crossMat)
      crosshair.userData = { type: 'source-artwork-center-crosshair', label: 'Artwork center' }
      crosshair.raycast = () => {}
      handlesGroup.add(crosshair)

      const turntableHandle = this.createTurntableHandle(metrics)
      if (turntableHandle) handlesGroup.add(turntableHandle)
      this._turntableHandleMesh = turntableHandle

      const screenSpaceTypes = new Set([
        'resize-handle',
        'source-handle-move',
        'source-handle-scale',
        'source-handle-link',
        'source-artwork-center-crosshair',
        'turntable-handle',
      ])
      handlesGroup.traverse((obj) => {
        if (screenSpaceTypes.has(obj.userData?.type)) {
          obj.userData.screenSpaceBaseScale = obj.scale.clone()
        }
      })

      return handlesGroup
    },

    rebuildInteractionOverlays(metrics = null) {
      if (!this.threeScene) return
      const overlayGroup = this.interactionGroup || new THREE.Group()
      overlayGroup.userData = { type: 'interaction-overlays' }
      if (!overlayGroup.parent) this.threeScene.add(overlayGroup)

      const toRemove = []
      overlayGroup.traverse((obj) => {
        if (obj !== overlayGroup && obj.userData?.type) toRemove.push(obj)
      })
      toRemove.forEach((obj) => {
        if (obj.parent) obj.parent.remove(obj)
        this._disposeObject3D(obj)
      })

      overlayGroup.scale.set(1, 1, 1)
      overlayGroup.position.set(overlayGroup.position.x || 0, EYE_LEVEL, this.getInteractionOverlayZ())

      this._turntableHandleMesh = null
      this.interactionGroup = overlayGroup
      this.syncInteractionGroupTransform()
      this.handlesGroup = null
      this.dimGroup = null
      this.sizeSnapOverlayGroup = null

      if (this.interactive && metrics?.artW > 0 && metrics?.artH > 0) {
        this.handlesGroup = this.createInteractionHandles(metrics)
        const matWidth = metrics.matWidth || {}
        const maxMatWidth = Math.max(
          Number(matWidth.top) || 0,
          Number(matWidth.bottom) || 0,
          Number(matWidth.left) || 0,
          Number(matWidth.right) || 0
        )
        this.dimGroup = createDimensionLines(
          metrics.artW,
          metrics.artH,
          (metrics.frameWidth || 0) + maxMatWidth,
          metrics.outerWidth,
          metrics.outerHeight,
          this.overlayMaterials().dimension
        )
      }

      this.rebuildSizeSnapOverlay()
      if (this.handlesGroup) overlayGroup.add(this.handlesGroup)
      if (this.dimGroup) overlayGroup.add(this.dimGroup)
      if (this.sizeSnapOverlayGroup) overlayGroup.add(this.sizeSnapOverlayGroup)

      overlayGroup.traverse((obj) => {
        if (obj.isMesh
          && obj.userData?.type !== 'resize-handle'
          && obj.userData?.type !== 'turntable-handle'
          && !obj.userData?.type?.startsWith?.('source-')) {
          obj.castShadow = true
          obj.receiveShadow = true
        }
      })
    },

    createWallSnapOverlay() {
      if (!this.threeScene) return null
      const group = new THREE.Group()
      group.userData = { type: 'wall-snap-overlay' }
      group.visible = false

      const layout = this._dynamicWallLayout || this.getDynamicWallLayout()
      const wallWidth = layout.wallWidth || WALL_WIDTH
      const wallCenterX = Number.isFinite(layout.wallCenterX) ? layout.wallCenterX : this.getWallCenterX()
      const left = wallCenterX - wallWidth / 2
      const right = wallCenterX + wallWidth / 2
      const z = 0.35

      const makeLine = (axis, points) => {
        const geometry = new THREE.BufferGeometry().setFromPoints(points)
        const material = new THREE.LineBasicMaterial({
          color: 0x00e5ff,
          transparent: true,
          opacity: 0,
          depthTest: false,
        })
        const line = new THREE.Line(geometry, material)
        line.userData = {
          type: 'wall-snap-line',
          axis,
          baseOverlayOpacity: 0.35,
          overlayOpacity: 0.35,
        }
        group.add(line)
      }

      makeLine('y', [
        new THREE.Vector3(left, EYE_LEVEL, z),
        new THREE.Vector3(right, EYE_LEVEL, z),
      ])
      makeLine('x', [
        new THREE.Vector3(wallCenterX, 0, z),
        new THREE.Vector3(wallCenterX, WALL_HEIGHT, z),
      ])

      this.threeScene.add(group)
      this.wallSnapOverlayGroup = group
      return group
    },

    disposeWallSnapOverlay() {
      const group = this.wallSnapOverlayGroup
      if (!group) return
      if (group.parent) group.parent.remove(group)
      group.traverse((obj) => {
        if (obj.geometry?.dispose) obj.geometry.dispose()
        if (obj.material?.dispose) obj.material.dispose()
      })
      this.wallSnapOverlayGroup = null
    },

    rebuildWallSnapOverlay() {
      this.disposeWallSnapOverlay()
      this.createWallSnapOverlay()
    },

    updateWallSnapOverlayAnimation(deltaSeconds) {
      if (!this.wallSnapOverlayGroup) return

      const fadeRate = 7
      const delta = this.wallSnapOverlayTargetOpacity - this.wallSnapOverlayOpacity
      if (Math.abs(delta) < 0.001) {
        this.wallSnapOverlayOpacity = this.wallSnapOverlayTargetOpacity
      } else {
        const step = Math.min(Math.abs(delta), deltaSeconds * fadeRate)
        this.wallSnapOverlayOpacity += Math.sign(delta) * step
      }

      const isVisible = this.wallSnapOverlayOpacity > 0.001
      this.wallSnapOverlayGroup.visible = isVisible
      if (!isVisible) return

      this.wallSnapOverlayGroup.traverse((obj) => {
        if (!obj.isLine || !obj.material) return
        const axis = obj.userData?.axis
        const active = axis === 'x' ? this.activeWallSnap.x : axis === 'y' ? this.activeWallSnap.y : false
        const baseOpacity = active ? 1 : (obj.userData?.baseOverlayOpacity || 0.35)
        obj.userData.overlayOpacity = baseOpacity
        obj.material.opacity = baseOpacity * this.wallSnapOverlayOpacity
      })
    },

    getResizeOverlayConstraints() {
      return this.resizeOverlayConstraints ?? this.resizeConstraints ?? []
    },

    getResizeIntentDimensions() {
      return {
        width: this.pendingResizeIntent?.intentWidth ?? this.dragWidth ?? this.parsedArtWidth,
        height: this.pendingResizeIntent?.intentHeight ?? this.dragHeight ?? this.parsedArtHeight,
      }
    },

    hasNearResizeOverlayConstraints() {
      if (!this.isDragging || this.dragMode !== 'resize') return false
      const { width, height } = this.getResizeIntentDimensions()
      return this.getResizeOverlayConstraints().some((constraint) =>
        Boolean(this.evaluateConstraintForResize(width, height, constraint)),
      )
    },

    refreshSizeSnapOverlayVisibility() {
      this.sizeSnapOverlayTargetOpacity = (
        this.resizeSnapOverlayVisible ||
        this.hasNearResizeOverlayConstraints()
      ) ? 1 : 0
      this.updateSizeSnapOverlayLineOpacities()
    },

    updateSizeSnapOverlayLineOpacities() {
      if (!this.sizeSnapOverlayGroup) return
      const { width: intentWidth, height: intentHeight } = this.getResizeIntentDimensions()
      const isResizeDragging = this.isDragging && this.dragMode === 'resize'
      const matchesOrientation = (left, right) => {
        if (!left || !right) return true
        return Math.abs(Number(left.width) - Number(right.width)) < 0.001
          && Math.abs(Number(left.height) - Number(right.height)) < 0.001
      }
      const constraintStates = new Map()
      const getConstraintState = (constraint) => {
        if (!constraint) return null
        if (!constraintStates.has(constraint)) {
          constraintStates.set(
            constraint,
            this.evaluateConstraintForResize(intentWidth, intentHeight, constraint),
          )
        }
        return constraintStates.get(constraint)
      }

      this.sizeSnapOverlayGroup.traverse((obj) => {
        if (!obj.isLineLoop || !obj.material) return
        if (obj.userData?.type === 'size-snap-outline') {
          obj.userData.overlayOpacity = this.resizeSnapOverlayVisible ? 1 : 0
          return
        }
        if (obj.userData?.type === 'size-constraint-outline') {
          const constraint = obj.userData?.constraint
          const objOrientation = obj.userData?.orientation
          const state = getConstraintState(constraint)
          const matchesRelevantOrientation = state && matchesOrientation(objOrientation, state.orientation)
          const showForOverlayPreview = this.resizeSnapOverlayVisible && matchesRelevantOrientation
          const showForResize = isResizeDragging && matchesRelevantOrientation
          obj.userData.overlayOpacity = (showForOverlayPreview || showForResize) ? 1 : 0
        }
      })
    },

    updateSizeSnapOverlayAnimation(deltaSeconds) {
      if (!this.sizeSnapOverlayGroup) return

      const fadeRate = 7
      const delta = this.sizeSnapOverlayTargetOpacity - this.sizeSnapOverlayOpacity
      if (Math.abs(delta) < 0.001) {
        this.sizeSnapOverlayOpacity = this.sizeSnapOverlayTargetOpacity
      } else {
        const step = Math.min(Math.abs(delta), deltaSeconds * fadeRate)
        this.sizeSnapOverlayOpacity += Math.sign(delta) * step
      }

      const isVisible = this.sizeSnapOverlayOpacity > 0.001
      this.sizeSnapOverlayGroup.visible = isVisible
      if (!isVisible) return

      this.sizeSnapOverlayGroup.traverse((obj) => {
        if (!obj.isLineLoop || !obj.material) return
        obj.material.opacity = (obj.userData?.overlayOpacity || 0) * this.sizeSnapOverlayOpacity
      })
    },

    setActiveResizeConstraint(nextState) {
      const prev = this.activeResizeConstraint
      const prevConstraint = prev?.constraint || null
      const nextConstraint = nextState?.constraint || null
      const sameOrientation = (!prev?.orientation && !nextState?.orientation) || (
        prev?.orientation && nextState?.orientation
        && Math.abs(Number(prev.orientation.width) - Number(nextState.orientation.width)) < 0.001
        && Math.abs(Number(prev.orientation.height) - Number(nextState.orientation.height)) < 0.001
      )
      const unchanged = prevConstraint === nextConstraint && Boolean(prev?.exceeded) === Boolean(nextState?.exceeded) && sameOrientation
      this.activeResizeConstraint = nextState || null
      if (!unchanged) {
        this.refreshSizeSnapOverlayVisibility()
      }
    },

    resizeConstraintMessageKey(constraint) {
      const source = String(constraint?.source || 'unknown').trim().toLowerCase()
      const name = String(constraint?.optionName || constraint?.label || 'default').trim().toLowerCase()
      return `resize-constraint-${source}-${name.replace(/[^a-z0-9]+/g, '-')}`
    },

    clearAllResizeConstraintMessages() {
      this.$emit('viewer-message', { removePrefix: 'resize-constraint-' })
      this.$emit('viewer-message', { key: 'resize-max', remove: true })
      this._activeResizeConstraintMessageKeys = new Set()
    },

    syncExceededResizeConstraintMessages(exceededStates = []) {
      const nextKeys = new Set()
      exceededStates.forEach((state) => {
        const constraint = state?.constraint
        if (!constraint) return
        const key = this.resizeConstraintMessageKey(constraint)
        nextKeys.add(key)
        this.$emit('viewer-message', {
          key,
          variant: constraint.variant || (constraint.severity === 'warning' ? 'warning' : 'danger'),
          text: this.formatResizeConstraintMessage(state),
        })
      })

      this._activeResizeConstraintMessageKeys.forEach((key) => {
        if (!nextKeys.has(key)) {
          this.$emit('viewer-message', { key, remove: true })
        }
      })
      this._activeResizeConstraintMessageKeys = nextKeys
    },

    emitResizeConstraintState(state) {
      this.$emit('resize-constraint-state', state
        ? {
            exceeded: Boolean(state.exceeded),
            source: state.constraint?.source || '',
            variant: state.constraint?.variant || (state.constraint?.severity === 'warning' ? 'warning' : 'danger'),
            constraint: state.constraint,
          }
        : { exceeded: false })
    },

    syncResizeConstraintState(width, height, { clamped = false, constraint = null } = {}) {
      const constraintState = this.getResizeConstraintState(width, height, {
        clamped,
        constraint,
      })
      const exceededFrameStates = this.getExceededResizeConstraintStates(width, height, {
        clamped,
        clampConstraint: constraint,
      })
      const matConstraintState = this.getResizeConstraintState(width, height, {
        source: 'mat',
      })
      this.emitResizeConstraintState(matConstraintState || { exceeded: false, constraint: { source: 'mat' } })
      this.setActiveResizeConstraint(constraintState)
      this.syncExceededResizeConstraintMessages(exceededFrameStates)
      return { constraintState, matConstraintState, exceededFrameStates }
    },

    clearResizeConstraintState({ notifyConstraintState = false } = {}) {
      if (notifyConstraintState) {
        this.emitResizeConstraintState({ exceeded: false, constraint: { source: 'mat' } })
      }
      this.clearAllResizeConstraintMessages()
      this.setActiveResizeConstraint(null)
    },

    evaluateConstraintForResize(width, height, candidate, { forceExceeded = false } = {}) {
      const parsedWidth = Number(width)
      const parsedHeight = Number(height)
      if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight)) return null

      const orientations = Array.isArray(candidate.maxOrientations) && candidate.maxOrientations.length
        ? candidate.maxOrientations
        : [{ width: candidate.maxWidth ?? candidate.width, height: candidate.maxHeight ?? candidate.height }]
      const validOrientations = orientations
        .map(orientation => ({
          width: Number(orientation.width ?? orientation.maxWidth),
          height: Number(orientation.height ?? orientation.maxHeight),
        }))
        .filter(orientation => Number.isFinite(orientation.width) && Number.isFinite(orientation.height))
      if (!validOrientations.length) return null

      const preferWide = parsedWidth >= parsedHeight
      const chooseRelevantOrientation = (items) => {
        const fitting = items.filter(orientation =>
          parsedWidth <= orientation.width && parsedHeight <= orientation.height
        )
        if (fitting.length) {
          return fitting.reduce((best, item) => {
            if (!best) return item
            const itemPrimary = preferWide ? item.width : item.height
            const bestPrimary = preferWide ? best.width : best.height
            if (itemPrimary !== bestPrimary) return itemPrimary > bestPrimary ? item : best
            const itemArea = item.width * item.height
            const bestArea = best.width * best.height
            return itemArea > bestArea ? item : best
          }, null)
        }

        return items.reduce((best, item) => {
          const fitWidth = Math.min(parsedWidth, item.width)
          const fitHeight = Math.min(parsedHeight, item.height)
          const score = fitWidth * fitHeight
          const distance = Math.hypot(parsedWidth - fitWidth, parsedHeight - fitHeight)
          if (!best || score > best.score || (score === best.score && distance < best.distance)) {
            return { ...item, score, distance }
          }
          return best
        }, null)
      }

      const orientation = chooseRelevantOrientation(validOrientations)
      if (!orientation) return null

      const widthDelta = orientation.width - parsedWidth
      const heightDelta = orientation.height - parsedHeight
      const fitsRelevantOrientation = widthDelta >= 0 && heightDelta >= 0
      const exceeded = forceExceeded || !fitsRelevantOrientation
      const near = exceeded || (
        fitsRelevantOrientation &&
        (
          widthDelta <= RESIZE_CONSTRAINT_VISIBILITY_THRESHOLD ||
          heightDelta <= RESIZE_CONSTRAINT_VISIBILITY_THRESHOLD
        )
      )
      if (!near) return null

      return {
        constraint: candidate,
        orientation: { width: orientation.width, height: orientation.height },
        exceeded,
        distance: Math.min(Math.abs(widthDelta), Math.abs(heightDelta)),
      }
    },

    getExceededResizeConstraintStates(width, height, { clamped = false, clampConstraint = null } = {}) {
      const constraints = (this.resizeConstraints || [])
        .filter(item => item?.type === 'max' && item?.source === 'frame')
      const results = []
      constraints.forEach((candidate) => {
        const state = this.evaluateConstraintForResize(width, height, candidate, {
          forceExceeded: clamped && clampConstraint === candidate,
        })
        if (state?.exceeded) results.push(state)
      })
      return results
    },

    getResizeConstraintState(width, height, { clamped = false, constraint = null, source = null } = {}) {
      const constraints = (constraint ? [constraint] : (this.resizeConstraints || []).filter(item => item?.type === 'max'))
        .filter(item => !source || item?.source === source)
      if (!constraints.length) return null

      let best = null
      constraints.forEach((candidate) => {
        const state = this.evaluateConstraintForResize(width, height, candidate, {
          forceExceeded: clamped && constraint === candidate,
        })
        if (!state) return
        if (!best || (state.exceeded && !best.exceeded) || state.distance < best.distance) {
          best = state
        }
      })

      return best ? { constraint: best.constraint, orientation: best.orientation, exceeded: best.exceeded } : null
    },

    formatResizeConstraintMessage(state) {
      const constraint = state?.constraint
      if (!constraint) return ''
      const maxWidth = Number(constraint.materialMaxWidth ?? constraint.maxWidth ?? constraint.width)
      const maxHeight = Number(constraint.materialMaxHeight ?? constraint.maxHeight ?? constraint.height)
      const fmt = (value) => Number(value).toFixed(2).replace(/\.00$/, '')
      if (constraint.message) return String(constraint.message)
      const label = constraint.label || 'Selected material'
      if (Number.isFinite(maxWidth) && Number.isFinite(maxHeight)) {
        return `${label} maximum size: ${fmt(maxWidth)}" x ${fmt(maxHeight)}"`
      }
      return `${label} maximum size reached`
    },

    easeOutBack(t) {
      const c1 = 1.15
      const c3 = c1 + 1
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
    },

    getResizeSnapTarget(width, height) {
      const normalizedCandidates = (this.resizeSnapCandidates || [])
        .map((candidate) => ({
          width: Math.max(1, Math.round((Number(candidate?.value?.w) || 0) * 4) / 4),
          height: Math.max(1, Math.round((Number(candidate?.value?.h) || 0) * 4) / 4),
        }))
        .filter((candidate) => Number.isFinite(candidate.width) && Number.isFinite(candidate.height))

      if (!normalizedCandidates.length) {
        this.activeResizeSnap = null
        return null
      }

      const rawWidth = Number(width)
      const rawHeight = Number(height)
      const getDistance = (candidate) => Math.hypot(rawWidth - candidate.width, rawHeight - candidate.height)
      const nearest = normalizedCandidates.reduce((best, candidate) => {
        if (!best) return candidate
        return getDistance(candidate) < getDistance(best) ? candidate : best
      }, null)

      if (!nearest) {
        this.activeResizeSnap = null
        return null
      }

      if (getDistance(nearest) > SNAP_ENGAGEMENT_THRESHOLD) {
        this.activeResizeSnap = null
        return null
      }

      const active = normalizedCandidates.find((candidate) =>
        candidate.width === this.activeResizeSnap?.width && candidate.height === this.activeResizeSnap?.height
      )

      if (!active) {
        this.activeResizeSnap = nearest
        return nearest
      }

      const activeDistance = getDistance(active)
      const nearestDistance = getDistance(nearest)
      const switched = nearest.width !== active.width || nearest.height !== active.height
      if (switched && nearestDistance + SNAP_SWITCH_HYSTERESIS < activeDistance) {
        this.activeResizeSnap = nearest
      }

      return this.activeResizeSnap || nearest
    },

    beginFormResizePreview() {
      if (this._formResizePreviewActive) return
      this.debouncedRebuild?.cancel?.()
      this._formResizePreviewActive = true
    },

    applyFormResizePreview(width, height) {
      const nextWidth = Number(width)
      const nextHeight = Number(height)
      if (!(nextWidth > 0) || !(nextHeight > 0)) return
      this.beginFormResizePreview()
      this.applyResizeVisual(nextWidth, nextHeight)
      if (this.collisionEnabled) this._applyResizeCollision()
    },

    finishFormResizePreview({ commit = false } = {}) {
      if (!this._formResizePreviewActive) return []
      if (commit && this.collisionEnabled) this._applyResizeCollision()
      const positions = commit ? this._collectMovedRenderItemPositions() : []
      const selectedKey = String(this._selectedRenderKey ?? '')

      this._formResizePreviewActive = false
      if (!commit && this._renderItemInstances) {
        const selected = this._renderItemInstances.get(selectedKey)
        const baseDims = selected?.baseBuiltDims
        if (baseDims?.artW > 0 && baseDims?.artH > 0) {
          this.applyResizeVisual(baseDims.artW, baseDims.artH)
        }
        this._renderItemInstances.forEach((instance, key) => {
          if (String(key) === selectedKey || !instance?.group || !instance.basePosition) return
          instance.group.position.x = instance.basePosition.x
          instance.group.position.y = instance.basePosition.y
        })
        this.markNeedsRender()
      }
      if (positions.length) this.$emit('resize-collision-positions', { positions })
      return positions
    },

    applyResizeVisual(width, height) {
      this.markNeedsRender()  // live drag geometry
      const sourceAdj = this.adjustSourceForResize(width, height)
      this.updateSceneDimensions(width, height, sourceAdj)
      this.applySelectedRenderItemResizeVisual(width, height)
      // Neighbor push for resize runs in the render loop (so it keeps easing even when the cursor
      // pauses); here we only need the geometry + camera updates.
      if (this.isDragging && this.dragMode === 'resize' && this.autoFramingAllowed()) {
        this.fitCamera()
      }
    },

    emitResizeDims(width, height, intentMeta = null) {
      const sourceAdj = this.adjustSourceForResize(width, height)
      if (!this.lastEmittedDims || this.lastEmittedDims.width !== width || this.lastEmittedDims.height !== height) {
        this.lastEmittedDims = { width, height }
        this.$emit('resize', { width, height, ...(intentMeta || {}) })
      }
      if (sourceAdj) {
        this.$emit('source-transform', sourceAdj)
      }
    },

    commitResizeDims(width, height) {
      const intentMeta = this.pendingResizeIntent
      this.pendingResizeIntent = null
      this.applyResizeVisual(width, height)
      this.emitResizeDims(width, height, intentMeta)
    },

    applySelectedRenderItemResizeVisual(width, height) {
      const instance = this.getSelectedRenderInstance()
      const baseDims = instance?.baseBuiltDims || instance?.builtDims
      const nextW = Number(width)
      const nextH = Number(height)
      if (!Number.isFinite(nextW) || !Number.isFinite(nextH) || nextW <= 0 || nextH <= 0) return
      const source = this.adjustSourceForResize(nextW, nextH) || {
        scaleX: this.parsedArtworkScaleX,
        scaleY: this.parsedArtworkScaleY,
        rotation: this.parsedArtworkRotation,
        offsetLeft: this.parsedArtworkOffsetLeft,
        offsetTop: this.parsedArtworkOffsetTop,
      }
      applyDesignStudioItemResizeVisual(instance, {
        width: nextW,
        height: nextH,
        sourceTransform: source,
        imageAspect: this._getImageAspect(),
        artworkTexture: this._getArtworkSourceTexture(),
        substrateBackColor: this.substrateBackData?.color,
        baseDims,
        baseScale: instance?.baseScale,
      })
      // The geometry is rebuilt to the new size, but builtDims/specs aren't — record the live
      // finished footprint so collision (resize push, and a later move of this item) uses the
      // current size instead of the stale original. Cleared naturally when the item is rebuilt.
      if (instance) {
        const metrics = resolveDesignStudioItemSceneMetrics({
          builtDims: baseDims || instance.builtDims || {},
          overrides: { artWidth: nextW, artHeight: nextH },
        })
        instance.liveFootprint = {
          w: Number(metrics.outerWidth) || nextW,
          h: Number(metrics.outerHeight) || nextH,
        }
      }
    },

    applyMatWidthVisual(matWidthsArray) {
      this.markNeedsRender()  // live mat preview
      const instance = this.getSelectedRenderInstance()
      const source = {
        scaleX: this.parsedArtworkScaleX,
        scaleY: this.parsedArtworkScaleY,
        rotation: this.parsedArtworkRotation,
        offsetLeft: this.parsedArtworkOffsetLeft,
        offsetTop: this.parsedArtworkOffsetTop,
      }
      applyDesignStudioItemMatWidthVisual(instance, {
        matWidthsArray,
        sourceTransform: source,
        imageAspect: this._getImageAspect(),
        artworkTexture: this._getArtworkSourceTexture(),
        substrateBackColor: this.substrateBackData?.color,
      })
    },

    startResizeSnapAnimation(targetWidth, targetHeight) {
      const currentWidth = Number(this.dragWidth != null ? this.dragWidth : this.parsedArtWidth)
      const currentHeight = Number(this.dragHeight != null ? this.dragHeight : this.parsedArtHeight)
      const nextWidth = Number(targetWidth)
      const nextHeight = Number(targetHeight)
      if (!Number.isFinite(nextWidth) || !Number.isFinite(nextHeight)) return

      const activeAnimation = this.resizeSnapAnimation
      if (
        activeAnimation
        && Math.abs(activeAnimation.toWidth - nextWidth) < 0.001
        && Math.abs(activeAnimation.toHeight - nextHeight) < 0.001
      ) {
        return
      }

      this.pendingDragDims = null
      if (this.dragRafId != null) {
        cancelAnimationFrame(this.dragRafId)
        this.dragRafId = null
      }

      if (Math.abs(currentWidth - nextWidth) < 0.001 && Math.abs(currentHeight - nextHeight) < 0.001) {
        this.resizeSnapAnimation = null
        this.dragWidth = nextWidth
        this.dragHeight = nextHeight
        this.commitResizeDims(nextWidth, nextHeight)
        return
      }

      this.emitResizeDims(nextWidth, nextHeight)
      this.resizeSnapAnimation = {
        fromWidth: currentWidth,
        fromHeight: currentHeight,
        toWidth: nextWidth,
        toHeight: nextHeight,
        startTime: performance.now(),
        duration: 180,
      }
    },

    updateResizeSnapAnimation(now) {
      if (!this.resizeSnapAnimation || !this.isDragging || this.dragMode !== 'resize') return

      const anim = this.resizeSnapAnimation
      const t = Math.min(1, (now - anim.startTime) / anim.duration)
      const eased = this.easeOutBack(t)
      const width = anim.fromWidth + (anim.toWidth - anim.fromWidth) * eased
      const height = anim.fromHeight + (anim.toHeight - anim.fromHeight) * eased

      this.dragWidth = width
      this.dragHeight = height
      this.applyResizeVisual(width, height)

      if (t >= 1) {
        this.resizeSnapAnimation = null
      }
    },

    _resizeDimTooltip(w, h) {
      const fmt = (v) => Number(v).toFixed(2)
      const outer = this.resolveOuterDimensions(w, h)
      return `Image: ${fmt(w)}" × ${fmt(h)}"\nFinished: ${fmt(outer.width)}" × ${fmt(outer.height)}"`
    },

    resolveOuterDimensions(artWidth = this.parsedArtWidth, artHeight = this.parsedArtHeight) {
      const artW = Number(artWidth)
      const artH = Number(artHeight)
      const instance = this.getSelectedRenderInstance()
      const dragBaseDims = this.isDragging && this.dragMode === 'resize' ? instance?.baseBuiltDims : null
      const builtDims = dragBaseDims || instance?.builtDims || null
      const metrics = builtDims
        ? resolveDesignStudioItemSceneMetrics({
          builtDims,
          overrides: { artWidth: artW, artHeight: artH },
        })
        : resolveDesignStudioItemSceneMetrics({
          rendererProps: {
            ...this.buildScalarRenderSpec().rendererProps,
            artWidth: artW,
            artHeight: artH,
          },
        })
      return {
        width: Number.isFinite(Number(metrics.outerWidth)) ? Number(metrics.outerWidth) : artW,
        height: Number.isFinite(Number(metrics.outerHeight)) ? Number(metrics.outerHeight) : artH,
      }
    },

    artworkDimTooltip(data = {}) {
      const fmt = (v) => Number(v).toFixed(2)
      const artW = Number.isFinite(Number(data.itemArtWidth)) ? Number(data.itemArtWidth) : this.parsedArtWidth
      const artH = Number.isFinite(Number(data.itemArtHeight)) ? Number(data.itemArtHeight) : this.parsedArtHeight
      const resolvedOuter = this.resolveOuterDimensions(artW, artH)
      const outerW = Number.isFinite(Number(data.itemOuterWidth)) ? Number(data.itemOuterWidth) : resolvedOuter.width
      const outerH = Number.isFinite(Number(data.itemOuterHeight)) ? Number(data.itemOuterHeight) : resolvedOuter.height
      return `Image: ${fmt(artW)}" × ${fmt(artH)}"\nFinished: ${fmt(outerW)}" × ${fmt(outerH)}"`
    },

    _getImageAspect() {
      const instance = this.getSelectedRenderInstance()
      const img = instance?.refs?.artworkMesh?.userData?.artworkTexture?.image
        || instance?.refs?.canvasWrapMesh?.userData?.artworkTexture?.image
        || instance?.refs?.substrateBackMesh?.userData?.artworkTexture?.image
        || instance?.refs?.artworkMesh?.material?.map?.image
      if (img) {
        const w = img.width || img.naturalWidth
        const h = img.height || img.naturalHeight
        if (w > 0 && h > 0) return w / h
      }
      return this.parsedArtWidth / this.parsedArtHeight
    },

    _getArtworkDisplayTexture() {
      const instance = this.getSelectedRenderInstance()
      return instance?.refs?.artworkMesh?.material?.map
        || instance?.refs?.canvasWrapMesh?.userData?.artworkTexture
        || instance?.refs?.substrateBackMesh?.userData?.artworkTexture
        || null
    },

    _getArtworkSourceTexture() {
      const instance = this.getSelectedRenderInstance()
      return instance?.refs?.artworkMesh?.userData?.artworkTexture
        || instance?.refs?.canvasWrapMesh?.userData?.artworkTexture
        || instance?.refs?.substrateBackMesh?.userData?.artworkTexture
        || instance?.refs?.artworkMesh?.material?.map?.userData?.sourceTexture
        || this._getArtworkDisplayTexture()
        || null
    },

    applySelectedRenderItemSourceVisual(transform) {
      const instance = this.getSelectedRenderInstance()
      const baseDims = instance?.baseBuiltDims || instance?.builtDims
      const artW = Number(baseDims?.artW)
      const artH = Number(baseDims?.artH)
      if (!instance?.refs || !Number.isFinite(artW) || !Number.isFinite(artH) || artW <= 0 || artH <= 0) return
      applyDesignStudioItemSourceVisual(instance, {
        artWidth: artW,
        artHeight: artH,
        transform,
        imageAspect: this._getImageAspect(),
        artworkTexture: this._getArtworkSourceTexture(),
        substrateBackColor: this.substrateBackData?.color,
        baseDims,
      })
    },

    _applySourceAndEmit(transform) {
      this.applySelectedRenderItemSourceVisual(transform)
      this._updateSourceGhost(transform)
      this.$emit('source-transform', transform)
    },

    handleSourceMoveDrag(dxInches, dyInches) {
      const s = this.dragSourceStart
      if (!s) return
      const rawAspect = this._getImageAspect()
      const imgAspect = Math.abs(s.rotation || 0) % 180 === 90 ? 1 / rawAspect : rawAspect
      const baseCoverW = Math.max(s.artWidth, s.artHeight * imgAspect)
      const baseCoverH = baseCoverW / imgAspect
      const uw = baseCoverW * Math.abs(s.scaleX || 1)
      const uh = baseCoverH * Math.abs(s.scaleY || s.scaleX || 1)
      const maxOL = (uw - s.artWidth) / 2
      const maxOT = (uh - s.artHeight) / 2
      const ol = Math.min(Math.max(-maxOL, s.offsetLeft + dxInches), maxOL)
      const ot = Math.min(Math.max(-maxOT, s.offsetTop + dyInches), maxOT)
      const clampedDx = ol - s.offsetLeft
      const clampedDy = ot - s.offsetTop
      const transform = {
        scaleX: s.scaleX,
        scaleY: s.scaleY,
        rotation: s.rotation,
        offsetLeft: ol,
        offsetTop: ot,
        dragDeltaX: clampedDx,
        dragDeltaY: clampedDy,
      }
      this.pendingSourceTransform = transform
      if (this.sourceDragRafId == null) {
        this.sourceDragRafId = requestAnimationFrame(() => this.applyPendingSourceTransform())
      }
    },

    handleSourceScaleDrag(dxInches) {
      const s = this.dragSourceStart
      if (!s) return
      const handleBaseOffset = Math.max(0.001, inches(s.artWidth) * 0.25)
      const startMag = Math.max(1, Math.abs(s.scaleX || 1))
      const requestedHandleScale = Math.max(0.1, (handleBaseOffset + dxInches) / handleBaseOffset)
      const nextScale = Math.max(1, startMag * requestedHandleScale)
      const scaleHandleScale = nextScale / startMag
      const signX = (s.scaleX || 1) < 0 ? -1 : 1
      const signY = (s.scaleY || s.scaleX || 1) < 0 ? -1 : 1
      const nextScaleX = signX * nextScale
      const nextScaleY = signY * nextScale
      const { offsetLeft: ol, offsetTop: ot } = this.getTransformForVisibleCenter(s, {
        scaleX: nextScaleX,
        scaleY: nextScaleY,
        rotation: s.rotation,
        artWidth: s.artWidth,
        artHeight: s.artHeight,
      })
      const transform = {
        scale: nextScale,
        scaleX: nextScaleX,
        scaleY: nextScaleY,
        rotation: s.rotation,
        offsetLeft: ol,
        offsetTop: ot,
        scaleHandleScale,
      }
      this.pendingSourceTransform = transform
      if (this.sourceDragRafId == null) {
        this.sourceDragRafId = requestAnimationFrame(() => this.applyPendingSourceTransform())
      }
    },

    getTransformForVisibleCenter(startTransform, nextTransform) {
      const artW = Number(nextTransform.artWidth ?? startTransform.artWidth)
      const artH = Number(nextTransform.artHeight ?? startTransform.artHeight)
      const imageAspect = this._getImageAspect()
      const currentCrop = getCropFromScale(
        startTransform.artWidth,
        startTransform.artHeight,
        startTransform.scaleX ?? startTransform.scale,
        startTransform.offsetLeft,
        startTransform.offsetTop,
        imageAspect,
        6,
        startTransform.scaleY ?? startTransform.scaleX ?? startTransform.scale,
        startTransform.rotation
      )
      const visibleCenterX = currentCrop.cropLeft + startTransform.artWidth / 2
      const visibleCenterY = currentCrop.cropTop + startTransform.artHeight / 2
      const relX = currentCrop.fullWidth > 0 ? (visibleCenterX - currentCrop.fullWidth / 2) / currentCrop.fullWidth : 0
      const relY = currentCrop.fullHeight > 0 ? (visibleCenterY - currentCrop.fullHeight / 2) / currentCrop.fullHeight : 0
      const nextCrop = getCropFromScale(
        artW,
        artH,
        nextTransform.scaleX ?? nextTransform.scale,
        0,
        0,
        imageAspect,
        6,
        nextTransform.scaleY ?? nextTransform.scaleX ?? nextTransform.scale,
        nextTransform.rotation
      )
      const maxLeft = Math.max(0, nextCrop.fullWidth - artW)
      const maxTop = Math.max(0, nextCrop.fullHeight - artH)
      const nextVisibleCenterX = nextCrop.fullWidth / 2 + relX * nextCrop.fullWidth
      const nextVisibleCenterY = nextCrop.fullHeight / 2 + relY * nextCrop.fullHeight
      const cropLeft = Math.min(Math.max(0, nextVisibleCenterX - artW / 2), maxLeft)
      const cropTop = Math.min(Math.max(0, nextVisibleCenterY - artH / 2), maxTop)
      return {
        offsetLeft: Math.round((((nextCrop.fullWidth - artW) / 2) - cropLeft) * 100) / 100,
        offsetTop: Math.round((((nextCrop.fullHeight - artH) / 2) - cropTop) * 100) / 100,
      }
    },

    applyPendingSourceTransform() {
      this.sourceDragRafId = null
      const transform = this.pendingSourceTransform
      this.pendingSourceTransform = null
      const s = this.dragSourceStart
      if (!transform || !s) return
      this._applySourceAndEmit(transform)
      this.updateSceneDimensions(this.parsedArtWidth, this.parsedArtHeight, transform)
    },

    adjustSourceForResize(newW, newH) {
      const s = this.dragSourceStart
      if (!s) return null
      const rawAspect = this._getImageAspect()
      const imgAspect = Math.abs(s.rotation || 0) % 180 === 90 ? 1 / rawAspect : rawAspect
      const oldBaseCoverW = Math.max(s.artWidth, s.artHeight * imgAspect)
      const baseCoverW = Math.max(newW, newH * imgAspect)
      const baseCoverH = baseCoverW / imgAspect
      // Scale offsets proportionally to the change in base cover size so the
      // visual center of the composition stays fixed as the opening is resized.
      const coverRatio = oldBaseCoverW > 0 ? baseCoverW / oldBaseCoverW : 1
      const uw = baseCoverW * Math.abs(s.scaleX || 1)
      const uh = baseCoverH * Math.abs(s.scaleY || s.scaleX || 1)
      const maxOL = (uw - newW) / 2
      const maxOT = (uh - newH) / 2
      const ol = Math.min(Math.max(-maxOL, s.offsetLeft * coverRatio), maxOL)
      const ot = Math.min(Math.max(-maxOT, s.offsetTop * coverRatio), maxOT)
      return { scaleX: s.scaleX, scaleY: s.scaleY, rotation: s.rotation, offsetLeft: ol, offsetTop: ot }
    },

    // ------------------------------------------------------------------
    // Uncropped image ghost overlay (visible during source drag)
    // ------------------------------------------------------------------

    _showSourceGhost() {
      this._hideSourceGhost()

      const tex = this._getArtworkSourceTexture()
      if (!tex) return

      const artW = this.parsedArtWidth
      const artH = this.parsedArtHeight
      const imgAspect = this._getImageAspect()
      const crop = getCropFromScale(
        artW, artH,
        this.parsedArtworkScaleX,
        this.parsedArtworkOffsetLeft,
        this.parsedArtworkOffsetTop,
        imgAspect,
        3,
        this.parsedArtworkScaleY,
        this.parsedArtworkRotation
      )

      const transform = {
        scaleX: this.parsedArtworkScaleX,
        scaleY: this.parsedArtworkScaleY,
        rotation: this.parsedArtworkRotation,
      }
      const ghostTex = this.createSourceGhostTexture(tex, transform, crop)
      if (!ghostTex) return

      const ghostMat = new THREE.MeshBasicMaterial({
        map: ghostTex,
        transparent: true,
        opacity: 0,
        depthTest: false,
        side: THREE.FrontSide,
      })

      const ghostGeo = new THREE.PlaneGeometry(1, 1)
      const ghostMesh = new THREE.Mesh(ghostGeo, ghostMat)

      ghostMesh.scale.set(inches(crop.fullWidth), inches(crop.fullHeight), 1)
      ghostMesh.rotation.z = 0
      const ghostX = inches((crop.fullWidth - artW) / 2 - crop.cropLeft)
      const ghostY = inches((artH - crop.fullHeight) / 2 + crop.cropTop)
      ghostMesh.position.set(ghostX, ghostY, 0.15)

      ghostMesh.renderOrder = 100
      ghostMesh.userData = { type: 'source-ghost' }
      ghostMesh.raycast = () => {}

      this.sourceGhostMesh = ghostMesh
      if (this.interactionGroup) {
        this.interactionGroup.add(ghostMesh)
      }

      const mat = this.sourceGhostMesh.material
      const startTime = performance.now()
      const duration = 400
      const startOpacity = 0
      const endOpacity = 0.25

      if (this.sourceGhostFadeId != null) {
        cancelAnimationFrame(this.sourceGhostFadeId)
      }

      const animate = (now) => {
        // Drives its own rAF, so the render loop only knows to draw if asked.
        this.markNeedsRender()
        const t = Math.min((now - startTime) / duration, 1)
        // easeOutQuad
        const eased = 1 - (1 - t) * (1 - t)
        const nextOpacity = startOpacity + (endOpacity - startOpacity) * eased
        if (mat) {
          mat.opacity = nextOpacity
          mat.needsUpdate = true
        }
        if (t < 1 && this.sourceGhostMesh) {
          this.sourceGhostFadeId = requestAnimationFrame(animate)
        } else {
          this.sourceGhostFadeId = null
        }
      }
      this.sourceGhostFadeId = requestAnimationFrame(animate)
    },

    sourceGhostTextureOptions(transform, crop = null) {
      if (!crop?.fullWidth || !crop?.fullHeight) return null
      return {
        planeWidth: crop.fullWidth,
        planeHeight: crop.fullHeight,
        fullWidth: crop.fullWidth,
        fullHeight: crop.fullHeight,
        cropLeft: 0,
        cropTop: 0,
        scaleX: transform.scaleX ?? transform.scale,
        scaleY: transform.scaleY ?? transform.scaleX ?? transform.scale,
        rotation: transform.rotation,
      }
    },

    createSourceGhostTexture(sourceTexture, transform, crop = null) {
      const opts = this.sourceGhostTextureOptions(transform, crop)
      return sourceTexture && opts ? createArtworkUvTransformTexture(sourceTexture, opts) : null
    },

    _updateSourceGhost(transform) {
      if (!this.sourceGhostMesh) return

      const artW = this.parsedArtWidth
      const artH = this.parsedArtHeight
      const imgAspect = this._getImageAspect()
      const crop = getCropFromScale(
        artW, artH,
        transform.scaleX ?? transform.scale, transform.offsetLeft, transform.offsetTop,
        imgAspect,
        3,
        transform.scaleY ?? transform.scaleX ?? transform.scale,
        transform.rotation
      )

      if (this.dragMode !== 'source-move') {
        const sourceTexture = this._getArtworkSourceTexture()
        const textureOpts = this.sourceGhostTextureOptions(transform, crop)
        const nextMap = textureOpts
          ? resolveArtworkUvTransformTexture(this.sourceGhostMesh.material?.map, sourceTexture, textureOpts)
          : null
        if (nextMap && this.sourceGhostMesh.material) {
          const prevMap = this.sourceGhostMesh.material.map
          this.sourceGhostMesh.material.map = nextMap
          if (prevMap !== nextMap) prevMap?.dispose?.()
          this.sourceGhostMesh.material.needsUpdate = true
        }
      }
      this.sourceGhostMesh.scale.set(inches(crop.fullWidth), inches(crop.fullHeight), 1)
      this.sourceGhostMesh.rotation.z = 0
      const ghostX = inches((crop.fullWidth - artW) / 2 - crop.cropLeft)
      const ghostY = inches((artH - crop.fullHeight) / 2 + crop.cropTop)
      this.sourceGhostMesh.position.set(ghostX, ghostY, 0.15)
    },

    _hideSourceGhost() {
      if (!this.sourceGhostMesh) return

      const mesh = this.sourceGhostMesh
      const mat = mesh.material
      const startOpacity = mat?.opacity ?? 0
      const endOpacity = 0
      const startTime = performance.now()
      const duration = 200

      if (this.sourceGhostFadeId != null) {
        cancelAnimationFrame(this.sourceGhostFadeId)
      }

      const animate = (now) => {
        // Drives its own rAF, so the render loop only knows to draw if asked.
        this.markNeedsRender()
        const t = Math.min((now - startTime) / duration, 1)
        // easeInQuad
        const eased = t * t
        const nextOpacity = startOpacity + (endOpacity - startOpacity) * eased
        if (mat) {
          mat.opacity = nextOpacity
          mat.needsUpdate = true
        }

        if (t < 1 && this.sourceGhostMesh === mesh) {
          this.sourceGhostFadeId = requestAnimationFrame(animate)
        } else {
          this.sourceGhostFadeId = null
          if (mesh.parent) {
            mesh.parent.remove(mesh)
          }
          mesh.geometry?.dispose()
          mesh.material?.map?.dispose()
          mesh.material?.dispose()
          if (this.sourceGhostMesh === mesh) {
            this.sourceGhostMesh = null
          }
        }
      }

      this.sourceGhostFadeId = requestAnimationFrame(animate)
    },

    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    enableCanvasRendering() {
      if (this.threeRenderer) {
        this.startRenderLoop()
        return
      }
      this.initThree()
      this.rebuildScene()
      this.startRenderLoop()
    },

    initThree() {
      const container = this.$refs.container
      const canvas = this.$refs.canvas
      if (!container || !canvas) return

      const rect = container.getBoundingClientRect()
      const width = Math.max(rect.width, 200)
      const height = Math.max(rect.height, 200)

      // Renderer with soft shadow mapping
      this.threeRenderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        preserveDrawingBuffer: true,
        alpha: false,
        logarithmicDepthBuffer: true,
      })
      // three calls getProgramInfoLog()/getShaderInfoLog() after every shader
      // link when this is on, and those force a synchronous GPU readback that
      // stalls the main thread. Materials recompile whenever the scene's
      // substrate/frame/mat set changes, so switching items paid that stall
      // repeatedly — it was the single largest cost when stepping through a
      // project. Keep it for the 3D debug view, where the diagnostics are why
      // you're there.
      this.threeRenderer.debug.checkShaderErrors = this.debugLighting === true
      this.threeRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      this.threeRenderer.setSize(width, height)
      this.threeRenderer.outputColorSpace = THREE.SRGBColorSpace
      setupShadowRenderer(this.threeRenderer)
      setupPCSS()

      // Scene
      this.threeScene = new THREE.Scene()
      this.updateBackground()
      setupSceneEnvironment(this.threeRenderer, this.threeScene)

      // Perspective camera — 65° FOV for a natural, wide-angle gallery feel
      this.threeCamera = createTelephotoCamera(width, height, 65)

      // Lighting: IBL handles ambient; single directional key + spotlight cast PCSS shadows
      this.sceneLights = createSingleFrameLighting({
        centerY: EYE_LEVEL,
        ceilingHeight: CEILING_HEIGHT,
      })
      this.sceneLights.forEach(l => this.threeScene.add(l))

      // Wall backdrop: gallery wall running floor to ceiling, frame at eye level
      this.updateWall({ updateRoom: true })
      this.updateDecor()

      // Raycaster for interactions
      this.threeRaycaster = new THREE.Raycaster()
      this.threeMouse = new THREE.Vector2()

      // Pointer lock controls for free-look mode
      this._pointerControls = new PointerLockControls(this.threeCamera, canvas)
      this.initOrbitControls()
      this.syncRenderItems()
    },

    updateBackground() {
      if (this.threeScene) {
        this.threeScene.background = new THREE.Color(this.backgroundColor)
      }
    },

    updateWall({ updateRoom = false } = {}) {
      this.markNeedsRender()  // wall/room geometry changed
      if (!this.threeScene) return
      const layout = this._dynamicWallLayout || this.getDynamicWallLayout()
      const wallWidth = layout.wallWidth || WALL_WIDTH
      const wallCenterX = Number.isFinite(layout.wallCenterX) ? layout.wallCenterX : 0
      // Remove existing wall
      if (this.wallMesh) {
        this.threeScene.remove(this.wallMesh)
        if (this.wallMesh.geometry) this.wallMesh.geometry.dispose()
        if (this.wallMesh.material) this.wallMesh.material.dispose()
        this.wallMesh = null
      }
      if (this.wallEnabled) {
        this._wallZOffset = WALL_Z_OFFSET
        this.wallMesh = createWallPlane(wallWidth, WALL_HEIGHT, this.wallColor, WALL_Z_OFFSET, WALL_HEIGHT / 2)
        this.wallMesh.position.x = wallCenterX
        this.threeScene.add(this.wallMesh)
      }
      if (updateRoom) this.updateRoom()
      this.positionDecor()
    },

    _clearRoomGeometry() {
      for (const mesh of this._roomMeshes) {
        if (mesh.parent) mesh.parent.remove(mesh)
        if (mesh.geometry) mesh.geometry.dispose()
        const mats = Array.isArray(mesh.material) ? mesh.material : (mesh.material ? [mesh.material] : [])
        mats.forEach((m) => m?.dispose?.())
      }
      this._roomMeshes = []

      for (const light of this._roomLights) {
        if (light.parent) light.parent.remove(light)
      }
      this._roomLights = []
    },

    _clearRoomEnvironment() {
      if (this._skyMesh) {
        if (this._skyMesh.parent) this._skyMesh.parent.remove(this._skyMesh)
        this._skyMesh.geometry?.dispose()
        this._skyMesh.material?.dispose()
        this._skyMesh = null
        // Restore default IBL now that sky is gone
        if (this.threeRenderer && this.threeScene) {
          setupSceneEnvironment(this.threeRenderer, this.threeScene)
        }
      }
    },

    updateRoom() {
      if (!this.threeScene) return
      this._clearRoomGeometry()
      if (!this.roomEnabled) {
        this._clearRoomEnvironment()
        return
      }

      if (!this._skyMesh) {
        // Sky mesh is visual only (skybox visible through windows).
        // Restore RoomEnvironment IBL immediately after so lighting is unaffected by sky colour.
        this._skyMesh = setupSkyEnvironment(this.threeRenderer, this.threeScene)
        setupSceneEnvironment(this.threeRenderer, this.threeScene)
      }

      const zOffset = this._wallZOffset || 2
      const layout = this._dynamicWallLayout || this.getDynamicWallLayout()
      const wallWidth = layout.wallWidth || WALL_WIDTH
      const wallCenterX = Number.isFinite(layout.wallCenterX) ? layout.wallCenterX : 0
      const { meshes, lights } = createRoomGeometry({ zOffset, wallWidth })
      for (const mesh of meshes) {
        mesh.position.x += wallCenterX
        this.threeScene.add(mesh)
      }
      for (const light of lights) this.threeScene.add(light)
      this._roomMeshes = meshes
      this._roomLights = lights
    },

    // ------------------------------------------------------------------
    // Scene building
    // ------------------------------------------------------------------

    async rebuildScene(opts = {}) {
      const silent = opts.silent === true || this.previewMode
      if (!silent && this.cameraAnimRafId != null) {
        // A rebuild means the scene is changing, so an ease already in flight is
        // travelling towards the framing of a scene about to be replaced —
        // there is nothing left worth arriving at. Deferring the rebuild until
        // it finished made any switch taken before the previous one settled wait
        // out the rest of that second, which is what turned quick navigation
        // into alternating fast and slow. Drop it and rebuild now; the fit at
        // the end of this rebuild starts a fresh ease to the new framing.
        cancelAnimationFrame(this.cameraAnimRafId)
        this.cameraAnimRafId = null
      }
      if (this.suspendHeavyWork) {
        this._pendingHeavyRebuild = true
        return
      }
      if (!this.threeScene) return

      const version = ++this.rebuildVersion
      if (!silent) this.loading = true

      try {
        await this.syncRenderItems()
        // Geometry and textures land here, asynchronously — well after whatever
        // prop change asked for the rebuild already marked its two frames.
        this.markNeedsRender()
        if (version !== this.rebuildVersion) return
        this.startPendingItemRotationAnimation()
        this.updateWall()
        this.updateHandlesVisibility(this.hoveredObject)

        if (!silent && version === this.rebuildVersion) {
          this.loading = false
        }

        if (!silent) {
          if (!this.hasInitialFit || this.awaitingConfig) {
            await this.applyAutoFramingForCurrentScene({ instant: true })
            // Wait for Vue to update DOM and Three.js to render a frame
            await new Promise((resolve) => {
              requestAnimationFrame(() => requestAnimationFrame(resolve))
            })
            if (!this.awaitingConfig) {
              this.hasInitialFit = true
            }
          } else if (this.autoFramingAllowed() && !this.scrollSnapLayout?.enabled) {
            // Deliberately not awaited. This eases the camera from the previous
            // item's framing to this one's over about a second, but the scene is
            // already built and within a few percent of its final framing the
            // moment it starts. Awaiting it delayed scene-ready — and so the
            // still image covering the rebuild — until the ease finished, which
            // held a cover over a finished scene for the whole animation.
            //
            // Not under the overlay, where the host owns when a run of item
            // changes is over and calls fitForItemSettled. A rebuild finishing
            // mid-run cannot tell a settled item from one being passed through:
            // a held key pauses before it repeats, and the reframe that started
            // in that pause was still easing — one preset looser — several
            // items later.
            this.applyAutoFramingForCurrentScene({ instant: false })
              .catch(err => console.error('FrameRenderer: auto-framing failed', err))
          }
        }
      } catch (err) {
        console.error('FrameRenderer: failed to build scene', err)
      } finally {
        if (!silent && version === this.rebuildVersion) {
          this.loading = false
          if (!this.awaitingConfig) {
            // The camera fit has finished and options are loaded
            this.showThumbnail = false
          }
          // Geometry is in place and the camera has been fitted to it. Callers
          // covering the rebuild with a still image can uncover now; a
          // superseded rebuild stays quiet and lets the current one report.
          this.$emit('scene-ready')
        }
        this.markNeedsRender()
      }
    },

    /**
     * Update scene geometry scales and positions for new dimensions.
     * No geometry creation - GPU-friendly, just transforms.
     */
    updateSceneDimensions(artW, artH, sourceOverride) {
      if (!this.builtDims) return
      if (this.handlesGroup) {
        const aw = inches(artW)
        const ah = inches(artH)
        const outer = this.resolveOuterDimensions(artW, artH)
        const turntableX = -(inches(outer.width) / 2)
        const s = sourceOverride || {
          scaleX: this.parsedArtworkScaleX,
          scaleY: this.parsedArtworkScaleY,
          rotation: this.parsedArtworkRotation,
          offsetLeft: this.parsedArtworkOffsetLeft,
          offsetTop: this.parsedArtworkOffsetTop,
        }
        const corners = [
          { x: -aw / 2, y: ah / 2 },
          { x: aw / 2, y: ah / 2 },
          { x: -aw / 2, y: -ah / 2 },
          { x: aw / 2, y: -ah / 2 },
        ]
        const imgCenterX = inches(s.offsetLeft)
        const imgCenterY = -inches(s.offsetTop)
        const dragHandleX = inches(Number(s.dragDeltaX) || 0)
        const dragHandleY = -inches(Number(s.dragDeltaY) || 0)
        const sourceHandlesFollowDrag = this.isDragging && this.dragMode === 'source-move'
        const sourceScaleHandleActive = this.isDragging && this.dragMode === 'source-scale'
        const scaleHandleScale = sourceScaleHandleActive && Number.isFinite(Number(s.scaleHandleScale))
          ? Math.max(0.1, Number(s.scaleHandleScale))
          : 1
        const scaleOffset = aw * 0.25 * scaleHandleScale
        let i = 0
        this.handlesGroup.traverse((obj) => {
          if (obj.userData?.type === 'resize-handle') {
            obj.position.set(corners[i].x, corners[i].y, 0.5)
            i++
          } else if (obj.userData?.type === 'source-handles') {
            obj.position.set(sourceHandlesFollowDrag ? dragHandleX : 0, sourceHandlesFollowDrag ? dragHandleY : 0, 0)
          } else if (obj.userData?.type === 'source-handle-move') {
            obj.position.set(0, 0, 0.6)
          } else if (obj.userData?.type === 'source-handle-scale') {
            obj.position.set(scaleOffset, 0, 0.6)
          } else if (obj.userData?.type === 'source-handle-link') {
            const handleSize = OVERLAY_HANDLE_RADIUS
            const linkLen = Math.abs(scaleOffset)
            if (obj.geometry && obj.geometry.parameters) {
              const p = obj.geometry.parameters
              obj.scale.set(linkLen / p.width, (handleSize * 0.25) / p.height, 0.1 / p.depth)
              obj.userData.screenSpaceBaseScale?.copy(obj.scale)
            }
            obj.position.set(scaleOffset / 2, 0, 0.55)
          } else if (obj.userData?.type === 'source-artwork-center-crosshair') {
            obj.position.set(imgCenterX, imgCenterY, 0)
          } else if (obj.userData?.type === 'turntable-handle') {
            obj.position.x = turntableX
            obj.userData.rest?.copy(obj.position)
          }
        })
      }
    },

    /**
     * Legacy fit camera behavior: always fit the full framed piece with padding.
     * Retained as a compatibility fallback.
     */
    fitCameraLegacy() {
      if (!this.threeCamera || !this.threeRenderer) return
      const { width: outerWidth, height: outerHeight } = this.resolveOuterDimensions(this.parsedArtWidth, this.parsedArtHeight)

      const container = this.$refs.container
      if (!container) return
      const rect = container.getBoundingClientRect()
      const containerW = rect.width || 400
      const containerH = rect.height || 400
      const aspect = containerW / containerH

      const sceneW = inches(outerWidth)
      const sceneH = inches(outerHeight)

      // Compute distance for telephoto perspective camera
      const fov = this.threeCamera.fov
      const projectionFrontZ = this.getProjectionFrontZ()
      const dist = computeFitDistance(sceneW, sceneH, fov, aspect, 1.15)
      const targetZ = dist + projectionFrontZ

      this.threeCamera.aspect = aspect
      this.threeCamera.position.set(0, EYE_LEVEL, targetZ)
      this.threeCamera.zoom = 1
      this.threeCamera.lookAt(0, EYE_LEVEL, projectionFrontZ)
      this.threeCamera.updateProjectionMatrix()

      this.threeRenderer.setSize(containerW, containerH)
      this._playerShadowState = null
      this.updatePlayerShadowLight()
      this.syncOrbitControlsToCamera({ x: 0, y: EYE_LEVEL, distance: dist })
    },

    getOuterSceneSize() {
      const selectedInstance = this.getSelectedRenderInstance()
      if (selectedInstance?.builtDims?.outerWidth > 0 && selectedInstance?.builtDims?.outerHeight > 0) {
        return {
          sceneW: inches(selectedInstance.builtDims.outerWidth),
          sceneH: inches(selectedInstance.builtDims.outerHeight),
        }
      }

      const { width: outerWidth, height: outerHeight } = this.resolveOuterDimensions(this.parsedArtWidth, this.parsedArtHeight)
      return {
        sceneW: inches(outerWidth),
        sceneH: inches(outerHeight),
      }
    },
    getSelectedRenderInstance() {
      const spec = this.getSelectedRenderSpec()
      const key = spec?.key
      if (key == null || !this._renderItemInstances) return null
      return this._renderItemInstances.get(String(key)) || null
    },
    getSelectedRenderBaseZ() {
      const instance = this.getSelectedRenderInstance()
      const z = Number(instance?.basePosition?.z ?? instance?.group?.position?.z)
      return Number.isFinite(z) ? z : 0
    },
    /**
     * The piece's orientation away from the wall, or null when it is square
     * against it.
     *
     * One pose serves the flip, the click-spin and the drag — they differ only
     * in what drives it — and it is the single gate for everything that follows
     * from being off the wall, so a thumbnail export suppresses all of them by
     * suppressing this.
     */
    getEffectivePose() {
      if (this._suppressFlipForThumbnail) return null
      const pose = this._pose
      if (pose.yaw === 0 && pose.pitch === 0 && !this._poseAnim) return null
      return pose
    },
    getEffectiveTurntableDim() {
      return this._suppressFlipForThumbnail ? 0 : this._dimValue
    },
    /** True while the piece's front face points away from the room. */
    isShowingBack() {
      const pose = this.getEffectivePose()
      if (!pose) return false
      // z of the front normal after the pose — see getPoseQuaternion for why
      // yaw is applied before pitch.
      return Math.cos(pose.pitch) * Math.cos(pose.yaw) < 0
    },
    /**
     * How far the piece must stand off the wall to hold its current pose.
     *
     * Derived from the pose rather than animated alongside it, which is what
     * makes coming off the wall and turning one motion instead of two: it is
     * the same number whether the flip, the spin or your hand is doing the
     * turning, and it is right at every angle in between rather than only at
     * the ends.
     *
     * The piece is a box — front face at the origin, depth behind it — so what
     * it needs is that box's support distance along the wall normal, up to the
     * quarter turn where it is edge-on. Past that the stand-off is pinned
     * rather than followed back down: once the piece is off the wall it is an
     * object in the room, and swinging it back toward the plaster every half
     * turn would read as the wall pulling it in.
     */
    getPoseStandoff(dims = this.builtDims) {
      const arc = this.getPoseArcStandoff(dims)
      const anim = this._poseAnim
      if (anim?.standoffFrom == null) return arc
      // Coming home: let go of the hold over the return rather than at the
      // start of it, or the piece would step toward the wall the moment it
      // began turning back. Never below the arc, which is what it takes to
      // clear the wall at the angle it is passing through.
      return Math.max(arc, anim.standoffFrom * (1 - anim.eased))
    },
    getPoseArcStandoff(dims = this.builtDims) {
      const pose = this.getEffectivePose()
      if (!pose || !this.wallEnabled || !dims) return 0
      return this.computePoseStandoff(dims)
    },
    /**
     * Latch the piece off the wall once it has turned past edge-on.
     *
     * Latched rather than derived, because the pose alone cannot tell the two
     * cases apart: a piece square to the wall looks identical whether it has
     * just left the wall or come the whole way round to face front again, and
     * only one of those should be resting against it. Without this a piece left
     * spinning would breathe in and out once per revolution.
     */
    updatePoseOffWallLatch() {
      if (this._poseOffWall) return
      // Only while the turntable is being driven. Not on the way back, where
      // letting the piece follow the arc down onto the wall is the whole point
      // — and where a re-arm on the next frame would undo the release.
      if (this.turntablePhase === 'idle' || this.turntablePhase === 'returning') return
      if (Math.cos(this._pose.pitch) * Math.cos(this._pose.yaw) > 0) return
      this._poseOffWall = true
    },
    computePoseStandoff(dims = this.builtDims) {
      const box = this.getPoseClearanceBox(dims)
      if (!box) return 0
      const { halfW, halfH, depth } = box
      const cosPitch = Math.cos(this._pose.pitch)
      const sinPitch = Math.sin(this._pose.pitch)
      let cosYaw = Math.cos(this._pose.yaw)
      let absSinYaw = Math.abs(Math.sin(this._pose.yaw))
      if (cosYaw < 0 || this._poseOffWall) {
        // Past edge-on, hold — and keep holding once latched, so a piece that
        // carries on turning does not swing back toward the wall every half
        // revolution. Continuous at the boundary, where sin is already 1 and
        // cos already 0, so nothing steps.
        absSinYaw = 1
        cosYaw = 0
      }
      // How far the piece's own axes reach along the wall normal.
      const alongWidth = cosPitch * absSinYaw
      const alongHeight = Math.abs(sinPitch)
      const alongDepth = cosPitch * cosYaw
      const needed = (alongWidth * halfW)
        + (alongHeight * halfH)
        + (depth * Math.max(alongDepth, 0))
        - depth
      if (!(needed > 0)) return 0
      // Ease the air gap in over its own width so the piece leaves the wall
      // smoothly instead of stepping off it.
      return needed + (TURNTABLE_AIR_GAP_INCHES * Math.min(1, needed / TURNTABLE_AIR_GAP_INCHES))
    },
    getPoseClearanceBox(dims = this.builtDims) {
      if (!dims) return null
      const metrics = resolveDesignStudioItemSceneMetrics({ builtDims: dims })
      const halfW = inches(metrics.outerWidth) / 2
      const halfH = inches(metrics.outerHeight) / 2
      const depth = Math.max(0.01, metrics.backDepth ?? 2)
      if (!(halfW > 0) || !(halfH > 0)) return null
      return { halfW, halfH, depth }
    },
    /**
     * The stand-off framing should hold room for: none while the piece is on
     * the wall, the whole range of the turn once it is off. A flip is the
     * narrower case — one axis only — and reserving the turntable's corner-on
     * reach for it would stand the camera needlessly far back.
     */
    getPoseStandoffReserve() {
      if (this.turntablePhase !== 'idle') return this.getMaxPoseStandoff()
      if (this._flipTargetBack || this.getEffectivePose()) {
        return this.getMaxPoseStandoff({ yawOnly: true })
      }
      return 0
    },
    /**
     * The widest the piece ever stands off, over the whole range of the turn.
     *
     * Yaw and pitch together reach every orientation, so the worst case is not
     * either axis on its own — it is corner-on, where the piece's own corner
     * radius is what has to clear the wall. Taking the larger of the two single
     * axes instead understates it by a foot on a big piece, and the camera fit
     * that reads this would then be too close to hold a tilted piece whole.
     */
    getMaxPoseStandoff({ yawOnly = false } = {}) {
      if (!this.wallEnabled) return 0
      const box = this.getPoseClearanceBox()
      if (!box) return 0
      const { halfW, halfH, depth } = box
      // A flip only ever turns about the upright axis, so its worst case is
      // edge-on rather than corner-on — reserving the corner would stand the
      // camera further back than a flip ever needs.
      const reach = yawOnly
        ? Math.hypot(halfW, depth)
        : Math.sqrt((halfW * halfW) + (halfH * halfH) + (depth * depth))
      return Math.max(0, reach - depth) + TURNTABLE_AIR_GAP_INCHES
    },
    /**
     * The piece's world orientation: spun about its own vertical axis first,
     * then tipped about the viewer's horizontal one. Doing it in that order is
     * what makes it a turntable rather than a trackball — the spin axis stays
     * upright however far the piece is tipped.
     */
    getPoseQuaternion(baseRotation) {
      const scratch = this._poseScratch
      const quat = scratch.quat
      if (baseRotation) quat.setFromEuler(baseRotation)
      else quat.identity()
      scratch.yawQuat.setFromAxisAngle(THREE_UP, this._pose.yaw)
      scratch.pitchQuat.setFromAxisAngle(THREE_RIGHT, this._pose.pitch)
      return quat.premultiply(scratch.yawQuat).premultiply(scratch.pitchQuat)
    },
    getEffectiveItemRotationOffsetZ() {
      return this._suppressFlipForThumbnail ? 0 : (this.itemRotationOffsetZ || 0)
    },
    getSelectedRenderPoseZ() {
      return this.getSelectedRenderBaseZ()
        + this.withPoseShare(this.getActivePoseShare(), () => this.getPoseStandoff())
    },
    getInteractionOverlayZ() {
      return this.getSelectedRenderPoseZ()
    },
    /**
     * Which pieces the pose applies to.
     *
     * The active one normally; every selected one while a selection is up, so
     * that changing the hang across a selection turns all of them around rather
     * than just the one the form happens to be anchored on.
     */
    getPosedRenderKeys() {
      const keys = new Set()
      const ids = this.selectedItemIds
      if (Array.isArray(ids) && ids.length >= 2) {
        const wanted = new Set(ids.map(String))
        this.getRenderSpecs().forEach((spec) => {
          if (spec?.key != null && spec?.itemId != null && wanted.has(String(spec.itemId))) {
            keys.add(String(spec.key))
          }
        })
        if (keys.size) return keys
      }
      const key = this.getSelectedRenderSpec()?.key
      if (key != null) keys.add(String(key))
      return keys
    },
    /**
     * How much of the flip each piece is showing.
     *
     * Normally one piece takes all of it. But while backs are showing and there
     * is more than one piece to scroll between, the share is read off where the
     * camera is standing rather than off the selection — and that is what makes
     * the turn happen *during* the pan between pieces instead of after it. The
     * selection only changes once a scroll settles; the camera is moving the
     * whole way, and the piece being left should be turning back to the wall as
     * the one being arrived at turns away from it.
     *
     * Both read the same gap, so the two shares always sum to one and the
     * halves cannot drift apart.
     */
    getPoseShares() {
      const shares = new Map()
      const sharedAcrossPan = this._flipTargetBack
        && this.turntablePhase === 'idle'
        && (this.selectedItemIds?.length || 0) < 2
      if (sharedAcrossPan) {
        const specs = this.getScrollableRenderSpecs()
        if (specs.length > 1) {
          specs.forEach((spec) => {
            const share = this.getPanTurnForSpec(spec, specs)
            // Nought and two are both square to the wall — one having not
            // turned yet, the other having gone the whole way round.
            if (share > 0.001 && share < 1.999) shares.set(String(spec.key), share)
          })
          return shares
        }
      }
      this.getPosedRenderKeys().forEach(key => shares.set(key, 1))
      return shares
    },
    /**
     * How near the camera is to a piece, as a fraction of the way to the
     * neighbour on the camera's side: one when parked on it, zero at the next
     * piece along, and the two pieces the pan is between always summing to one.
     *
     * Unsigned, because this weights a blend — which side the camera is on does
     * not change how much a piece should count toward it.
     */
    getPanShareForSpec(spec, specs) {
      return 1 - Math.abs(1 - this.getPanTurnForSpec(spec, specs))
    },
    /**
     * How far through its turn a piece is, from where the camera is standing:
     * one when parked on it (showing its back), nought at the neighbour to its
     * left, two at the neighbour to its right.
     *
     * Signed, unlike the share above, and that is the whole point. Measuring
     * from distance alone turned every piece through the same nought-to-one
     * range whichever side the camera approached from, so the outgoing and
     * incoming pieces rotated against each other and the pair always turned the
     * same way regardless of which way you scrolled. Reading the offset with
     * its sign makes the angle a function of scroll position: pan right and
     * every piece winds forward through a full turn, pan left and they all
     * unwind through it, together and in the direction of travel.
     */
    getPanTurnForSpec(spec, specs) {
      const cameraX = this.threeCamera?.position?.x
      const x = Number(spec?.position?.x)
      if (!Number.isFinite(cameraX) || !Number.isFinite(x)) return 1
      const offset = cameraX - x
      if (Math.abs(offset) < 1e-4) return 1
      // Measured against the neighbour on the camera's side: crossing to that
      // one is what the turn is a fraction of.
      const towards = Math.sign(offset)
      let gap = Infinity
      specs.forEach((other) => {
        const otherX = Number(other?.position?.x)
        if (other === spec || !Number.isFinite(otherX)) return
        if (Math.sign(otherX - x) !== towards) return
        gap = Math.min(gap, Math.abs(otherX - x))
      })
      // Nothing that way — the camera has run past the end of the wall, and the
      // piece it is leaving behind has nothing to hand over to.
      if (!Number.isFinite(gap) || gap <= 0) return 1
      return THREE.MathUtils.clamp(1 + (offset / gap), 0, 2)
    },
    /** Read the pose as some fraction of its turn, without disturbing it. */
    withPoseShare(share, read) {
      const full = this._pose.yaw
      this._pose.yaw = full * share
      try {
        return read()
      } finally {
        this._pose.yaw = full
      }
    },
    getActivePoseShare() {
      const key = this.getSelectedRenderSpec()?.key
      if (key == null) return 1
      return this._poseShares.get(String(key)) ?? 0
    },
    applySelectedItemPose() {
      if (!this._renderItemInstances) return
      if (this._grabbed) return
      this._poseShares = this.getPoseShares()
      const pose = this.getEffectivePose()
      this._renderItemInstances.forEach((instance, key) => {
        if (!instance?.group) return
        const basePosition = instance.basePosition || instance.group.position
        const baseRotation = instance.baseRotation || instance.group.rotation
        // Only own z (stand-off) and rotation here; x/y belong to sync + collision, so leaving
        // them alone lets pushed neighbors stay put instead of snapping back to a stale base.
        instance.group.position.z = basePosition.z
        instance.group.rotation.set(baseRotation.x, baseRotation.y, baseRotation.z)

        const share = this._poseShares.get(String(key)) ?? 0
        if (share <= 0) return
        if (!pose) {
          instance.group.rotation.z = baseRotation.z + this.getEffectiveItemRotationOffsetZ()
          return
        }
        this.withPoseShare(share, () => {
          // Each piece clears the wall on its own geometry — a selection can
          // hold a postcard and a mural, and one stand-off does not fit both.
          instance.group.position.z = basePosition.z + this.getPoseStandoff(instance.builtDims)
          // Off the wall the piece has two axes of freedom, which no sum of
          // Euler components describes without picking an order.
          instance.group.quaternion.copy(this.getPoseQuaternion(baseRotation))
        })
      })
    },
    /**
     * Keep the handle/dimension overlay glued to the piece.
     *
     * The overlay is a sibling of the piece rather than a child of it, so every
     * transform the piece takes on has to be mirrored here — otherwise the
     * handle you are dragging stays flat on the wall while the piece it belongs
     * to turns away from it.
     */
    syncInteractionGroupTransform() {
      const group = this.interactionGroup
      if (!group) return
      if (this.getEffectivePose()) {
        const scratch = this._poseScratch
        scratch.euler.set(0, 0, this.getEffectiveItemRotationOffsetZ())
        this.withPoseShare(this.getActivePoseShare(), () => {
          group.quaternion.copy(this.getPoseQuaternion(scratch.euler))
        })
      } else {
        group.rotation.set(0, 0, this.getEffectiveItemRotationOffsetZ())
      }
      group.position.z = this.getInteractionOverlayZ()
    },
    /** Keep interaction handles at their Wall-mode projected size while zooming. */
    syncHandleScreenSpaceScale() {
      if (!this.handlesGroup || !this.threeCamera) return
      const metrics = this.getContainerMetrics()
      const wallPreset = FRAMING_PRESET_BY_MODE.get(CAMERA_MODE_WALL)
      if (!metrics || !wallPreset) return

      const baselineFov = scaleFovForViewport(metrics.renderH, STANDARD_REFERENCE_VIEWPORT_HEIGHT)
      const baselineDepth = presetCameraDistance(
        wallPreset,
        metrics.renderH,
        STANDARD_REFERENCE_VIEWPORT_HEIGHT,
      )
      this.threeCamera.updateMatrixWorld(true)
      this.interactionGroup?.updateMatrixWorld(true)
      const world = this._handleScaleWorldPosition || (this._handleScaleWorldPosition = new THREE.Vector3())
      const cameraSpace = this._handleScaleCameraPosition || (this._handleScaleCameraPosition = new THREE.Vector3())

      this.handlesGroup.traverse((obj) => {
        const base = obj.userData?.screenSpaceBaseScale
        if (!base) return
        obj.getWorldPosition(world)
        cameraSpace.copy(world).applyMatrix4(this.threeCamera.matrixWorldInverse)
        const scale = screenSpaceScaleForDepth(
          Math.abs(cameraSpace.z),
          baselineDepth,
          this.threeCamera.fov,
          baselineFov,
        )
        if (obj.userData.type === 'source-handle-link') {
          obj.scale.set(base.x, base.y * scale, base.z * scale)
        } else {
          obj.scale.copy(base).multiplyScalar(scale)
        }
        // The hit proxy left at the handle's resting place does not travel, so
        // it takes the scale for where it sits rather than for wherever the
        // handle has got to — see createTurntableHandle.
        const rest = obj.userData.rest && this.getTurntableHandleRestMatrix()
        if (!rest) return
        cameraSpace.setFromMatrixPosition(rest).applyMatrix4(this.threeCamera.matrixWorldInverse)
        const restScale = screenSpaceScaleForDepth(
          Math.abs(cameraSpace.z),
          baselineDepth,
          this.threeCamera.fov,
          baselineFov,
        )
        const restVector = obj.userData.restScale
          || (obj.userData.restScale = new THREE.Vector3())
        restVector.copy(base).multiplyScalar(restScale)
      })
      this.handlesGroup.updateMatrixWorld(true)
    },
    /**
     * Where the handle sits with the piece square against the wall.
     *
     * The handle itself rides the edge it is attached to, so a hover that lifts
     * that edge carries the handle out from under the pointer — which drops the
     * hover, which retracts the peek, which brings it back. Hit testing covers
     * this resting place as well as wherever the handle has got to, so the spot
     * you reached for stays live while the piece moves under you.
     */
    getTurntableHandleRestMatrix() {
      const rest = this._turntableHandleMesh?.userData?.rest
      if (!rest) return null
      const active = this.getActiveScenePosition()
      return this._poseScratch.matrix.makeTranslation(
        active.x + rest.x,
        active.y + rest.y,
        this.getSelectedRenderBaseZ() + rest.z,
      )
    },
    queueItemRotationAnimation(request) {
      const id = request?.id
      const deltaDegrees = Number(request?.deltaDegrees)
      if (id == null || !Number.isFinite(deltaDegrees) || deltaDegrees === 0) return
      if (this.itemRotationAnimation?.requestId === id || this.pendingItemRotationAnimation?.id === id) return
      this.pendingItemRotationAnimation = { id, deltaDegrees }
    },
    startPendingItemRotationAnimation() {
      const request = this.pendingItemRotationAnimation
      if (!request || !this.getSelectedRenderInstance()) return
      this.pendingItemRotationAnimation = null
      this.startItemRotationAnimation(request)
    },
    startItemRotationAnimation({ id, deltaDegrees }) {
      const deltaRadians = (Number(deltaDegrees) * Math.PI) / 180
      if (!Number.isFinite(deltaRadians) || deltaRadians === 0) return

      const currentOffset = Number(this.itemRotationOffsetZ) || 0
      const from = currentOffset + deltaRadians
      this.cancelItemRotationAnimation({ preserveOffset: true })
      this.itemRotationOffsetZ = from
      this.itemRotationAnimation = {
        requestId: id,
        from,
        to: 0,
        startTime: performance.now(),
        duration: 320,
      }
      this.applySelectedItemPose()
      this.updateInteractionRotationOffset()
      this.itemRotationAnimationRafId = requestAnimationFrame(this.updateItemRotationAnimation)
    },
    updateItemRotationAnimation(now) {
      const anim = this.itemRotationAnimation
      if (!anim) return
      this.markNeedsRender()
      const t = Math.min(1, (now - anim.startTime) / anim.duration)
      const eased = this.easeOutBack(t)
      this.itemRotationOffsetZ = anim.from + (anim.to - anim.from) * eased
      this.applySelectedItemPose()
      this.updateInteractionRotationOffset()
      if (t < 1) {
        this.itemRotationAnimationRafId = requestAnimationFrame(this.updateItemRotationAnimation)
      } else {
        this.cancelItemRotationAnimation()
      }
    },
    cancelItemRotationAnimation({ preserveOffset = false } = {}) {
      if (this.itemRotationAnimationRafId != null) {
        cancelAnimationFrame(this.itemRotationAnimationRafId)
        this.itemRotationAnimationRafId = null
      }
      this.itemRotationAnimation = null
      this.pendingItemRotationAnimation = null
      if (!preserveOffset) {
        this.itemRotationOffsetZ = 0
        this.applySelectedItemPose()
        this.updateInteractionRotationOffset()
      }
    },
    updateInteractionRotationOffset() {
      this.syncInteractionGroupTransform()
    },
    getProjectionFrontZ() {
      // A lone item's props describe the piece itself, so its front face is
      // known before the geometry exists and stays right through a switch.
      // A multi-item render's props do not describe the selected piece, so
      // there the built geometry is still the only source.
      if (!this.renderItems) return this.frontZ
      const targetInstance = this.getSelectedRenderInstance()
      const z = Number(targetInstance?.builtDims?.frontZ)
      if (Number.isFinite(z)) return z
      return this.frontZ
    },

    getRequiredCameraModeForVisibleArtwork() {
      if (this.cameraMode === CAMERA_MODE_FREE) return CAMERA_MODE_FREE

      // getSizingSceneSize, not getOuterSceneSize, for the same reason
      // computeCameraTarget uses it: the caller's dimensions are the incoming
      // piece's and are known before its geometry is built, where the built
      // outer size is still the piece being replaced. Choosing the mode from
      // that one left a switch showing a wall-sized piece at standard scale
      // until the rebuild landed and promoted it, then easing the difference —
      // while the distance beside it had already been fitted to the new piece.
      const size = this.getSizingSceneSize()
      if (!size) return this.cameraMode || CAMERA_MODE_STANDARD

      return this._selectAutoFramingMode(size.sceneW, size.sceneH)
    },

    async applyAutoFramingForCurrentScene({ instant = false, restart = false, fitModeChange = false, reselectMode = false } = {}) {
      if (!this.threeCamera || !this.threeRenderer) return
      if (this.cameraMode === CAMERA_MODE_FREE || this.freeLookActive) return
      if (instant && this.cameraAnimRafId != null) return
      if (!instant && this.cameraAnimRafId != null && !restart) return
      // Don't drive the camera while the overlay is being smooth-scrolled to an item —
      // the overlay is driving the camera until the scroll settles.
      if (this._scrollSnapSelectionFitPending) return

      // Piece framing doesn't consult the camera mode, so the mode negotiation
      // below has nothing to decide. Skipping it also avoids stalling on the
      // `request-camera-mode` round-trip: a parent that renders a lone item has
      // no reason to own a camera mode, and without an answer the scene would
      // never get its first fit.
      // Always instant. Animating the camera reads as movement through a room,
      // which is meaningful when the scene stays put and only the viewpoint
      // changes. Piece framing swaps one piece for an unrelated other, and the
      // finished framing is known from its dimensions before it's drawn — so
      // the incoming item should simply arrive correctly framed.
      if (this.framing === FRAMING_PIECE) {
        this.fitCamera()
        return
      }

      const requiredMode = this.getRequiredCameraModeForVisibleArtwork()
      if (requiredMode === CAMERA_MODE_FREE) return

      const currentMode = this.cameraMode || CAMERA_MODE_STANDARD
      const overlayAuthoritative = !instant && this._scrollSnapOverlayAuthoritative
      // reselectMode is the reset button: take the preset this piece actually
      // wants, in either direction, rather than only ever loosening.
      const shouldPromote = reselectMode || shouldPromoteCameraMode(currentMode, requiredMode)

      const modeChanged = (this.cameraMode == null || shouldPromote) && requiredMode !== this.cameraMode
      if (modeChanged) {
        if (!instant && this.scrollSnapOverlayToCameraMode(requiredMode, { behavior: 'smooth' })) {
          return
        }
        if (overlayAuthoritative) return
        if (!instant || fitModeChange) {
          this._suppressNextCameraModeFit = true
        }
        this.$emit('request-camera-mode', requiredMode, reselectMode ? { force: true } : {})
        await this.$nextTick()
        // A host that declines the request leaves the flag armed, and the next
        // mode change that does deserve a fit would silently lose it.
        if (this._suppressNextCameraModeFit && this.cameraMode !== requiredMode) {
          this._suppressNextCameraModeFit = false
        }
      }

      if (instant) {
        // A mode change normally gets its fit from the cameraMode watcher,
        // which animates — that is the movement a resize across a framing
        // threshold is meant to show. fitModeChange is for the caller that
        // wants the framing to simply be right: fit it here instead.
        if (!modeChanged || fitModeChange) this.fitCamera()
        return
      }

      await this.animateFitCamera()
    },

    /**
     * Frame the piece as it is now, mode included and with nothing seen moving.
     * A host swapping the item it shows calls this while the still image still
     * covers the canvas, so the incoming piece is revealed already framed
     * rather than sliding into place once its geometry lands. onResize is not
     * the same thing: there the piece on screen is the one being reframed, and
     * the movement is what shows the framing changed.
     */
    fitForItemChange() {
      // A selection scroll still in flight was aimed at the piece being
      // replaced, so it has nothing left to hand off — and while its latch is
      // set auto framing declines to run at all, which would leave the camera
      // wherever that scroll had reached, framed for the outgoing piece. Same
      // reasoning as onResize, which ends it for the same reason.
      if (this._scrollSnapSelectionFitPending) this.finishScrollSnapSelectionHandoff()
      this.markNeedsRender()
      return this.applyAutoFramingForCurrentScene({ instant: true, fitModeChange: true })
    },

    /**
     * Ease to the incoming item's framing after a run of switches has settled.
     *
     * The counterpart to fitForItemChange: that one is for a host showing one
     * deliberate swap, where the piece should simply arrive framed. When the
     * user is scanning quickly, refitting per item makes the camera jump on
     * every step, so the host holds it still and calls this once they stop —
     * the move then reads as one deliberate reframe rather than a stutter.
     */
    fitForItemSettled() {
      if (this._scrollSnapSelectionFitPending) this.finishScrollSnapSelectionHandoff()
      this.markNeedsRender()
      return this.applyAutoFramingForCurrentScene({ instant: false, restart: true })
    },

    getSizingSceneSize() {
      const targetW = parseFloat(this.targetWidth)
      const targetH = parseFloat(this.targetHeight)
      if (Number.isFinite(targetW) && targetW > 0 && Number.isFinite(targetH) && targetH > 0) {
        return {
          sceneW: inches(targetW),
          sceneH: inches(targetH),
        }
      }
      return this.getOuterSceneSize()
    },

    getContainerMetrics() {
      const container = this.$refs.container
      if (!container) return null
      const rect = container.getBoundingClientRect()
      const containerW = rect.width || 400
      const containerH = rect.height || 400

      // The canvas is the container: nothing is cropped and nothing is
      // letterboxed. The aspect is free because it no longer carries any
      // meaning — scale comes from the preset and perspective from the camera
      // distance, so the aspect only decides how much of the room is on either
      // side of the piece.
      return {
        containerW,
        containerH,
        containerAspect: containerW / containerH,
        renderW: containerW,
        renderH: containerH,
        renderAspect: containerW / containerH,
      }
    },

    computeCameraTarget(mode = this.cameraMode, sizeOverride = null) {
      const containerMetrics = this.getContainerMetrics()
      if (!containerMetrics) return null

      const { sceneW, sceneH } = sizeOverride || this.getSizingSceneSize()
      const { containerW, containerH, renderW, renderH, renderAspect } = containerMetrics
      // Field of view tracks the canvas so the camera does not have to move to
      // hold the scale — see scaleFovForViewport.
      const fov = scaleFovForViewport(renderH, STANDARD_REFERENCE_VIEWPORT_HEIGHT)

      // Piece framing ignores the room presets entirely: fill the viewport with
      // the outer frame and center on the piece. Same distance and centering
      // exportThumbnail uses, so the live render lines up with the thumbnail.
      if (this.framing === FRAMING_PIECE) {
        // getSizingSceneSize, not getOuterSceneSize: it prefers the caller's
        // target dimensions, which are known before the scene is built. Deriving
        // the framing from the built geometry instead makes the fit before a
        // rebuild disagree slightly with the fit after it, and the piece
        // visibly settles.
        const { sceneW: outerW, sceneH: outerH } = this.getSizingSceneSize() || {}
        if (Number.isFinite(outerW) && outerW > 0 && Number.isFinite(outerH) && outerH > 0) {
          const active = this.getActiveScenePosition()
          return {
            aspect: renderAspect,
            fov,
            containerW,
            containerH,
            renderW,
            renderH,
            targetX: active.x,
            targetY: active.y,
            targetZ: computeFitDistance(outerW, outerH, fov, renderAspect, this.piecePadding)
              + this.getProjectionFrontZ(),
          }
        }
      }

      // Parent `cameraMode` starts null; thumbnail + first fit use same auto pick as `request-camera-mode`.
      const effectiveMode = (mode == null)
        ? this._selectAutoFramingMode(sceneW, sceneH)
        : mode

      const baselineH = STANDARD_REFERENCE_VIEWPORT_HEIGHT
      const preset = FRAMING_PRESET_BY_MODE.get(effectiveMode) ?? AUTO_FRAMING_PRESETS[0]
      const modeDist = presetCameraDistance(preset, renderH, baselineH)
      // Plus whatever room the piece needs to turn in: an ensureFit preset
      // promises the whole piece stays on screen, and a piece off the wall is
      // nearer the camera and so projects larger. Reserved for the whole turn
      // rather than the angle of the moment, or the camera would track the
      // piece in and out instead of settling once.
      const fitDist = computeFitDistance(sceneW, sceneH, fov, renderAspect, FRAMING_CROP_GUARD_PADDING)
        + this.getPoseStandoffReserve()
      // Presets hold an exact scale, which means a canvas too small to show the
      // piece at that scale crops it. Only `ensureFit` presets trade the scale
      // away to keep the piece whole; for the rest the preset distance stands,
      // cropping and all, because holding the scale is what they are for.
      const dist = Number.isFinite(modeDist) && modeDist > 0
        ? (preset.ensureFit ? Math.max(modeDist, fitDist) : modeDist)
        : fitDist
      // Measured from the wall, not from the piece's front face. Offsetting by
      // the front face held every piece at the same distance from the camera,
      // which meant the camera itself moved in and out with each piece's depth
      // — so standard, wall and wide were not one viewpoint but a family of
      // them, and switching items slid the room around. Standing still is the
      // point of a named mode: the camera stays put and a deeper piece reads as
      // deeper, projecting slightly larger because it is nearer.
      const targetZ = dist

      return {
        aspect: renderAspect,
        fov,
        containerW,
        containerH,
        renderW,
        renderH,
        targetX: this.getActiveScenePosition().x,
        targetZ,
      }
    },

    getDisplaySizeForTarget(target, sceneSizeOverride = null, projectionFrontZ = this.getProjectionFrontZ()) {
      if (!target) return null
      const size = sceneSizeOverride || this.getSizingSceneSize()
      const { sceneW, sceneH } = size || {}
      if (!Number.isFinite(sceneW) || !Number.isFinite(sceneH) || sceneW <= 0 || sceneH <= 0) {
        return null
      }

      const { renderH } = target
      const fov = Number.isFinite(target.fov) ? target.fov : (this.threeCamera?.fov || 75)
      const fitDist = target.targetZ - projectionFrontZ

      const viewHeight = 2 * fitDist * Math.tan((THREE.MathUtils.degToRad(fov)) / 2)
      if (!Number.isFinite(viewHeight) || viewHeight <= 0) return null

      const pxPerUnit = renderH / viewHeight
      const width = Math.max(1, Math.round(sceneW * pxPerUnit))
      const height = Math.max(1, Math.round(sceneH * pxPerUnit))

      return { width, height }
    },

    getThumbnailDisplaySize() {
      const target = this.computeCameraTarget(this.cameraMode)
      if (this.thumbnailUrl && this.thumbnailUrl === this.artworkImageUrl) {
        return this.getDisplaySizeForTarget(target, {
          sceneW: inches(this.parsedArtWidth),
          sceneH: inches(this.parsedArtHeight),
        })
      }
      return this.getDisplaySizeForTarget(target)
    },

    fitCamera() {
      this.markNeedsRender()  // camera moved
      if (!this.threeCamera || !this.threeRenderer) return
      if (this.cameraMode === CAMERA_MODE_FREE || this.freeLookActive) return
      if (this.cameraAnimRafId != null) {
        cancelAnimationFrame(this.cameraAnimRafId)
        this.cameraAnimRafId = null
      }

      const target = this.computeCameraTarget(this.cameraMode)
      if (!target) {
        this.fitCameraLegacy()
        return
      }

      // targetY is only set by piece framing; room framing stays at eye level
      const eyeY = target.targetY ?? EYE_LEVEL
      this.threeCamera.aspect = target.aspect
      if (Number.isFinite(target.fov)) this.threeCamera.fov = target.fov
      this.threeCamera.position.set(target.targetX || 0, eyeY, target.targetZ)
      this.threeCamera.lookAt(target.targetX || 0, eyeY, this.getProjectionFrontZ())
      this.threeCamera.updateProjectionMatrix()
      this.threeRenderer.setSize(target.renderW, target.renderH)
      this._playerShadowState = null
      this.updatePlayerShadowLight()
      this.syncOrbitControlsToCamera({
        x: target.targetX || 0,
        y: eyeY,
        distance: target.targetZ - this.getProjectionFrontZ(),
      })
      // An in-flight ease was cancelled at the top of this method, so orbit may
      // still be held off for one that is no longer running.
      this.syncOrbitInteraction()
      // Anything sizing itself to the projected piece needs to recompute now
      this.cameraFitVersion += 1
      this.$emit('camera-fit')
      this.$nextTick(() => this.refreshScrollSnapOverlayLayout())
    },

    /** Animate camera distance from current to fitted over duration ms. No-ops if already at target. */
    animateFitCamera(duration = 1000) {
      if (!this.threeCamera || !this.threeRenderer) return Promise.resolve()
      if (this.cameraMode === CAMERA_MODE_FREE || this.freeLookActive) return Promise.resolve()
      if (this.cameraAnimRafId != null) {
        cancelAnimationFrame(this.cameraAnimRafId)
        this.cameraAnimRafId = null
      }

      const target = this.computeCameraTarget(this.cameraMode)
      if (!target) return Promise.resolve()
      const targetX = target.targetX || 0
      const targetY = target.targetY ?? EYE_LEVEL
      const targetZ = target.targetZ
      const startX = this.threeCamera.position.x
      const startY = this.threeCamera.position.y
      const startZ = this.threeCamera.position.z
      const startQuat = this.threeCamera.quaternion.clone()

      // Compute the target orientation (perpendicular, centered) without changing the live camera.
      this.threeCamera.position.set(targetX, targetY, targetZ)
      this.threeCamera.lookAt(targetX, targetY, this.getProjectionFrontZ())
      this.threeCamera.updateMatrixWorld()
      const targetQuat = this.threeCamera.quaternion.clone()
      this.threeCamera.position.set(startX, startY, startZ)
      this.threeCamera.quaternion.copy(startQuat)
      this.threeCamera.updateMatrixWorld()

      this.threeCamera.aspect = target.aspect
      if (Number.isFinite(target.fov)) this.threeCamera.fov = target.fov
      this.threeCamera.updateProjectionMatrix()
      this.threeRenderer.setSize(target.renderW, target.renderH)

      // Skip animation if already close to target position, but still snap orientation
      // (e.g. after free-look left the camera pointing an arbitrary direction).
      const posEps = 0.001
      if (
        Math.abs(targetX - startX) < posEps &&
        Math.abs(targetY - startY) < posEps &&
        Math.abs(targetZ - startZ) < posEps
      ) {
        this.threeCamera.position.set(targetX, targetY, targetZ)
        this.threeCamera.quaternion.copy(targetQuat)
        this.threeCamera.updateProjectionMatrix()
        this._playerShadowState = null
        this.updatePlayerShadowLight()
        this.syncOrbitControlsToCamera({ x: targetX, y: targetY, distance: targetZ - this.getProjectionFrontZ() })
        this.runPendingHeavyRebuild()
        return Promise.resolve()
      }

      // The animation writes the camera's position and orientation directly,
      // which is the one thing orbit must not be doing at the same time — its
      // per-frame update would re-aim the camera at a target the ease has
      // already left behind, and input mid-ease would fight it. Set here rather
      // than through syncOrbitInteraction because cameraAnimRafId, the state
      // that method reads, is not assigned until the first frame is scheduled
      // below. The landing re-syncs both.
      if (this._orbitControls) this._orbitControls.enabled = false

      return new Promise((resolve) => {
        const startTime = performance.now()

        const animate = (now) => {
          const t = Math.min((now - startTime) / duration, 1)
          const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

          this.markNeedsRender()
          this.threeCamera.position.x = startX + (targetX - startX) * eased
          this.threeCamera.position.y = startY + (targetY - startY) * eased
          this.threeCamera.position.z = startZ + (targetZ - startZ) * eased
          this.threeCamera.quaternion.copy(startQuat).slerp(targetQuat, eased)
          this.threeCamera.updateProjectionMatrix()
          this.updatePlayerShadowLight()

          if (t < 1) {
            this.cameraAnimRafId = requestAnimationFrame(animate)
          } else {
            this.cameraAnimRafId = null
            this.syncOrbitControlsToCamera({ x: targetX, y: targetY, distance: targetZ - this.getProjectionFrontZ() })
            this.syncOrbitInteraction()
            this.runPendingHeavyRebuild()
            this._scrollSnapOverlayAuthoritative = false
            this.refreshScrollSnapOverlayLayout()
            if (this._autoFramingRecheckPending) {
              this._autoFramingRecheckPending = false
              this.applyAutoFramingForCurrentScene({ instant: true })
            }
            resolve()
          }
        }
        this.cameraAnimRafId = requestAnimationFrame(animate)
      })
    },

    // ------------------------------------------------------------------
    // Orbit controls (browse item viewer)
    // ------------------------------------------------------------------

    /**
     * three's own OrbitControls, deliberately, rather than another hand-rolled
     * input model. The scroll-snap overlay exists because the design studio's
     * camera navigates a wall of items — scrolling picks which piece you are
     * looking at. The browse item viewer has exactly one piece, so its camera
     * is only ever a way of looking at that piece, which is the problem every
     * 3D viewer already solved: drag to orbit, wheel to dolly, right-drag to
     * pan, with pointer capture, touch and damping handled for us.
     *
     * What the framing presets keep is the *starting* viewpoint — see
     * autoFramingAllowed. Standard / wall / wide still choose how far back an
     * item loads at, and still promote-only as items go past; once it is on
     * screen the camera is the user's.
     */
    initOrbitControls() {
      if (this._orbitControls || !this.orbitEnabled) return
      if (!this.threeCamera || !this.$refs.container) return

      const controls = new OrbitControls(this.threeCamera, this.$refs.container)
      controls.enableDamping = true
      controls.dampingFactor = ORBIT_DAMPING_FACTOR
      // The piece hangs on a plane, so panning should slide across that plane.
      // Moving along the camera's own up axis instead would drift off the wall.
      controls.screenSpacePanning = true
      // Polar is owned by applyOrbitRoomLimits, which re-derives it per frame.
      controls.minAzimuthAngle = -THREE.MathUtils.degToRad(ORBIT_MAX_AZIMUTH_DEG)
      controls.maxAzimuthAngle = THREE.MathUtils.degToRad(ORBIT_MAX_AZIMUTH_DEG)
      // The render loop draws on demand, so a damped move that outlives the
      // gesture has to keep asking for frames of its own.
      controls.addEventListener('change', this._boundOrbitChange)

      this._orbitControls = controls
      this.syncOrbitControlsToCamera()
      this.syncOrbitInteraction()
    },

    destroyOrbitControls() {
      this.clearOrbitModeSettleTimer()
      if (!this._orbitControls) return
      this._orbitControls.removeEventListener('change', this._boundOrbitChange)
      // Also restores the container's touch-action, which connect() took over.
      this._orbitControls.dispose()
      this._orbitControls = null
    },

    /**
     * Re-pivot the orbit on whatever the camera is now looking at.
     *
     * OrbitControls holds the viewpoint as a spherical offset from its target,
     * so a programmatic camera move that leaves the target behind is invisible
     * until the next drag — which then swings the camera back to the old pivot.
     * Every path that positions the camera itself ends here.
     *
     * The bounds are re-derived at the same time because they are relative to
     * the framing: what counts as "too close" for a 5" piece at standard scale
     * is nowhere near what counts for a mural at wide.
     */
    syncOrbitControlsToCamera({ x = null, y = null, distance = null } = {}) {
      const controls = this._orbitControls
      if (!controls || !this.threeCamera) return

      const pivotX = Number.isFinite(x) ? x : this.threeCamera.position.x
      const pivotY = Number.isFinite(y) ? y : this.threeCamera.position.y
      const pivotZ = this.getProjectionFrontZ()
      controls.target.set(pivotX, pivotY, pivotZ)
      // Panning moves `target`; `cursor` is the fixed point maxTargetRadius is
      // measured from, so it stays where the framing put it.
      controls.cursor.set(pivotX, pivotY, pivotZ)

      const fitted = Number.isFinite(distance) && distance > 0
        ? distance
        : this.threeCamera.position.distanceTo(controls.target)
      const bounds = this.getOrbitDistanceBounds(fitted)
      if (bounds) {
        controls.minDistance = bounds.min
        controls.maxDistance = bounds.max
      }

      const size = this.getSizingSceneSize() || {}
      const extent = Math.max(Number(size.sceneW) || 0, Number(size.sceneH) || 0)
      controls.maxTargetRadius = extent > 0 ? extent * ORBIT_PAN_EXTENT_SCALE : Infinity

      this.applyOrbitRoomLimits()
      controls.update()
    },

    /**
     * Keep the viewpoint somewhere a person could stand.
     *
     * The polar limits are angles, but what they stand in for is a height. At
     * arm's length a 38-degree downward tilt puts the camera just above the
     * frame, which is the view you want for reading a frame profile; at the far
     * end of the dolly the same angle puts it fifteen feet up, above the wall
     * backdrop and looking at bare floor. So the range is re-derived from the
     * current distance to hold the camera inside the room's height, and the
     * fixed limits only cap how much tilt is ever allowed. Backing away then
     * eases the camera down towards level on its own, which is what walking
     * backwards across a room does.
     *
     * Runs per frame, because distance — the thing it is derived from — is
     * exactly what dollying changes.
     */
    applyOrbitRoomLimits() {
      const controls = this._orbitControls
      if (!controls) return
      const distance = controls.getDistance()
      if (!Number.isFinite(distance) || distance <= 0) return

      const targetY = controls.target.y
      // acos decreases, so the highest reachable camera gives the *smallest*
      // polar angle and the lowest gives the largest.
      const polarForHeight = y => Math.acos(THREE.MathUtils.clamp((y - targetY) / distance, -1, 1))
      let min = Math.max(THREE.MathUtils.degToRad(ORBIT_MIN_POLAR_DEG), polarForHeight(ORBIT_MAX_CAMERA_Y))
      let max = Math.min(THREE.MathUtils.degToRad(ORBIT_MAX_POLAR_DEG), polarForHeight(ORBIT_MIN_CAMERA_Y))
      // A target panned above the band, or a distance short enough that the
      // whole sphere clears it, can cross the two over.
      if (min > max) min = max = (min + max) / 2
      controls.minPolarAngle = min
      controls.maxPolarAngle = max
    },

    /**
     * How near and how far the orbit may dolly, in scene inches.
     *
     * Near is a fraction of the distance at which the piece exactly fills the
     * viewport — close enough to read a mat edge, not so close you end up
     * inside the frame. Far is a little past the loosest framing preset, which
     * is already "the whole wall and then some"; beyond it the camera leaves
     * the room and the piece is a speck. `fitted` widens the range rather than
     * defining it, so an oversized piece whose own fit sits outside these
     * anchors is still reachable.
     */
    getOrbitDistanceBounds(fitted) {
      const metrics = this.getContainerMetrics()
      const size = this.getSizingSceneSize()
      if (!metrics || !size) return null
      const { sceneW, sceneH } = size
      if (!(sceneW > 0) || !(sceneH > 0)) return null

      const fov = scaleFovForViewport(metrics.renderH, STANDARD_REFERENCE_VIEWPORT_HEIGHT)
      const pieceDist = computeFitDistance(sceneW, sceneH, fov, metrics.renderAspect, 1)

      const loosest = AUTO_FRAMING_PRESETS[AUTO_FRAMING_PRESETS.length - 1]
      const loosestTarget = this.computeCameraTarget(loosest.mode)
      const loosestDist = Number(loosestTarget?.targetZ) - this.getProjectionFrontZ()

      const usable = value => (Number.isFinite(value) && value > 0 ? value : null)
      const near = usable(pieceDist) ?? usable(fitted)
      const far = usable(loosestDist) ?? usable(fitted)
      if (!near || !far) return null

      const min = Math.max(ORBIT_MIN_DISTANCE_INCHES, near * ORBIT_MIN_DISTANCE_SCALE)
      const max = Math.max(far * ORBIT_MAX_DISTANCE_SCALE, usable(fitted) || 0, min * 2)
      return { min: Math.min(min, max / 2), max }
    },

    onOrbitControlsChange() {
      this.markNeedsRender()
      this.scheduleOrbitFramingModeSync()
    },

    clearOrbitModeSettleTimer() {
      if (this._orbitModeSettleTimer == null) return
      clearTimeout(this._orbitModeSettleTimer)
      this._orbitModeSettleTimer = null
    },

    scheduleOrbitFramingModeSync() {
      this.clearOrbitModeSettleTimer()
      this._orbitModeSettleTimer = setTimeout(() => {
        this._orbitModeSettleTimer = null
        this.syncFramingModeToOrbit()
      }, ORBIT_MODE_SETTLE_MS)
    },

    /**
     * Which framing preset the camera is currently standing in, by distance.
     *
     * Compared in log space: the presets are roughly a doubling apart
     * (standard, wall, wide), so the point that reads as halfway between two of
     * them is the geometric mean, not the arithmetic one.
     */
    getNearestFramingModeToCamera() {
      if (!this.threeCamera) return null
      const frontZ = this.getProjectionFrontZ()
      // Under the scroll surface there are no orbit controls to ask, but the
      // question — how far back is the camera standing — is the same one.
      const distance = this._orbitControls
        ? this._orbitControls.getDistance()
        : this.threeCamera.position.z - frontZ
      if (!Number.isFinite(distance) || distance <= 0) return null

      let best = null
      for (const preset of AUTO_FRAMING_PRESETS) {
        const presetDistance = Number(this.computeCameraTarget(preset.mode)?.targetZ) - frontZ
        if (!Number.isFinite(presetDistance) || presetDistance <= 0) continue
        const error = Math.abs(Math.log(distance / presetDistance))
        if (!best || error < best.error) best = { mode: preset.mode, error }
      }
      return best?.mode || null
    },

    /**
     * Keep the framing-preset selector pointing at wherever the dolly has
     * actually left the camera.
     *
     * Two things fall out of this, and the second is the point. The buttons
     * stop lying — without it they would still claim "Standard" from across the
     * room. And because the mode is the baseline the item-switch fit promotes
     * from, a user who has dollied in close switches to the next item at
     * standard rather than being thrown back out to whatever preset they last
     * pressed. Minimising that jump is the whole reason the mode tracks the
     * camera rather than the other way round.
     *
     * `force`, because dollying in is a demotion and the host's request handler
     * is otherwise promote-only. Fit suppressed, because the camera is already
     * where this mode describes — that is why the mode is changing — so the
     * watcher's fit would have nothing to do but undo the dolly.
     */
    async syncFramingModeToOrbit() {
      if (!this.orbitEnabled || this.framing === FRAMING_PIECE) return
      if (this.freeLookActive || this.cameraAnimRafId != null) return
      const mode = this.getNearestFramingModeToCamera()
      if (!mode || mode === this.cameraMode) return

      this._suppressNextCameraModeFit = true
      this.$emit('request-camera-mode', mode, { force: true, reason: 'orbit-dolly' })
      await this.$nextTick()
      // A host that declines the request leaves the flag armed, and the next
      // mode change that does deserve a fit would silently lose it.
      if (this._suppressNextCameraModeFit && this.cameraMode !== mode) {
        this._suppressNextCameraModeFit = false
      }
    },

    /**
     * Put the camera back where the framing presets say it belongs for this
     * piece — the "reset view" button.
     *
     * Re-picks the preset from scratch rather than promote-only, which is what
     * separates it from the per-item reset. Someone who has dollied and
     * orbited their way somewhere odd is asking for the default view of this
     * piece, not for the loosest preset they happened to pass through.
     */
    resetCameraView() {
      this.markNeedsRender()
      return this.applyAutoFramingForCurrentScene({ instant: false, restart: true, reselectMode: true })
    },

    /**
     * Decide, on hover, whether the next drag belongs to the camera or to the
     * scene.
     *
     * Both start on pointerdown and OrbitControls sees it first, so without
     * this a pull on a resize handle spins the camera instead. Hover already
     * knows which one it is going to be — the handle under the cursor is the
     * same thing onMouseDown will act on — so the decision is made there rather
     * than by racing the two handlers.
     */
    syncOrbitInteraction() {
      const controls = this._orbitControls
      if (!controls) return
      const data = this.hoveredObject?.userData || {}
      const overSceneHandle = data.type === 'resize-handle'
        || data.type === 'source-handle-move'
        || data.type === 'source-handle-scale'
        || (this.wallDragEnabled && data.renderItemId != null)
      // Keyed to free-look actually being active rather than to the 'free'
      // camera mode: pressing Esc unlocks the pointer but leaves the mode set,
      // and orbit is then the natural way to keep looking around from wherever
      // walking left the camera.
      controls.enabled = !overSceneHandle
        && data.type !== 'turntable-handle'
        && !this.isDragging
        && !this._grabbed
        && !this.freeLookActive
        // Turning the piece and orbiting around it are the same drag; while the
        // piece is off the wall it is the one that gets it.
        && this.turntablePhase === 'idle'
        && this.cameraAnimRafId == null
    },

    /**
     * Match the projection to the container without moving the viewpoint.
     *
     * Orbit's counterpart to fitCamera on resize. The framing presets chose
     * where the camera sits when the item loaded and the user may have moved it
     * since; a window resize is not a reason to take that back.
     */
    updateCameraViewport() {
      if (!this.threeCamera || !this.threeRenderer) return
      const target = this.computeCameraTarget(this.cameraMode)
      if (!target) return
      this.threeCamera.aspect = target.aspect
      if (Number.isFinite(target.fov)) this.threeCamera.fov = target.fov
      this.threeCamera.updateProjectionMatrix()
      this.threeRenderer.setSize(target.renderW, target.renderH)
      this._playerShadowState = null
      this.updatePlayerShadowLight()
      // Anything sizing itself to the projected piece needs to recompute now
      this.cameraFitVersion += 1
      this.$emit('camera-fit')
      this.markNeedsRender()
    },

    /**
     * Whether an automatic reframe may move the camera right now.
     *
     * Under scroll controls the answer is always yes — the framing presets are
     * the camera model, so anything that changes what is on screen reframes to
     * suit it. Orbit keeps the presets for arrival only: they still pick the
     * distance an item loads at, and still promote-only across a run of items,
     * but a resize drag, a preview toggle or a window resize must not quietly
     * undo a viewpoint the user chose. The reset paths (fitForItemChange,
     * fitForItemSettled) go around this deliberately — a new item is exactly
     * when the camera should go back to its framing.
     */
    autoFramingAllowed() {
      return !this.orbitEnabled || !this.hasInitialFit
    },

    // ------------------------------------------------------------------
    // Render loop
    // ------------------------------------------------------------------

    /**
     * Keep drawing for a while. Anything that could change the picture calls
     * this; it pushes a single deadline forward rather than counting frames or
     * asking what is currently animating.
     *
     * The window is generous on purpose. Enumerating every animation and every
     * mutation site was the alternative, and that list quietly goes stale the
     * moment anyone adds a new one — a camera move driven straight from a scroll
     * handler was already missing from it, which left horizontal scrolling
     * drawing at the heartbeat. Drawing for a second longer than strictly needed
     * is a far cheaper mistake than not drawing at all, and the scene still
     * settles to the heartbeat whenever it is genuinely left alone.
     */
    markNeedsRender() {
      this._renderUntil = performance.now() + RENDER_LINGER_MS
      this.startRenderLoop()
    },

    /**
     * Draw rate for the 3D debug readout. Counted rather than timed, and only
     * while that view is on, so it costs nothing in normal use.
     */
    trackRenderRate(now) {
      this._renderRateCount = (this._renderRateCount || 0) + 1
      if (this._renderRateSince == null) this._renderRateSince = now
      const elapsed = now - this._renderRateSince
      if (elapsed < 500) return
      this.renderFps = Math.round((this._renderRateCount * 1000) / elapsed)
      this.renderActive = true
      this._renderRateCount = 0
      this._renderRateSince = now
    },

    startRenderLoop() {
      // Idempotent: several init paths call this, and a second concurrent rAF
      // chain would be unstoppable (stopRenderLoop only knows the newest id).
      if (this.animFrameId != null || this._teardown) return
      // Set directly rather than through markNeedsRender, which starts the loop
      // — and would recurse, because animFrameId is not assigned until the first
      // pass below runs.
      this._renderUntil = performance.now() + RENDER_LINGER_MS
      let lastTime = performance.now()
      const render = (now) => {
        this.animFrameId = requestAnimationFrame(render)
        if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return

        // Cap the draw rate: on a 120Hz+ display the extra frames cost a full
        // scene draw each and are indistinguishable. Every animation below is
        // driven by elapsed time rather than frame count, so they stay
        // time-correct however often this runs.
        if (now - lastTime < RENDER_FRAME_BUDGET_MS) return

        const deltaSeconds = Math.max(0, Math.min((now - lastTime) / 1000, 0.25))
        lastTime = now

        // Draw while the scene was recently touched. Free-look is the one state
        // asked about directly: held keys move the camera with no event per
        // frame to push the deadline.
        //
        // Going idle stops the loop outright rather than ticking over at some
        // low rate. The canvas keeps whatever was drawn last (the context is
        // created with preserveDrawingBuffer), so a paused renderer still shows
        // its scene while costing nothing at all — no draws, and no callbacks
        // either. markNeedsRender starts it again.
        if (!(now < this._renderUntil || this.freeLookActive)) {
          this.stopRenderLoop()
          return
        }
        if (this.debugLighting) this.trackRenderRate(now)

        if (this.freeLookActive) {
          this._updateFreeLookMovement(deltaSeconds)
        } else if (this._orbitControls && this.cameraAnimRafId == null) {
          // Damping outlives the gesture, so this runs whether or not input is
          // currently allowed; `enabled` only gates new input. Skipped while a
          // fit is easing, which owns the camera outright.
          this.applyOrbitRoomLimits()
          this._orbitControls.update()
        }

        // Damping and the idle drift both keep turning after the drag ends, so
        // this runs every frame the loop is awake, not only while the pointer
        // is down. The rig's own 'change' event keeps the loop awake, and the
        // delta is what makes the drift frame-rate independent. Skipped while a
        // pose animation is running, which owns the pose until a hand on the
        // piece cancels it — see onTurntableInteract.
        if (this._turntableControls?.enabled && !this._poseAnim) {
          this._turntableControls.update(deltaSeconds)
          this.readTurntableControls()
        }
        if (this._turntableViewControls?.enabled) {
          this.syncTurntableViewControls()
          this._turntableViewControls.update()
        }
        this.updatePoseAnimation(now)
        this.updatePoseOffWallLatch()
        this.updateTurntableDim(deltaSeconds)

        this.applySelectedItemPose()

        if (this.interactionGroup) {
          this.interactionGroup.visible = !this.suspendHeavyWork && !this._grabbed
          if (!this._grabbed) {
            this.syncInteractionGroupTransform()
            const activePos = this.getActiveScenePosition()
            this.interactionGroup.position.x = activePos.x
            this.interactionGroup.position.y = activePos.y
            this.syncHandleScreenSpaceScale()
          }
        }

        this._updateGrabbedItem()
        if (this.collisionEnabled && (
          (this.isDragging && this.dragMode === 'resize') || this._formResizePreviewActive
        )) {
          this._applyResizeCollision()
        }
        this._updateFreeLookDrag()
        this._updateLasso()
        this._updateDecorEntranceAnimation(now)
        this.updateResizeSnapAnimation(now)
        this.updateSizeSnapOverlayAnimation(deltaSeconds)
        this.updateWallSnapOverlayAnimation(deltaSeconds)
        if (!this._grabbed) this.updateHoverFromAimPoint()

        // Drifts the clouds while the scene is being drawn for some other
        // reason, and deliberately does not ask for a draw of its own: at this
        // cloud speed a frozen sky is indistinguishable from a moving one, and
        // making it a reason to render would mean the scene never idles.
        if (this._skyMesh) this._skyMesh.material.uniforms.time.value = now * 0.001

        this.updatePlayerShadowLight()
        if (this._shadowCameraHelper) this._shadowCameraHelper.update()
        this.renderSceneFrame()
      }
      render(performance.now())
    },

    /**
     * Park the loop only once the canvas has stayed disabled — see the
     * canvasEnabled watcher for why a disable is not taken at its word.
     */
    scheduleRenderLoopStop() {
      if (this._renderLoopStopTimer != null) return
      this._renderLoopStopTimer = setTimeout(() => {
        this._renderLoopStopTimer = null
        if (this.canvasEnabled) return
        this.stopRenderLoop()
      }, CANVAS_DISABLE_GRACE_MS)
    },

    clearPendingRenderLoopStop() {
      if (this._renderLoopStopTimer == null) return
      clearTimeout(this._renderLoopStopTimer)
      this._renderLoopStopTimer = null
    },

    stopRenderLoop() {
      this.clearPendingRenderLoopStop()
      if (this.animFrameId) {
        cancelAnimationFrame(this.animFrameId)
        this.animFrameId = null
      }
      this._renderRateCount = 0
      this._renderRateSince = null
      this.renderActive = false
      this.renderFps = 0
    },

    /**
     * Browsers stop servicing requestAnimationFrame for hidden tabs, so the loop
     * already idles when backgrounded — but that is a policy we would rather not
     * depend on (it varies for merely occluded windows, and across browsers).
     * Tearing the loop down explicitly makes "no work in a background tab" a
     * property of this component instead of an assumption about the host.
     */
    onDocumentVisibilityChange() {
      if (document.hidden) {
        this.stopRenderLoop()
        return
      }
      if (!this.threeRenderer) return
      // The scene may have changed while we were not drawing.
      this.markNeedsRender()
      this.startRenderLoop()
    },

    /** Wrap to (-π, π], so "back to square" is always the short way round. */
    normalizeAngle(angle) {
      const wrapped = (Number(angle) || 0) % (Math.PI * 2)
      if (wrapped > Math.PI) return wrapped - Math.PI * 2
      if (wrapped <= -Math.PI) return wrapped + Math.PI * 2
      return wrapped
    },

    // ------------------------------------------------------------------
    // Pose motion
    //
    // Everything that turns the piece — the flip button, the hang menu, the
    // handle's click-spin, the handle's drag, and the way back to the wall —
    // moves the same two numbers, and the stand-off follows from them (see
    // getPoseStandoff). So none of these moves has to describe coming off the
    // wall: they only say where the piece should end up pointing, and leaving
    // the wall falls out of the turn.
    // ------------------------------------------------------------------

    cancelPoseAnimation() {
      this._poseAnim = null
    },

    /**
     * Turn the piece to a given orientation over time.
     *
     * Duration is per unit of travel rather than fixed, so reversing a half-done
     * flip takes as long as the half it has to undo — which is what makes the
     * hang menu survive being opened and shut faster than the flip runs.
     */
    animatePoseTo({ yaw = 0, pitch = 0, speed = TURNTABLE_TURN_MS_PER_PI, minDuration = 260, ease = null, standoffFrom = null, onDone = null } = {}) {
      const from = { yaw: this._pose.yaw, pitch: this._pose.pitch }
      const travel = Math.max(Math.abs(yaw - from.yaw), Math.abs(pitch - from.pitch))
      if (travel < 1e-4) {
        this._poseAnim = null
        this._pose.yaw = yaw
        this._pose.pitch = pitch
        onDone?.()
        this.markNeedsRender()
        return
      }
      this._poseAnim = {
        kind: 'tween',
        fromYaw: from.yaw,
        fromPitch: from.pitch,
        toYaw: yaw,
        toPitch: pitch,
        startTime: performance.now(),
        duration: Math.max(minDuration, speed * (travel / Math.PI)),
        ease,
        eased: 0,
        standoffFrom,
        onDone,
      }
      this.markNeedsRender()
    },

    /**
     * How fast the click-spin is turning, as a function of how far it has come.
     *
     * Expressed as a velocity over angle rather than an angle over time because
     * the shape being asked for is about the turn, not about the clock: wind up
     * through the first half, fastest as the piece comes side-on at a half
     * turn, then shed speed through the last quarter until it is drifting. It
     * never reaches zero — past the settle point it hands over to the same
     * steady drift OrbitControls' own autoRotate will keep up afterwards, so
     * there is no seam where one stops and the other starts.
     */
    turntableSpinVelocity(turned) {
      if (turned <= TURNTABLE_SPIN_PEAK_AT) {
        // Winding up: eased, so the turn reads as the piece taking hold rather
        // than being switched on.
        const span = TURNTABLE_SPIN_PEAK_RAD_PER_S - TURNTABLE_SPIN_LAUNCH_RAD_PER_S
        return TURNTABLE_SPIN_LAUNCH_RAD_PER_S + span * THREE.MathUtils.smootherstep(turned / TURNTABLE_SPIN_PEAK_AT, 0, 1)
      }
      if (turned >= TURNTABLE_SPIN_SETTLE_AT) return TURNTABLE_SPIN_IDLE_RAD_PER_S
      // Shedding speed: eased at both ends, so the curve leaves the peak and
      // arrives at the drift with zero slope. Anything with a corner here —
      // linear, say — is a visible flinch at the two moments the eye is most
      // likely to be watching the piece rather than the picture.
      const span = TURNTABLE_SPIN_PEAK_RAD_PER_S - TURNTABLE_SPIN_IDLE_RAD_PER_S
      const p = (turned - TURNTABLE_SPIN_PEAK_AT) / (TURNTABLE_SPIN_SETTLE_AT - TURNTABLE_SPIN_PEAK_AT)
      return TURNTABLE_SPIN_IDLE_RAD_PER_S + (span * (1 - THREE.MathUtils.smootherstep(p, 0, 1)))
    },

    /**
     * Step whichever pose transition is running. Driven from the render loop
     * rather than its own rAF: the loop already applies the pose every frame,
     * so this only has to move the numbers and keep the loop awake.
     */
    updatePoseAnimation(now) {
      const anim = this._poseAnim
      if (!anim) return
      this.markNeedsRender()

      if (anim.kind === 'spin') {
        const dt = Math.min(0.05, Math.max(0, (now - anim.lastTime) / 1000))
        anim.lastTime = now
        anim.turned += this.turntableSpinVelocity(anim.turned) * dt
        this._pose.yaw = anim.fromYaw + anim.turned
        if (anim.turned < TURNTABLE_SPIN_SETTLE_AT) return
        this._poseAnim = null
        anim.onDone?.()
        return
      }

      const t = anim.duration > 0 ? Math.min(1, (now - anim.startTime) / anim.duration) : 1
      const eased = anim.ease ? anim.ease(t) : THREE.MathUtils.smootherstep(t, 0, 1)
      anim.eased = eased
      this._pose.yaw = anim.fromYaw + (anim.toYaw - anim.fromYaw) * eased
      this._pose.pitch = anim.fromPitch + (anim.toPitch - anim.fromPitch) * eased
      if (t < 1) return
      this._poseAnim = null
      anim.onDone?.()
    },

    /** Ease the scrim toward whatever the current mode wants. */
    updateTurntableDim(deltaSeconds) {
      const target = this._suppressFlipForThumbnail ? 0 : this._dimTarget
      if (this._dimValue === target) return
      const step = Math.max(0, deltaSeconds) / (TURNTABLE_DIM_FADE_MS / 1000)
      const delta = target - this._dimValue
      this._dimValue = Math.abs(delta) <= step ? target : this._dimValue + Math.sign(delta) * step
      this.markNeedsRender()
    },

    // --- the flip -------------------------------------------------------

    /**
     * Show the front or the back, idempotently.
     *
     * A half turn is a turntable move with the second axis left alone, so it
     * runs through the same animator and gets the same coming-off-the-wall
     * motion for free — and lands flat against the wall again at the far end,
     * because at a half turn the piece needs no clearance.
     *
     * Keyed to the requested state rather than the current angle, so repeat
     * calls during the turn are no-ops instead of restarts. That is what
     * callers mirroring some other piece of UI — a menu being open — want, as
     * against toggleFlip, which is what a button wants.
     */
    setFlipped(showBack) {
      const target = Boolean(showBack)
      if (this._flipTargetBack === target) return
      this._flipTargetBack = target
      this.exitTurntable({ instant: true })
      // A half turn sweeps the same arc off the wall as the handle does, so it
      // needs the same room to stay whole while it does it.
      this.requestTurntableFraming()
      this.animatePoseTo({
        // Home is forward once the back is showing — the piece has carried its
        // edge most of the way round, and rewinding from there reads as undo.
        // Before that it is barely into the turn and the way it came is also
        // the way home. Keyed to the face on show rather than to reaching
        // exactly a half turn, which a flip interrupted a degree short of the
        // end would miss, sending it back the way it came.
        yaw: target ? Math.PI : (this.isShowingBack() ? Math.PI * 2 : 0),
        pitch: 0,
        onDone: () => {
          // Fold the extra revolution away so the next flip starts from square.
          if (!target) this._pose.yaw = 0
          this.$emit('flipped', { showingBack: target })
        },
      })
    },

    flipToBack() {
      this.setFlipped(true)
    },

    flipToFront() {
      this.setFlipped(false)
    },

    /** Toggle between front and back view. */
    toggleFlip() {
      // Off the wall there is no front/back to toggle between — every
      // orientation is already reachable — so the button becomes the way back.
      if (this.turntablePhase !== 'idle') {
        this.exitTurntable()
        return
      }
      this.setFlipped(!this._flipTargetBack)
    },

    // ------------------------------------------------------------------
    // Turntable mode
    //
    // The pose above is available on the wall too — that is what the flip is.
    // What this adds is the mode around it: the room dimmed, OrbitControls on
    // the piece, the wheel dollying the camera, and a click anywhere else
    // putting it back.
    // ------------------------------------------------------------------

    /**
     * OrbitControls, pointed at the piece instead of at the camera.
     *
     * Turning an object and orbiting a camera around it are the same gesture
     * seen from opposite ends — a drag becomes a change in azimuth and polar
     * angle, with the same damping, momentum, touch handling and pointer
     * capture either way. So rather than reimplement that, the controls drive a
     * throwaway camera on a fixed sphere and the piece takes the inverse of
     * wherever that camera ended up. The room stays where it is, which is the
     * whole point: this dims the scene and lifts one thing out of it.
     *
     * Nothing here limits azimuth or polar angle because OrbitControls' own
     * defaults — a full turn horizontally, half a turn vertically — are already
     * the range this is specified to have.
     */
    ensureTurntableControls() {
      if (this._turntableControls) return this._turntableControls
      const rig = new THREE.PerspectiveCamera(50, 1, 0.1, TURNTABLE_RIG_RADIUS * 4)
      rig.position.set(0, 0, TURNTABLE_RIG_RADIUS)
      // Constructed unattached: connecting takes over the container's
      // touch-action, and while the piece is on the wall that belongs to
      // whatever else is driving the camera.
      const controls = new OrbitControls(rig, null)
      controls.enableDamping = true
      controls.dampingFactor = TURNTABLE_DAMPING_FACTOR
      controls.enablePan = false
      controls.enableZoom = false
      controls.rotateSpeed = TURNTABLE_ROTATE_SPEED
      // The drift the click-spin decays into, continued by the controls' own
      // auto-rotation so there is no handover to see. Same direction as a
      // rightward drag, and it pauses itself while a drag is in progress.
      controls.autoRotateSpeed = TURNTABLE_SPIN_IDLE_RAD_PER_S / ((Math.PI * 2) / 60)
      controls.autoRotate = false
      controls.target.set(0, 0, 0)
      controls.enabled = false
      controls.addEventListener('change', this._boundTurntableChange)
      controls.addEventListener('start', this._boundTurntableInteract)
      this._turntableRig = rig
      this._turntableControls = controls
      return controls
    },

    /**
     * A second OrbitControls, on the real camera rather than on the rig.
     *
     * The rig one turns the piece and takes the left button. This one takes the
     * right button and the wheel, which is what OrbitControls binds pan and
     * dolly to already — every action is gated by its own enable flag, so the
     * two instances divide the buttons between them without either having to
     * know about the other.
     *
     * Its target sits on the axis the camera is already looking down, so
     * adopting it moves nothing, and dolly converges on the piece rather than
     * on the wall behind it.
     */
    ensureTurntableViewControls() {
      if (this._turntableViewControls) return this._turntableViewControls
      if (!this.threeCamera) return null
      const controls = new OrbitControls(this.threeCamera, null)
      controls.enableRotate = false
      controls.enableDamping = true
      controls.dampingFactor = TURNTABLE_DAMPING_FACTOR
      // Panning should slide across the piece's plane, not along the camera's
      // own up axis, which would drift off it.
      controls.screenSpacePanning = true
      controls.enabled = false
      controls.addEventListener('change', this._boundTurntableChange)
      controls.addEventListener('start', this._boundTurntableInteract)
      this._turntableViewControls = controls
      return controls
    },

    /**
     * Point the view controls at the piece and bound how far they may travel:
     * from right up against its front face to the loosest preset's distance.
     * Re-derived each frame, because the front face moves as the piece turns.
     */
    syncTurntableViewControls() {
      const controls = this._turntableViewControls
      if (!controls || !this.threeCamera) return
      const front = this.getProjectionFrontZ() + this.getPoseStandoff()
      const bounds = this.getTurntableDollyBounds()
      controls.minDistance = Math.max(1, bounds.near - front)
      controls.maxDistance = Math.max(controls.minDistance + 1, bounds.far - front)
      if (controls.enabled) return
      // Adopting the point the camera is already aimed at, so arming the
      // controls does not move it.
      controls.target.set(this.threeCamera.position.x, EYE_LEVEL, front)
    },

    connectTurntableControls() {
      const controls = this.ensureTurntableControls()
      const container = this.$refs.container
      if (!controls || !container || controls.domElement) return controls
      this._turntableTouchAction = container.style.touchAction
      controls.connect(container)
      const view = this.ensureTurntableViewControls()
      if (view && !view.domElement) {
        this.syncTurntableViewControls()
        view.connect(container)
      }
      return controls
    },

    disconnectTurntableControls() {
      const view = this._turntableViewControls
      if (view?.domElement) {
        view.enabled = false
        view.disconnect()
        view.domElement = null
      }
      const controls = this._turntableControls
      const container = controls?.domElement
      if (!container) return
      controls.enabled = false
      controls.autoRotate = false
      controls.disconnect()
      controls.domElement = null
      // disconnect() leaves touch-action at 'auto', which is not necessarily
      // where it started — the camera's own OrbitControls may own it.
      container.style.touchAction = this._turntableTouchAction || ''
    },

    destroyTurntableControls() {
      this.disconnectTurntableControls()
      if (this._turntableViewControls) {
        this._turntableViewControls.removeEventListener('change', this._boundTurntableChange)
        this._turntableViewControls.removeEventListener('start', this._boundTurntableInteract)
        this._turntableViewControls = null
      }
      if (!this._turntableControls) return
      this._turntableControls.removeEventListener('change', this._boundTurntableChange)
      this._turntableControls.removeEventListener('start', this._boundTurntableInteract)
      this._turntableControls = null
      this._turntableRig = null
    },

    /**
     * Crack the piece open while its handle is under the pointer.
     *
     * Just another pose, so hovering shows you the exact motion the drag is
     * about to continue — the edge lifts and the piece stands off the wall by
     * the same rule, and a drag starting here simply carries on from the angle
     * the hover left.
     */
    syncTurntableHandlePeek(hovering) {
      const wanted = Boolean(hovering)
        && this.turntablePhase === 'idle'
        && !this._flipTargetBack
      if (this._posePeeking === wanted) return
      this._posePeeking = wanted
      this.animatePoseTo({ yaw: wanted ? this.getPeekYaw() : 0, pitch: 0 })
    },
    /** The angle that lifts this piece's edge by the peek distance. */
    getPeekYaw() {
      const box = this.getPoseClearanceBox()
      const lever = box?.halfW
      if (!(lever > 0)) return 0
      return Math.asin(THREE.MathUtils.clamp(TURNTABLE_PEEK_INCHES / lever, 0, 1))
    },

    /**
     * A hand on the piece ends the idle drift for good, and takes the turn over
     * from a spin still in flight rather than being ignored until it finishes.
     */
    onTurntableInteract() {
      if (this._turntableControls) this._turntableControls.autoRotate = false
      if (this.turntablePhase !== 'spinning') return
      this.cancelPoseAnimation()
      this.engageTurntable()
    },

    /**
     * Put the rig where it would have to be for the piece to already look the
     * way it does — so handing the gesture over to OrbitControls mid-pose (on
     * release of a drag, or at the end of a spin) does not jump.
     */
    syncTurntableControlsFromPose() {
      const controls = this._turntableControls
      if (!controls || !this._turntableRig) return
      const spherical = this._poseScratch.spherical
      spherical.set(TURNTABLE_RIG_RADIUS, (Math.PI / 2) - this._pose.pitch, -this._pose.yaw)
      spherical.makeSafe()
      this._turntableRig.position.setFromSpherical(spherical)
      this._turntableRig.lookAt(controls.target)
      controls.update()
    },

    /** The pose the rig is currently describing. Inverse of the sync above. */
    readTurntableControls() {
      const rig = this._turntableRig
      if (!rig) return
      const spherical = this._poseScratch.spherical.setFromVector3(rig.position)
      this._pose.yaw = -spherical.theta
      this._pose.pitch = (Math.PI / 2) - spherical.phi
    },

    setTurntablePhase(phase) {
      if (this.turntablePhase === phase) return
      const wasIdle = this.turntablePhase === 'idle'
      this.turntablePhase = phase
      const active = phase !== 'idle'
      // Leaving idle hands the pose to the gesture; the peek is no longer its own.
      if (active) this._posePeeking = false
      this._dimTarget = active ? 1 : 0
      // Armed through the spin as well, so the turn can be taken over in
      // flight; the spin keeps driving the pose until that actually happens.
      const handsOn = phase === 'engaged' || phase === 'spinning'
      if (this._turntableControls) this._turntableControls.enabled = handsOn
      if (this._turntableViewControls) {
        if (handsOn) this.syncTurntableViewControls()
        this._turntableViewControls.enabled = handsOn
      }
      this.syncOrbitInteraction()
      this.updateHandlesVisibility(this.hoveredObject)
      if (wasIdle && active) this.$emit('turntable', { active: true })
      if (!wasIdle && !active) this.$emit('turntable', { active: false })
      this.markNeedsRender()
    },

    /**
     * Enter turntable mode, carrying over whatever the piece was already doing.
     *
     * A flip in progress — or a piece already resting face-in — is absorbed
     * rather than undone, because the flip left the pose in exactly the terms
     * the turntable works in.
     */
    beginTurntable() {
      if (this.turntablePhase !== 'idle') return
      this.cancelPoseAnimation()
      this._flipTargetBack = false
      this.setTurntablePhase('peeling')
      this.requestTurntableFraming()
    },

    /** The click gesture: wind up, a full turn, then a steady drift. */
    startTurntableSpin() {
      // Connected before the spin starts, not after it ends: the controls have
      // to be listening for the whole turn or there is nothing for a hand on
      // the piece to interrupt. They stay inert meanwhile — the render loop
      // leaves them alone while a pose animation owns the pose.
      this.connectTurntableControls()
      this.setTurntablePhase('spinning')
      this._poseAnim = {
        kind: 'spin',
        fromYaw: this._pose.yaw,
        turned: 0,
        lastTime: performance.now(),
        onDone: () => this.engageTurntable({ drift: true }),
      }
      this.markNeedsRender()
    },

    engageTurntable({ drift = false } = {}) {
      // Rig first, phase second: setTurntablePhase is what arms the controls,
      // and arming them before they describe the pose the piece is already in
      // would snap it to wherever the rig was left.
      const controls = this.connectTurntableControls()
      this.syncTurntableControlsFromPose()
      if (controls) {
        controls.autoRotate = drift
        // Hand over at speed rather than from a standstill.
        //
        // OrbitControls' damping is a leaky integrator, so auto-rotation eases
        // up over about four tenths of a second — a dip at the exact moment the
        // spin hands over, which is the seam the velocity curve above exists to
        // avoid. Seeding it with the steady state that integrator converges to
        // removes the ramp. Assigned rather than added to, because whatever the
        // last gesture left in there is still decaying: adding overshot by a
        // third, and on a non-drift engage the residue read as the piece
        // creeping on its own.
        //
        // Guarded, so a three.js rename costs the smoothness rather than the
        // feature.
        const delta = controls._sphericalDelta
        if (delta) {
          const perFrame = TURNTABLE_SPIN_IDLE_RAD_PER_S / 60
          const damping = controls.dampingFactor || TURNTABLE_DAMPING_FACTOR
          delta.theta = drift ? -perFrame * (1 - damping) / damping : 0
          delta.phi = 0
        }
      }
      this.setTurntablePhase('engaged')
      this.$emit('viewer-message', {
        key: 'turntable',
        text: 'Drag to turn · scroll to zoom · click anywhere else to hang it back',
      })
      this.markNeedsRender()
    },

    /**
     * Hang the piece back up.
     *
     * Turning square and settling against the wall are the same motion here as
     * everywhere else — the stand-off is derived from the angle, so the piece
     * sinks back as it squares up without anything sequencing the two.
     */
    exitTurntable({ instant = false } = {}) {
      if (this.turntablePhase === 'idle') return
      this.disconnectTurntableControls()
      this._turntableDrag = null
      this._turntablePress = null
      // Covers an exit that lands mid-drag — an item switch, say — which still
      // owes the document its cursor and text selection back.
      this._endTurntableDragListeners()
      document.removeEventListener('mouseup', this._boundTurntablePressEnd)
      this.$emit('viewer-message', { key: 'turntable', remove: true })

      const held = this.getPoseStandoff()
      const settle = () => {
        this._pose.yaw = 0
        this._pose.pitch = 0
        this._poseOffWall = false
        this.setTurntablePhase('idle')
        this.applySelectedItemPose()
        this.syncInteractionGroupTransform()
        this.markNeedsRender()
      }

      if (instant) {
        this.cancelPoseAnimation()
        settle()
        return
      }

      this._poseOffWall = false
      this.setTurntablePhase('returning')
      this.restoreCameraFramingAfterTurntable()
      this.animatePoseTo({
        // Unwind to the nearest square, not to zero: after a spin or two the
        // piece is several revolutions from the origin and looks identical.
        yaw: this._pose.yaw - this.normalizeAngle(this._pose.yaw),
        pitch: 0,
        speed: TURNTABLE_RETURN_MS_PER_PI,
        // The piece has depth to travel as well as an angle, and near square
        // the angle is almost nothing. Priced on rotation alone the return
        // pulls a foot of depth in over the minimum duration, which is the cut
        // that reads as the animation ending early.
        minDuration: (held / TURNTABLE_RETURN_INCHES_PER_SEC) * 1000,
        standoffFrom: held,
        onDone: settle,
      })
    },

    // --- camera ---------------------------------------------------------

    /**
     * Pull the camera back far enough that the piece stays whole for the whole
     * turn, by asking for the tightest framing preset that manages it.
     *
     * Going through the presets rather than moving the camera directly keeps
     * the turntable inside the same framing vocabulary as everything else: the
     * mode buttons keep telling the truth, and the way out is symmetrical —
     * see restoreCameraFramingAfterTurntable.
     */
    requestTurntableFraming() {
      const mode = this.getTurntableFramingMode()
      if (!mode) return
      // Promote-only: coming off the wall is a reason to step back, never a
      // reason to close in on someone who has already stepped back further.
      if (mode !== this.cameraMode
        && shouldPromoteCameraMode(this.cameraMode || CAMERA_MODE_STANDARD, mode)) {
        this.$emit('request-camera-mode', mode, { force: true, reason: 'turntable' })
        return
      }
      // Already in a preset loose enough to name, but an ensureFit preset's
      // distance now includes room for the turn, so it has moved even though
      // the mode has not. Nothing else will notice, so refit here — this is
      // what keeps an oversized piece whole in wide while it turns.
      this.refitCameraForPose()
    },

    refitCameraForPose() {
      if (!this.threeCamera || this.freeLookActive) return
      const target = this.computeCameraTarget(this.cameraMode)
      if (!target) return
      if (Math.abs(target.targetZ - this.threeCamera.position.z) < 1) return
      this.animateFitCamera()
    },

    /** Camera z at which the piece stays whole through the whole turn. */
    getTurntableFitCameraZ() {
      const metrics = this.getContainerMetrics()
      const size = this.getSizingSceneSize()
      if (!metrics || !size) return null
      const fov = scaleFovForViewport(metrics.renderH, STANDARD_REFERENCE_VIEWPORT_HEIGHT)
      const fit = computeFitDistance(size.sceneW, size.sceneH, fov, metrics.renderAspect, AUTO_FRAMING_PIECE_PADDING)
      if (!Number.isFinite(fit)) return null
      return fit + this.getProjectionFrontZ() + this.getMaxPoseStandoff()
    },

    getTurntableFramingMode() {
      const needed = this.getTurntableFitCameraZ()
      if (!Number.isFinite(needed)) return null
      for (const preset of AUTO_FRAMING_PRESETS) {
        const z = Number(this.computeCameraTarget(preset.mode)?.targetZ)
        if (Number.isFinite(z) && z >= needed) return preset.mode
      }
      return AUTO_FRAMING_PRESETS[AUTO_FRAMING_PRESETS.length - 1].mode
    },

    /**
     * How near and how far the wheel may dolly while the piece is off the wall:
     * from right up against its front face to the loosest preset's distance.
     * Free movement between them — the presets are places to stand, and this is
     * the one mode where the piece rather than the room is the subject.
     */
    getTurntableDollyBounds() {
      const front = this.getProjectionFrontZ() + this.getPoseStandoff()
      const near = front + TURNTABLE_MIN_DOLLY_INCHES
      const loosest = AUTO_FRAMING_PRESETS[AUTO_FRAMING_PRESETS.length - 1].mode
      const wideZ = Number(this.computeCameraTarget(loosest)?.targetZ)
      return { near, far: Number.isFinite(wideZ) ? Math.max(wideZ, near) : near }
    },

    /**
     * On the way out, adopt the framing preset nearest to wherever the dolly
     * left the camera, and settle onto it. Without this the mode buttons would
     * still claim whatever was chosen on the way in, and the camera would stay
     * parked at some distance no preset describes.
     */
    async restoreCameraFramingAfterTurntable() {
      const mode = this.getNearestFramingModeToCamera()
      if (!mode) return
      if (mode !== this.cameraMode) {
        // Own the fit rather than leaving it to the cameraMode watcher, which
        // would smooth-scroll the overlay to the new preset.
        //
        // The overlay snaps to whole presets, so it cannot express a dolly that
        // stopped between two of them — it is sitting at whichever preset the
        // browser snapped it to, which may be the one behind us. Scrolling from
        // there starts by hauling the camera back to that preset before setting
        // off, which is the lurch this exit was showing.
        this._suppressNextCameraModeFit = true
        this.$emit('request-camera-mode', mode, { force: true, reason: 'turntable-exit' })
        await this.$nextTick()
        // A host that declines the request leaves the flag armed, and the next
        // mode change that does deserve a fit would silently lose it.
        if (this._suppressNextCameraModeFit && this.cameraMode !== mode) {
          this._suppressNextCameraModeFit = false
        }
      }
      // Eases from wherever the dolly left the camera, rather than from
      // wherever the overlay thinks it is.
      await this.animateFitCamera()
      // Landed on a preset, which is the one thing the overlay can express — so
      // this is the moment it can be brought back into step.
      this._scrollSnapOverlayAuthoritative = false
      this.syncScrollSnapOverlayToCamera()
    },

    // --- peel gesture ---------------------------------------------------

    _beginTurntablePeel(event) {
      this.beginTurntable()
      if (this.turntablePhase !== 'peeling') return
      this._turntableDrag = {
        startX: event.clientX,
        startY: event.clientY,
        yaw0: this._pose.yaw,
        pitch0: this._pose.pitch,
        moved: 0,
      }
      document.addEventListener('mousemove', this._boundTurntableDragMove)
      document.addEventListener('mouseup', this._boundTurntableDragEnd)
      document.body.style.cursor = 'grabbing'
      document.body.style.userSelect = 'none'
      event.preventDefault()
      this.markNeedsRender()
    },

    _onTurntableDragMove(event) {
      const drag = this._turntableDrag
      if (!drag) return
      const dx = event.clientX - drag.startX
      const dy = event.clientY - drag.startY
      drag.moved = Math.max(drag.moved, Math.hypot(dx, dy))
      this._pose.yaw = drag.yaw0 + (dx * TURNTABLE_DRAG_RADIANS_PER_PX)
      this._pose.pitch = THREE.MathUtils.clamp(
        drag.pitch0 + (dy * TURNTABLE_DRAG_RADIANS_PER_PX),
        -Math.PI / 2,
        Math.PI / 2,
      )
      this.markNeedsRender()
    },

    _endTurntableDragListeners() {
      document.removeEventListener('mousemove', this._boundTurntableDragMove)
      document.removeEventListener('mouseup', this._boundTurntableDragEnd)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    },

    _onTurntableDragEnd() {
      const drag = this._turntableDrag
      this._turntableDrag = null
      this._endTurntableDragListeners()
      if (!drag || this.turntablePhase !== 'peeling') return
      // A press that never travelled is a click, and a click asks for the full
      // turn rather than for the piece to sit a degree proud of the wall.
      if (drag.moved < TURNTABLE_CLICK_SLOP_PX) this.startTurntableSpin()
      else this.engageTurntable()
    },

    // --- click-away -----------------------------------------------------

    isPointerOnSelectedItem() {
      const key = this.hoveredObject?.userData?.renderItemKey
      if (key == null) return false
      return this.isSelectedRenderObject(this.hoveredObject)
    },

    /**
     * While the turntable is up every drag belongs to OrbitControls, so the
     * only thing left to read off a press is whether it was a click — and a
     * click on anything other than the piece puts the piece back.
     */
    _beginTurntablePress(event) {
      document.removeEventListener('mouseup', this._boundTurntablePressEnd)
      this._turntablePress = {
        x: event.clientX,
        y: event.clientY,
        onItem: this.isPointerOnSelectedItem(),
      }
      document.addEventListener('mouseup', this._boundTurntablePressEnd, { once: true })
    },

    _onTurntablePressEnd(event) {
      const press = this._turntablePress
      this._turntablePress = null
      if (!press || this.turntablePhase === 'idle') return
      const moved = Math.hypot(
        (event?.clientX ?? press.x) - press.x,
        (event?.clientY ?? press.y) - press.y,
      )
      if (moved >= TURNTABLE_CLICK_SLOP_PX) return
      if (press.onItem) return
      this.exitTurntable()
    },

    // --- dimming --------------------------------------------------------

    /**
     * A full-viewport scrim between the room and the piece.
     *
     * Drawn as its own pass rather than as geometry in the scene because what
     * has to be dimmed is "everything except one object", and no plane in the
     * scene separates those two: the piece turns, and the room has a floor and
     * furniture in front of the wall as well as behind it. Two passes with the
     * depth buffer cleared in between says exactly that, and has the useful
     * side effect of keeping the piece unoccluded however far it turns.
     */
    ensureTurntableDimPass() {
      if (this._turntableDimScene) return
      const scene = new THREE.Scene()
      const quad = new THREE.Mesh(
        new THREE.PlaneGeometry(2, 2),
        new THREE.MeshBasicMaterial({
          color: 0x0b0d10,
          transparent: true,
          opacity: 0,
          depthTest: false,
          depthWrite: false,
        }),
      )
      quad.frustumCulled = false
      scene.add(quad)
      this._turntableDimScene = scene
      this._turntableDimQuad = quad
      this._turntableDimCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
    },

    disposeTurntableDimPass() {
      if (!this._turntableDimQuad) return
      this._turntableDimQuad.geometry.dispose()
      this._turntableDimQuad.material.dispose()
      this._turntableDimScene = null
      this._turntableDimQuad = null
      this._turntableDimCamera = null
    },

    /**
     * Draw the frame. Plain single pass unless the turntable is up, in which
     * case: the room, the scrim over it, then the piece again on a cleared
     * depth buffer. The lights stay visible through the second pass — they are
     * what the piece is lit by — and the shadow maps are frozen across it,
     * since the only thing that changed is which objects are being drawn.
     */
    renderSceneFrame() {
      const renderer = this.threeRenderer
      if (!renderer) return
      const dim = this.getEffectiveTurntableDim()
      if (!(dim > 0.001)) {
        renderer.render(this.threeScene, this.threeCamera)
        return
      }

      this.ensureTurntableDimPass()
      const selected = this.getSelectedRenderInstance()?.group || null
      renderer.render(this.threeScene, this.threeCamera)

      const prevAutoClear = renderer.autoClear
      const prevShadowAutoUpdate = renderer.shadowMap.autoUpdate
      const prevBackground = this.threeScene.background
      const hidden = []
      renderer.autoClear = false
      renderer.shadowMap.autoUpdate = false

      try {
        this._turntableDimQuad.material.opacity = dim * TURNTABLE_DIM_OPACITY
        renderer.render(this._turntableDimScene, this._turntableDimCamera)
        renderer.clearDepth()

        // A scene background is not just something drawn first — a THREE.Color
        // one forces a full clear of the colour buffer whatever autoClear says,
        // and a texture one paints a mesh over everything. Either way the second
        // pass would erase the room and the scrim it was asked to leave behind,
        // so the background belongs to the first pass only.
        this.threeScene.background = null

        for (const child of this.threeScene.children) {
          if (!child.visible || child.isLight) continue
          if (child === selected || child === this.interactionGroup) continue
          child.visible = false
          hidden.push(child)
        }
        renderer.render(this.threeScene, this.threeCamera)
      } finally {
        for (const child of hidden) child.visible = true
        this.threeScene.background = prevBackground
        renderer.autoClear = prevAutoClear
        renderer.shadowMap.autoUpdate = prevShadowAutoUpdate
      }
    },

    // ------------------------------------------------------------------
    // Interaction handlers
    // ------------------------------------------------------------------

    getMouseNDC(event) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      return new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      )
    },

    getScrollableRenderSpecs() {
      const rawSpecs = this.getRenderSpecs()
      const wallLayout = this.getDynamicWallLayout(rawSpecs)
      return rawSpecs
        .map(spec => this.normalizeRenderSpec(spec, wallLayout))
        // Scalar (single-item) renders intentionally have no persisted itemId, but they still
        // need the vertical scroll track that drives standard <-> wall zoom. Every render spec
        // has a stable key; itemId is only required later when changing project selection.
        .filter(spec => spec?.key != null && Number.isFinite(Number(spec.position?.x)))
    },

    getCameraPanBounds(specs = this.getScrollableRenderSpecs()) {
      if (!specs.length) return null
      const xs = specs.map(spec => Number(spec.position.x)).filter(Number.isFinite)
      if (!xs.length) return null
      return {
        min: Math.min(...xs),
        max: Math.max(...xs),
      }
    },

    clampCameraPanX(x) {
      const bounds = this.getCameraPanBounds()
      if (!bounds) return x
      return Math.min(Math.max(x, bounds.min), bounds.max)
    },

    setCameraCenterX(x) {
      if (!this.threeCamera) return
      const centerX = this.clampCameraPanX(x)
      const projectionFrontZ = this.getProjectionFrontZ()
      this.threeCamera.position.x = centerX
      this.threeCamera.lookAt(centerX, EYE_LEVEL, projectionFrontZ)
      this.threeCamera.updateMatrixWorld()
      this._playerShadowState = null
      this.updatePlayerShadowLight()
      this.updateHoverFromAimPoint()
    },

    getScrollSnapSpecWidth(spec) {
      const instance = spec?.key != null ? this._renderItemInstances?.get(String(spec.key)) : null
      const builtWidth = Number(instance?.builtDims?.outerWidth)
      if (Number.isFinite(builtWidth) && builtWidth > 0) return builtWidth

      const targetWidth = Number(spec?.rendererProps?.targetWidth)
      if (Number.isFinite(targetWidth) && targetWidth > 0) return targetWidth

      const artWidth = Number(spec?.rendererProps?.artWidth)
      return Number.isFinite(artWidth) && artWidth > 0 ? artWidth : 24
    },
    getStableScrollSnapPxPerUnit({ viewportWidth, span, itemCount }) {
      if (!Number.isFinite(viewportWidth) || viewportWidth <= 0 || !Number.isFinite(span) || span <= 0) {
        return 8
      }
      const gaps = Math.max(1, Number(itemCount) - 1)
      const pxPerUnit = (viewportWidth * gaps) / span
      return Math.max(SCROLL_SNAP_MIN_PX_PER_UNIT, Math.min(SCROLL_SNAP_MAX_PX_PER_UNIT, pxPerUnit))
    },

    buildScrollSnapLayout() {
      const container = this.$refs.container
      // Orbit replaces the overlay outright rather than sitting under it: the
      // overlay is a full-bleed scroller above the canvas, so while it exists
      // no pointer reaches anything else.
      if (this.orbitEnabled) return null
      if (!container || !this.interactive || this.freeLookActive || this.cameraMode === CAMERA_MODE_FREE) {
        return null
      }

      const rect = container.getBoundingClientRect()
      const viewportWidth = rect.width || 0
      const viewportHeight = rect.height || 0
      if (viewportWidth <= 0 || viewportHeight <= 0) return null

      const specs = this.getScrollableRenderSpecs()
        .sort((a, b) => Number(a.position.x) - Number(b.position.x))
      if (!specs.length) return null

      const minX = Number(specs[0].position.x)
      const maxX = Number(specs[specs.length - 1].position.x)
      const span = Math.max(1, maxX - minX)
      const pxPerUnit = this.getStableScrollSnapPxPerUnit({
        viewportWidth,
        span,
        itemCount: specs.length,
      })
      const scrollRange = span * pxPerUnit
      const edgeOverscrollPx = SCROLL_SNAP_EDGE_OVERSCROLL_PX
      const edgeOverscrollUnits = edgeOverscrollPx / pxPerUnit
      const scrollMinX = minX - edgeOverscrollUnits
      // One row per framing preset, each a viewport tall, stacked in preset
      // order so scrolling down walks tightest -> loosest.
      const rowStride = Math.max(1, viewportHeight)
      const verticalRange = rowStride * Math.max(1, AUTO_FRAMING_PRESETS.length - 1)
      // Vertical edge padding mirrors horizontal. Without it, growing the window can push
      // scrollTop past the scrollable max before the track has been updated, causing the
      // browser's mandatory snap to jump to the first item.
      const vertEdgeOverscrollPx = SCROLL_SNAP_EDGE_OVERSCROLL_PX
      const modeRows = AUTO_FRAMING_PRESETS.map((preset, index) => ({
        mode: preset.mode,
        top: vertEdgeOverscrollPx + (rowStride * index),
        center: vertEdgeOverscrollPx + (viewportHeight / 2) + (rowStride * index),
        label: preset.mode.charAt(0).toUpperCase() + preset.mode.slice(1),
      }))
      const items = specs.map((spec) => {
        const itemWidth = Math.max(24, Math.min(360, this.getScrollSnapSpecWidth(spec) * pxPerUnit))
        const center = (viewportWidth / 2) + ((Number(spec.position.x) - scrollMinX) * pxPerUnit)
        return {
          key: String(spec.key),
          itemId: spec.itemId,
          x: Number(spec.position.x),
          left: center - (itemWidth / 2),
          width: itemWidth,
          label: spec.itemId != null ? String(spec.itemId) : String(spec.key),
        }
      })
      const signature = JSON.stringify({
        viewportWidth: Math.round(viewportWidth),
        viewportHeight: Math.round(viewportHeight),
        pxPerUnit: Number(pxPerUnit.toFixed(4)),
        edgeOverscrollPx: Math.round(edgeOverscrollPx),
        items: items.map(item => ({
          key: item.key,
          id: item.itemId,
          x: Number(item.x.toFixed(4)),
          width: Math.round(item.width),
        })),
      })

      return {
        enabled: true,
        signature,
        minX: scrollMinX,
        maxX,
        pxPerUnit,
        viewportWidth,
        viewportHeight,
        verticalRange,
        rowStride,
        vertEdgeOverscrollPx,
        trackWidth: Math.ceil(viewportWidth + scrollRange + (edgeOverscrollPx * 2)),
        trackHeight: Math.ceil(viewportHeight + verticalRange + (vertEdgeOverscrollPx * 2)),
        items,
        modeRows,
        cells: items.flatMap(item => modeRows.map(row => ({
          key: `${item.key}:${row.mode}`,
          itemId: item.itemId,
          itemKey: item.key,
          x: item.x,
          mode: row.mode,
          left: item.left,
          top: row.top,
          width: item.width,
          height: viewportHeight,
          label: `${item.label} · ${row.label}`,
        }))),
      }
    },

    refreshScrollSnapOverlayLayout() {
      if (this.cameraAnimRafId != null) return
      const layout = this.buildScrollSnapLayout()
      if (!layout) {
        this.scrollSnapLayout = null
        this._scrollSnapLayoutSignature = null
        return
      }
      if (layout.signature === this._scrollSnapLayoutSignature) {
        if (!this._scrollSnapSelectionFitPending) {
          this.$nextTick(() => this.syncScrollSnapOverlayToCamera())
        }
        return
      }
      this.scrollSnapLayout = layout
      this._scrollSnapLayoutSignature = layout.signature
      // If we're mid-scroll to a new item, re-issue the scroll so the target uses
      // the freshly-computed snap positions (pxPerUnit may have changed).
      if (this._scrollSnapSelectionFitPending) {
        this.$nextTick(() => this.scrollSnapOverlayToSelectedSpec(undefined, { behavior: 'smooth' }))
        return
      }
      // Layout changed (e.g. window resize) — the overlay position is now stale.
      // Hand control back to the camera so syncScrollSnapOverlayToCamera can
      // reposition the overlay to the correct snap point.
      this._scrollSnapOverlayAuthoritative = false
      this.$nextTick(() => this.syncScrollSnapOverlayToCamera())
    },

    syncScrollSnapOverlayToCamera() {
      // Don't overwrite the overlay while a smooth scroll to the selected item is in progress —
      // the overlay is driving the camera, not the other way around.
      if (this._scrollSnapSelectionFitPending) return
      if (this._scrollSnapOverlayAuthoritative) return
      if (this.cameraAnimRafId != null) return
      const overlay = this.$refs.scrollSnapOverlay
      const layout = this.scrollSnapLayout
      if (!overlay || !layout || !this.threeCamera) return

      const scrollLeft = (this.clampCameraPanX(this.threeCamera.position.x) - layout.minX) * layout.pxPerUnit
      const progress = this.getWheelModeProgress()
      const vertEdge = layout.vertEdgeOverscrollPx ?? 0
      const lastRow = Math.max(0, (layout.modeRows?.length ?? 1) - 1)
      const scrollTop = (THREE.MathUtils.clamp(progress, 0, lastRow) * layout.rowStride) + vertEdge
      if (Math.abs(overlay.scrollLeft - scrollLeft) < 1 && Math.abs(overlay.scrollTop - scrollTop) < 1) return

      this.suppressScrollSnapOverlayScroll()
      overlay.scrollLeft = scrollLeft
      overlay.scrollTop = scrollTop
    },

    /**
     * Ignore the overlay's scroll events for the couple of frames they take to
     * arrive. Its offset is written by us as often as by the user — a camera
     * sync assigns it, and a re-layout has the browser clamp it to the new
     * scroll extent — and neither of those is a gesture.
     */
    suppressScrollSnapOverlayScroll() {
      this._syncingScrollSnapOverlay = true
      if (this._scrollSnapSyncRafId != null) {
        cancelAnimationFrame(this._scrollSnapSyncRafId)
      }
      this._scrollSnapSyncRafId = requestAnimationFrame(() => {
        this._scrollSnapSyncRafId = requestAnimationFrame(() => {
          this._scrollSnapSyncRafId = null
          this._syncingScrollSnapOverlay = false
        })
      })
    },

    finishScrollSnapOverlaySync() {
      if (this._scrollSnapSyncRafId != null) {
        cancelAnimationFrame(this._scrollSnapSyncRafId)
        this._scrollSnapSyncRafId = null
      }
      this._syncingScrollSnapOverlay = false
    },

    beginUserScrollSnapOverlayInput() {
      this.finishScrollSnapOverlaySync()
      this.cancelCameraAnimation({ runPending: true })
      this._scrollSnapOverlayAuthoritative = true
      this._scrollSnapSelectionFitPending = false
      this._scrollSnapInstantFitKey = null
      this._scrollSnapInstantJumpKeys.clear()
    },

    ignoreScrollSnapOverlayScroll() {
      if (!this._syncingScrollSnapOverlay) return false
      this.clearScrollSnapSettleTimer()
      if (this.cameraAnimRafId == null) {
        this._syncingScrollSnapOverlay = false
      }
      return true
    },

    clearScrollSnapSettleTimer() {
      if (this._scrollSnapSettleTimer != null) {
        clearTimeout(this._scrollSnapSettleTimer)
        this._scrollSnapSettleTimer = null
      }
    },

    getClosestScrollSnapItem() {
      const overlay = this.$refs.scrollSnapOverlay
      const layout = this.scrollSnapLayout
      if (!overlay || !layout?.items?.length) return null
      const centerX = layout.minX + (overlay.scrollLeft / layout.pxPerUnit)
      return layout.items.reduce((closest, item) => {
        const dist = Math.abs(item.x - centerX)
        if (!closest || dist < closest.dist) return { item, dist }
        return closest
      }, null)?.item || null
    },

    getClosestScrollSnapMode() {
      const overlay = this.$refs.scrollSnapOverlay
      const layout = this.scrollSnapLayout
      if (!overlay || !layout?.modeRows?.length) return null
      const centerY = (layout.viewportHeight / 2) + overlay.scrollTop
      return layout.modeRows.reduce((closest, row) => {
        const dist = Math.abs(row.center - centerY)
        if (!closest || dist < closest.dist) return { row, dist }
        return closest
      }, null)?.row?.mode || null
    },

    getScrollSnapItemForSpec(spec) {
      const layout = this.scrollSnapLayout
      if (!layout?.items?.length || !spec?.key) return null
      return layout.items.find(item => String(item.key) === String(spec.key)) || null
    },

    getScrollSnapSpecForRenderItem({ itemId = null, key = null } = {}) {
      if (key != null) {
        const spec = this.getRenderSpecByKey(key)
        if (spec) return spec
      }
      if (itemId == null) return null
      return this.getScrollableRenderSpecs()
        .find(spec => String(spec?.itemId) === String(itemId)) || null
    },

    scrollSnapOverlayToRenderItem({ itemId = null, key = null, behavior = 'smooth' } = {}) {
      if (this.freeLookActive || this.cameraMode === CAMERA_MODE_FREE) return false
      if (!this.scrollSnapLayout?.enabled || !this.$refs.scrollSnapOverlay) return false

      const spec = this.getScrollSnapSpecForRenderItem({ itemId, key })
      const target = this.getScrollSnapTargetForSpec(spec)
      if (!spec || !target) return false

      this.finishScrollSnapOverlaySync()
      this.cancelCameraAnimation({ runPending: true })
      this._scrollSnapSelectionFitPending = false
      return this.scrollSnapOverlayToSelectedSpec(spec, { behavior })
    },

    getScrollSnapTargetForSpec(spec) {
      const layout = this.scrollSnapLayout
      const item = this.getScrollSnapItemForSpec(spec)
      if (!layout || !item) return null
      // Which item, only. The overlay's two axes each own one thing — across
      // for the piece, down for the framing preset — and writing the other
      // one's axis is how they came to disagree: an item change carried a
      // framing with it, taken from whichever of the two had not yet caught up.
      return { item, left: (item.x - layout.minX) * layout.pxPerUnit }
    },
    scrollSnapOverlayToCameraMode(mode, { behavior = 'smooth' } = {}) {
      const overlay = this.$refs.scrollSnapOverlay
      const layout = this.scrollSnapLayout
      if (!overlay || !layout || this.freeLookActive || this.cameraMode === CAMERA_MODE_FREE) return false

      const vertEdge = layout.vertEdgeOverscrollPx ?? 0
      const top = vertEdge + (layout.rowStride * this.getScrollSnapModeRow(mode))
      this.finishScrollSnapOverlaySync()
      this.cancelCameraAnimation({ runPending: true })
      this._scrollSnapOverlayAuthoritative = true
      overlay.scrollTo({ left: overlay.scrollLeft, top, behavior })
      this.scheduleScrollSnapOverlaySelection()
      return true
    },

    shouldScrollSnapToRenderItem({ itemId = null } = {}) {
      return Boolean(
        itemId != null &&
        String(itemId) !== String(this.selectedItemId || '') &&
        !this.freeLookActive &&
        this.cameraMode !== CAMERA_MODE_FREE &&
        this.getScrollableRenderSpecs().length > 1
      )
    },

    requestScrollSnapToRenderItem(item) {
      this.finishScrollSnapOverlaySync()
      this.cancelCameraAnimation({ runPending: true })
      this._scrollSnapSelectionFitPending = false
      if (!this.scrollSnapOverlayToRenderItem(item)) {
        this.$nextTick(() => {
          this.refreshScrollSnapOverlayLayout()
          this.$nextTick(() => this.scrollSnapOverlayToRenderItem(item))
        })
      }
    },

    scrollSnapOverlayToSelectedSpec(spec = this.getSelectedRenderSpec(), { behavior = 'smooth' } = {}) {
      const overlay = this.$refs.scrollSnapOverlay
      const target = this.getScrollSnapTargetForSpec(spec)
      if (!overlay || !target) return false

      const { left } = target

      // An instant jump is the arrow-key / next-item path, and the scroll event
      // it fires is exactly what carries the camera there — so unlike a
      // camera-driven sync write it is not suppressed. The framing that follows
      // the selection change is taken instantly too, or the piece would arrive
      // in one step with the camera still easing in behind it.
      this._scrollSnapInstantFitKey = behavior === 'instant' && target.item?.key != null
        ? String(target.item.key)
        : null
      if (this._scrollSnapInstantFitKey) this._scrollSnapInstantJumpKeys.add(this._scrollSnapInstantFitKey)
      else this._scrollSnapInstantJumpKeys.clear()

      // Omitting top holds the current one: the piece changes, the zoom does not.
      overlay.scrollTo({ left, behavior })
      this.scheduleScrollSnapOverlaySelection()
      return true
    },

    settleScrollSnapOverlaySelection() {
      this._scrollSnapSettleTimer = null
      this.applyScrollSnapSelection({
        item: this.getClosestScrollSnapItem(),
        mode: this.getClosestScrollSnapMode(),
      })
    },

    /**
     * Adopt the snap position the overlay rests at (or has committed to): take
     * its framing preset, and hand its item to the host as the selection.
     * Reached from the settle timer and — sooner, while the scroll is still
     * moving — from the browser's own scroll-snap events.
     */
    applyScrollSnapSelection({ item, mode }) {
      if (mode && mode !== this.cameraMode && this.cameraAnimRafId == null) {
        this._suppressNextCameraModeFit = true
        this.$emit('request-camera-mode', mode, { force: true, reason: 'scroll-snap-overlay' })
      }

      // Wheel-scrolling the snap overlay must not navigate away and wipe an
      // active multi-selection.
      if ((this.selectedItemIds?.length || 0) >= 2) return

      // Only a host that owns a selection can be handed one. A lone item's
      // overlay shows the piece already on screen, and comparing its id against
      // an absent selectedItemId made every settle a mismatch: each one armed
      // the handoff latch and asked to select the item already selected. The
      // branch that clears the latch needs that same comparison to be false, so
      // it never ran — leaving auto framing switched off for good.
      const selectedId = this.selectedItemId
      const settledOnSelection = selectedId == null || String(item?.itemId) === String(selectedId)

      if (selectedId != null && item?.itemId && !settledOnSelection) {
        this._scrollSnapSelectionFitPending = true
        this.$emit('select-render-item', {
          itemId: item.itemId,
          key: item.key,
        })
        return
      }

      if (this._scrollSnapSelectionFitPending && item?.itemId && settledOnSelection) {
        this.finishScrollSnapSelectionHandoff()
      }
    },

    resolveScrollSnapEventTargets(event) {
      const layout = this.scrollSnapLayout
      const itemKey = event?.snapTargetInline?.dataset?.snapItemKey
      const eventMode = event?.snapTargetBlock?.dataset?.snapMode || null
      return {
        item: itemKey != null
          ? (layout?.items || []).find(entry => String(entry.key) === String(itemKey)) || null
          : null,
        mode: (layout?.modeRows || []).some(row => row.mode === eventMode) ? eventMode : null,
      }
    },

    /**
     * The browser has finished snapping. This is the same moment the settle
     * timer was guessing at, told to us outright — so the item changes as soon
     * as the scroll stops rather than a fifth of a second later, and without
     * putting the swap inside a frame the scroll is still animating.
     */
    onScrollSnapOverlaySnapChange(event) {
      if (this._syncingScrollSnapOverlay) return

      const { item, mode } = this.resolveScrollSnapEventTargets(event)
      if (this.isScrollSnapInstantJumpTarget(item)) return
      // It fires with the scroll at rest, so the target it names has to be the
      // one the overlay is sitting on. Anything else is a straggler from a jump
      // the overlay has already left.
      const restingItem = this.getClosestScrollSnapItem()
      if (item && restingItem && String(item.key) !== String(restingItem.key)) return
      if (item || mode) this.applyScrollSnapSelection({ item, mode })
    },

    /**
     * The snap the browser is reporting is the landing of an instant jump we
     * made on the host's behalf. The host is already navigating there and takes
     * the framing with it, so treating this as a fresh gesture would only
     * re-announce the selection and hand the fit back to the settle path.
     */
    isScrollSnapInstantJumpTarget(item) {
      return Boolean(item && this._scrollSnapInstantJumpKeys.has(String(item.key)))
    },

    finishScrollSnapSelectionHandoff() {
      this.clearScrollSnapSettleTimer()
      this._scrollSnapSelectionFitPending = this._scrollSnapOverlayAuthoritative = false
    },

    scheduleScrollSnapOverlaySelection() {
      this.clearScrollSnapSettleTimer()
      this._scrollSnapSettleTimer = setTimeout(() => this.settleScrollSnapOverlaySelection(), SCROLL_SNAP_SETTLE_MS)
    },

    onScrollSnapOverlayScroll() {
      if (this.ignoreScrollSnapOverlayScroll()) return
      const overlay = this.$refs.scrollSnapOverlay
      const layout = this.scrollSnapLayout
      if (!overlay || !layout || !this.threeCamera) return

      this._scrollSnapOverlayAuthoritative = true
      this.cancelCameraAnimation({ runPending: true })
      const centerX = layout.minX + (overlay.scrollLeft / layout.pxPerUnit)
      const targets = this.getModeTargets()
      const vertEdge = layout.vertEdgeOverscrollPx ?? 0
      if (targets) {
        const last = targets.length - 1
        const progress = (overlay.scrollTop - vertEdge) / layout.rowStride
        let cameraZ
        if (progress < 0) {
          // Harmonic (1/Z) interpolation resists zooming in past the tightest preset naturally
          cameraZ = 1 / THREE.MathUtils.lerp(1 / targets[0].targetZ, 1 / targets[1].targetZ, progress)
        } else if (progress > last) {
          // Scale down overscroll past the loosest preset — harmonic amplifies this direction
          cameraZ = THREE.MathUtils.lerp(
            targets[last - 1].targetZ, targets[last].targetZ, 1 + (progress - last) * 0.4
          )
        } else {
          const row = Math.min(Math.floor(progress), last - 1)
          cameraZ = THREE.MathUtils.lerp(targets[row].targetZ, targets[row + 1].targetZ, progress - row)
        }
        this.setCameraCenterAndDistance(centerX, cameraZ, this.cameraMode || CAMERA_MODE_STANDARD)
      }
      else this.setCameraCenterX(centerX)
      this.scheduleScrollSnapOverlaySelection()
    },

    onScrollSnapOverlayWheel(event) {
      this.beginUserScrollSnapOverlayInput()
      event.stopPropagation()
    },

    onScrollSnapOverlayPointerDown(event) {
      if (event.button !== 2) return
      if (this.debugScroll) return
      const overlay = this.$refs.scrollSnapOverlay
      if (!overlay) return

      this.restoreScrollSnapOverlayContextMenuBypass()
      overlay.style.pointerEvents = 'none'
      setTimeout(() => {
        window.addEventListener('pointermove', this.restoreScrollSnapOverlayContextMenuBypass, { once: true, capture: true })
      }, 0)
    },

    restoreScrollSnapOverlayContextMenuBypass() {
      window.removeEventListener('pointermove', this.restoreScrollSnapOverlayContextMenuBypass, { capture: true })
      const overlay = this.$refs.scrollSnapOverlay
      if (overlay) overlay.style.pointerEvents = ''
    },

    setCameraCenterAndDistance(centerX, z, mode = this.cameraMode) {
      if (!this.threeCamera || !this.threeRenderer) return
      const target = this.computeCameraTarget(mode)
      if (!target) return

      const projectionFrontZ = this.getProjectionFrontZ()
      this.threeCamera.aspect = target.aspect
      if (Number.isFinite(target.fov)) this.threeCamera.fov = target.fov
      this.threeCamera.position.set(centerX, EYE_LEVEL, z)
      this.threeCamera.lookAt(centerX, EYE_LEVEL, projectionFrontZ)
      this.threeCamera.updateProjectionMatrix()
      this.threeRenderer.setSize(target.renderW, target.renderH)
      this._playerShadowState = null
      this.updatePlayerShadowLight()
      this.updateHoverFromAimPoint()
    },

    getClosestRenderSpecToCameraCenter() {
      if (!this.threeCamera) return null
      const specs = this.getScrollableRenderSpecs()
      if (!specs.length) return null
      const centerX = this.threeCamera.position.x
      return specs.reduce((closest, spec) => {
        const dist = Math.abs(Number(spec.position.x) - centerX)
        if (!closest || dist < closest.dist) return { spec, dist }
        return closest
      }, null)?.spec || null
    },

    getRenderSpecNearX(x) {
      const specs = this.getScrollableRenderSpecs()
      if (!specs.length) return null
      return specs.reduce((closest, spec) => {
        const dist = Math.abs(Number(spec.position.x) - x)
        if (!closest || dist < closest.dist) return { spec, dist }
        return closest
      }, null)?.spec || null
    },

    cancelCameraAnimation({ runPending = false } = {}) {
      if (this.cameraAnimRafId != null) {
        cancelAnimationFrame(this.cameraAnimRafId)
        this.cameraAnimRafId = null
        // The ease held the camera and left the orbit pivot behind. Pick up
        // from wherever it stopped rather than snapping to the abandoned target.
        this.syncOrbitControlsToCamera()
        this.syncOrbitInteraction()
        if (runPending) this.runPendingHeavyRebuild()
      }
    },

    /** Camera target per framing preset, in preset (tightest -> loosest) order. */
    /**
     * Where each framing preset puts the camera — blended across the pieces the
     * pan is between, rather than taken from whichever piece is selected.
     *
     * This is what the overlay reads to turn a scroll position into a camera
     * distance, and it was answering for the selected piece only. Panning
     * toward a piece too big for the loosest preset to hold therefore travelled
     * at the outgoing piece's distance the whole way, and the extra room the
     * incoming one needs only appeared once the selection changed — by which
     * point the scroll had stopped and there was nothing left to animate it.
     *
     * Reading it from the pan instead means the camera dollies back over the
     * same travel that carries it sideways, and arrives already fitted.
     */
    getModeTargets() {
      const weights = this.getPanWeights()
      const targets = AUTO_FRAMING_PRESETS.map(preset => this.computeBlendedCameraTarget(preset.mode, weights))
      return targets.every(Boolean) && targets.length >= 2 ? targets : null
    },
    getPanWeights() {
      const specs = this.getScrollableRenderSpecs()
      if (specs.length < 2) return []
      const weights = []
      specs.forEach((spec) => {
        const share = this.getPanShareForSpec(spec, specs)
        if (share > 0.001) weights.push({ spec, share })
      })
      return weights
    },
    computeBlendedCameraTarget(mode, weights) {
      if (!weights.length) return this.computeCameraTarget(mode)
      let blended = null
      let targetZ = 0
      let total = 0
      for (const { spec, share } of weights) {
        const target = this.computeCameraTarget(mode, this.getSceneSizeForSpec(spec))
        if (!target) return null
        blended = blended || target
        targetZ += target.targetZ * share
        total += share
      }
      return total > 0 ? { ...blended, targetZ: targetZ / total } : blended
    },
    /** A piece's finished size, from its own built geometry where it exists. */
    getSceneSizeForSpec(spec) {
      const instance = spec?.key != null ? this._renderItemInstances?.get(String(spec.key)) : null
      const builtW = Number(instance?.builtDims?.outerWidth)
      const builtH = Number(instance?.builtDims?.outerHeight)
      if (builtW > 0 && builtH > 0) return { sceneW: inches(builtW), sceneH: inches(builtH) }
      const props = spec?.rendererProps || {}
      const width = Number(props.targetWidth) || Number(props.artWidth)
      const height = Number(props.targetHeight) || Number(props.artHeight)
      if (width > 0 && height > 0) return { sceneW: inches(width), sceneH: inches(height) }
      return this.getSizingSceneSize()
    },

    /** Row index of a mode on the overlay's vertical axis. */
    getScrollSnapModeRow(mode) {
      const rank = getCameraModeRank(mode)
      return rank === -1 ? 0 : rank
    },

    /**
     * Where the camera sits on the overlay's vertical axis, in rows: 0 is the
     * tightest preset, 1 the next, and so on, with fractions between them.
     */
    getWheelModeProgress() {
      if (!this.threeCamera) return 0
      const targets = this.getModeTargets()
      if (!targets) return 0

      const last = targets.length - 1
      const z = this.threeCamera.position.z
      for (let row = 0; row < last; row++) {
        const span = targets[row + 1].targetZ - targets[row].targetZ
        if (Math.abs(span) < 1e-6) continue
        const t = (z - targets[row].targetZ) / span
        if (t < 1 || row === last - 1) return THREE.MathUtils.clamp(row + t, 0, last)
      }
      // Every preset collapsed onto the same distance — fall back to the mode itself.
      return THREE.MathUtils.clamp(this.getScrollSnapModeRow(this.cameraMode), 0, last)
    },

    isInteractiveRaycastMesh(obj, showingBack, frontHandleTypes) {
      if (!obj?.isMesh || !obj.userData?.type) return false
      if (obj.userData.type === 'panel-bevel-rim') return false
      if (showingBack && frontHandleTypes.has(obj.userData.type)) return false
      // Off the wall the handles are hidden, and a hidden handle that still
      // answers the raycast would read as "not the piece" and hang it back up.
      if (this.turntablePhase !== 'idle' && frontHandleTypes.has(obj.userData.type)) return false
      if (obj.userData.type === 'source-handle-link') return false
      return true
    },

    onMouseMove(event) {
      if (!this.interactive || !this.threeRaycaster || !this.threeCamera) return
      if (this.freeLookActive) return
      this._interactionAimPoint = { clientX: event.clientX, clientY: event.clientY }
      if (!this._grabbed) this.updateHoverFromClientPoint(this._interactionAimPoint)
    },

    updateHoverFromClientPoint(point) {
      if (!this.interactive || !this.threeRaycaster || !this.threeCamera || !point) return
      // Dragging is handled by document mousemove (tracks beyond canvas)
      if (this.isDragging) return

      const ndc = this.getMouseNDC(point)
      this.threeRaycaster.setFromCamera(ndc, this.threeCamera)

      // Collect all meshes for raycasting
      const showingBack = this.isShowingBack()
      const frontHandleTypes = new Set([
        'resize-handle', 'source-handle-move', 'source-handle-scale', 'source-handle-link',
        'turntable-handle',
      ])
      const objects = []
      this.threeScene.traverse((obj) => {
        if (this.isInteractiveRaycastMesh(obj, showingBack, frontHandleTypes)) {
          objects.push(obj)
        }
      })

      const intersects = this.threeRaycaster.intersectObjects(objects)

      if (this.hoveredObject) {
        this.onUnhover(this.hoveredObject)
      }

      if (intersects.length > 0) {
        // In flip/back view, front handles are excluded from raycasting entirely.
        const hit = (!showingBack
            && intersects.find(i => frontHandleTypes.has(i.object.userData?.type)))
          || intersects[0]
        this.hoveredObject = hit.object
        this.hoveredIntersectionPoint = hit.point?.clone?.() || null
        this.onHover(hit.object, point, hit)
        this.updateHandlesVisibility(hit.object)
      } else {
        this.tooltipVisible = false
        this.$refs.container.style.cursor = 'default'
        this.hoveredObject = null
        this.hoveredIntersectionPoint = null
        this.updateHandlesVisibility(null)
      }

      this.syncOrbitInteraction()
    },

    getViewportCenterAimPoint() {
      const rect = this.$refs.canvas?.getBoundingClientRect?.()
      if (!rect) return null
      return {
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
      }
    },

    getInteractionAimPoint() {
      if (this.freeLookActive) {
        return this.getViewportCenterAimPoint()
      }
      return this._interactionAimPoint
    },

    updateHoverFromAimPoint() {
      const point = this.getInteractionAimPoint()
      if (!point) return
      this.updateHoverFromClientPoint(point)
    },

    getTooltipAnchorPoint(point) {
      if (this.freeLookActive) {
        return this.getViewportCenterAimPoint() || point
      }
      return point
    },

    setTooltipPositionFromClientPoint(point) {
      const rect = this.$refs.container?.getBoundingClientRect?.()
      if (!rect || !point) return
      this.tooltipX = point.clientX - rect.left + 12
      this.tooltipY = point.clientY - rect.top - 8
    },

    onHover(mesh, event, intersection = null) {
      const data = mesh.userData
      if (!data) return

      this.setTooltipPositionFromClientPoint(this.getTooltipAnchorPoint(event))

      // Cursor
      if (this.turntablePhase !== 'idle') {
        this.$refs.container.style.cursor = this._turntableDrag ? 'grabbing' : 'grab'
      } else if (data.type === 'resize-handle') {
        this.$refs.container.style.cursor = this.getCursorForCorner(data.corner)
      } else if (data.type === 'turntable-handle') {
        this.$refs.container.style.cursor = 'grab'
      } else if (data.renderItemKey != null && this.wallDragEnabled) {
        this.$refs.container.style.cursor = 'grab'
      } else {
        this.$refs.container.style.cursor = 'default'
      }

      // Tooltip
      if (this.turntablePhase !== 'idle') {
        this.tooltipVisible = false
      } else if (data.type === 'resize-handle') {
        mesh.material.opacity = 0.95
        this.tooltipText = 'Drag to resize'
        this.tooltipVisible = true
      } else if (data.type === 'turntable-handle') {
        this.tooltipText = 'Drag to spin — click for a full turn'
        this.tooltipVisible = true
      } else if (data.type === 'artwork') {
        this.tooltipText = this.artworkDimTooltip(data)
        this.tooltipVisible = true
      } else if (data.type === 'frame-rail') {
        this.tooltipText = `Frame: ${data.widthInches}" wide`
        this.tooltipVisible = true
      } else if (data.type === 'linen-liner-band') {
        this.tooltipText = `Linen Liner: ${data.widthInches}" wide`
        this.tooltipVisible = true
      } else if (data.type === 'mat-piece') {
        const num = data.matNum != null && data.matNum !== '' ? data.matNum : null
        const hoverSide = this.resolveMatHoverSide(data, intersection?.point || this.hoveredIntersectionPoint)
        const side = hoverSide || data.side || data.label || 'border'
        const sideWidths = data.widthsInches || null
        const w = sideWidths && sideWidths[side] != null && !Number.isNaN(Number(sideWidths[side]))
          ? Number(sideWidths[side])
          : (data.widthInches != null && !Number.isNaN(Number(data.widthInches))
            ? Number(data.widthInches)
            : null)
        this.tooltipText = (num != null && w != null)
          ? `Mat ${num}: ${w}" ${side}`
          : (w != null ? `Mat: ${w}" ${side}` : `Mat ${side}`)
        this.tooltipVisible = true
      } else {
        this.tooltipVisible = false
      }
    },

    onUnhover() {
      // Handle visibility is managed by updateHandlesVisibility
    },

    /** Show/hide resize handles based on hover state. Handles visible only when hovering framed image. */
    updateHandlesVisibility(hoveredMesh) {
      if (!this.handlesGroup) return
      if (this._grabbed) return
      const showingBack = this.isShowingBack()
      const hoveredIsSelectedItem = !hoveredMesh || this.isSelectedRenderObject(hoveredMesh)
      const multi = (this.selectedItemIds?.length || 0) >= 2
      // Every handle edits the piece in its wall pose, so off the wall they are
      // all meaningless — except the one still being pulled, which is the thing
      // taking it off the wall.
      const peeling = this.turntablePhase === 'peeling'
      const onWall = this.turntablePhase === 'idle'
      const show = onWall && !multi && !showingBack && (this.isDragging || (hoveredMesh && hoveredIsSelectedItem && (
        hoveredMesh.userData?.type === 'artwork' ||
        hoveredMesh.userData?.type === 'frame-rail' ||
        hoveredMesh.userData?.type === 'linen-liner-band' ||
        hoveredMesh.userData?.type === 'mat-piece' ||
        hoveredMesh.userData?.type === 'resize-handle' ||
        hoveredMesh.userData?.type === 'source-handle-move' ||
        hoveredMesh.userData?.type === 'source-handle-scale' ||
        hoveredMesh.userData?.type === 'turntable-handle'
      )))
      const baseOpacity = show ? 0.6 : 0
      const isHandleType = (t) =>
        t === 'resize-handle'
        || t === 'source-handle-move'
        || t === 'source-handle-scale'
        || t === 'source-handle-link'
        || t === 'source-artwork-center-crosshair'
        || t === 'turntable-handle'
      const hoveredHandle = show && hoveredMesh && isHandleType(hoveredMesh.userData?.type) ? hoveredMesh : null
      const hoveredType = hoveredHandle?.userData?.type
      this.syncTurntableHandlePeek(hoveredType === 'turntable-handle')
      const hoveredCorner = hoveredHandle?.userData?.corner
      this.handlesGroup.traverse((obj) => {
        if ((obj.isMesh || obj.isLineSegments) && isHandleType(obj.userData?.type)) {
          if (obj.userData?.type === 'turntable-handle') {
            const aimed = peeling || hoveredType === 'turntable-handle'
            obj.material.opacity = aimed ? 0.95 : (show ? 0.75 : 0)
            return
          }
          if (obj.userData?.type === 'source-artwork-center-crosshair') {
            const offsetActive = Math.abs(this.parsedArtworkOffsetLeft) > 0.001 || Math.abs(this.parsedArtworkOffsetTop) > 0.001
            obj.material.opacity = show && offsetActive ? 0.8 : 0
            return
          }
          const isHovered = hoveredHandle && (
            obj === hoveredHandle ||
            (obj.userData?.type === hoveredType && obj.userData?.corner === hoveredCorner)
          )
          obj.material.opacity = isHovered ? 0.95 : baseOpacity
        }
      })

    },

    getCursorForCorner(corner) {
      const cursors = {
        'top-left': 'nwse-resize',
        'top-right': 'nesw-resize',
        'bottom-left': 'nesw-resize',
        'bottom-right': 'nwse-resize',
      }
      return cursors[corner] || 'pointer'
    },

    onMouseDown(event) {
      if (event.button !== 0) return
      if (!this.interactive) return
      if (this.turntablePhase !== 'idle') {
        // The turntable owns the pointer while it is up: OrbitControls takes
        // the drags, and all that is left to decide is whether this press was
        // a click somewhere other than the piece.
        this._beginTurntablePress(event)
        return
      }
      if (!this.hoveredObject) {
        this._beginLasso(event)
        return
      }
      const data = this.hoveredObject.userData || {}

      if (data.type === 'turntable-handle') {
        this._beginTurntablePeel(event)
        return
      }

      if (data.type === 'resize-handle') {
        this._beginDrag(event, 'resize', this.getCursorForCorner(data.corner))
        this.dragHandle = data.corner
        this.dragStartDims = { width: this.parsedArtWidth, height: this.parsedArtHeight }
        this.$emit('resize-drag-start', this.parsedArtWidth / this.parsedArtHeight)
        return
      }

      if (data.type === 'source-handle-move' || data.type === 'source-handle-scale') {
        const mode = data.type === 'source-handle-move' ? 'source-move' : 'source-scale'
        this._beginDrag(event, mode, mode === 'source-move' ? 'move' : 'ew-resize')
        this._showSourceGhost()
        return
      }

      if (data.renderItemId != null) {
        if (this.wallDragEnabled) {
          const instance = this._renderItemInstances?.get(String(data.renderItemKey))
          this._beginItemGrab(instance, data, event)
        } else {
          event?.preventDefault?.()
          const modifiers = {
            ctrlKey: Boolean(event?.ctrlKey),
            metaKey: Boolean(event?.metaKey),
            shiftKey: Boolean(event?.shiftKey),
          }
          const hasModifier = modifiers.ctrlKey || modifiers.metaKey || modifiers.shiftKey
          // Modifier clicks are selection gestures — never scroll-snap/navigate the camera
          if (!hasModifier && this.shouldScrollSnapToRenderItem({ itemId: data.renderItemId })) {
            this.requestScrollSnapToRenderItem({
              itemId: data.renderItemId,
              key: data.renderItemKey,
              behavior: 'smooth',
            })
            return
          }
          this.$emit('select-render-item', {
            itemId: data.renderItemId,
            key: data.renderItemKey,
            ...modifiers,
          })
        }
        return
      }

      // Mousedown on a non-item scene surface (wall/room): rubber-band selection
      this._beginLasso(event)
    },

    // --- Rubber-band (lasso) selection — mirrors the projects2 board ---
    //
    // One world-space implementation for both view modes: the lasso is a
    // rectangle on the wall plane (z=0) between an anchored point and the
    // pointer's current wall point. Only the pointer ray differs — the mouse
    // cursor normally, the crosshair (viewport center) under pointer lock.
    // Geometry is frame-driven so camera motion mid-gesture (free-look
    // sweeps, WASD, scroll-snap) keeps the rect and hits correct; mouse
    // events only refresh the pointer position and modifier state.

    _beginLasso(event) {
      if (this.isDragging || this._grabbed) return
      if ((this.renderItems?.length || 0) < 2) return
      event?.preventDefault?.()
      this._lassoPointer = this.freeLookActive
        ? null
        : { clientX: event.clientX, clientY: event.clientY }
      this._lassoPointerStart = this._lassoPointer ? { ...this._lassoPointer } : null
      const anchor = this._lassoWallPoint()
      if (!anchor) {
        this._lassoPointer = null
        this._lassoPointerStart = null
        return
      }
      const containerRect = this.$refs.container?.getBoundingClientRect?.()
      this.lassoOrigin = containerRect
        ? { x: containerRect.left, y: containerRect.top }
        : { x: 0, y: 0 }
      this._lassoWorldStart = anchor
      this._lassoItemWorldRects = this._collectItemWorldRects()
      // Base selection for additive (modifier-held) rubber-banding. Outside
      // multi-select, the single active item is the implicit selection and
      // must carry forward into the lasso the same way it does for ctrl/shift
      // click (see onSelectRenderItem's `base` seeding).
      this._lassoBase = ((this.selectedItemIds?.length || 0) >= 2
        ? this.selectedItemIds
        : (this.selectedItemId != null ? [this.selectedItemId] : [])
      ).slice()
      this._lassoAdditive = Boolean(event?.metaKey || event?.ctrlKey || event?.shiftKey)
      this._lassoLastEmit = null
      this._lassoLastIds = []
      this._lassoMove = (e) => this._onLassoMove(e)
      this._lassoUp = () => this._endLasso()
      document.addEventListener('mousemove', this._lassoMove)
      document.addEventListener('mouseup', this._lassoUp)
    },

    // Wall-plane (z=0) point under the pointer ray: the crosshair when
    // pointer-locked, else the mouse cursor.
    _lassoWallPoint() {
      if (!this.threeCamera || !this.threeRaycaster) return null
      let ndc = { x: 0, y: 0 }
      if (!this.freeLookActive) {
        if (!this._lassoPointer) return null
        ndc = this.getMouseNDC(this._lassoPointer)
      }
      this.threeRaycaster.setFromCamera(ndc, this.threeCamera)
      const { origin, direction } = this.threeRaycaster.ray
      if (direction.z >= 0) return null
      const t = -origin.z / direction.z
      return { x: origin.x + direction.x * t, y: origin.y + direction.y * t }
    },

    // Wall-plane footprints of every render item, in world units
    _collectItemWorldRects() {
      const rects = []
      this.getRenderSpecs().forEach((spec) => {
        if (spec?.itemId == null) return
        const instance = this._renderItemInstances?.get(String(spec.key))
        if (!instance?.group) return
        const half = this._collisionHalfExtent(instance, spec)
        const p = instance.group.position
        rects.push({
          itemId: spec.itemId,
          minX: p.x - half.halfW,
          maxX: p.x + half.halfW,
          minY: p.y - half.halfH,
          maxY: p.y + half.halfH,
        })
      })
      return rects
    },

    _projectWallPointToClient(x, y) {
      const canvasRect = this.$refs.canvas?.getBoundingClientRect?.()
      if (!canvasRect || !this.threeCamera) return null
      const v = new THREE.Vector3(x, y, 0)
      v.project(this.threeCamera)
      return {
        x: canvasRect.left + ((v.x + 1) / 2) * canvasRect.width,
        y: canvasRect.top + ((1 - v.y) / 2) * canvasRect.height,
      }
    },

    // Runs every render-loop frame while a lasso is armed (both view modes):
    // rect spans the anchored wall point and the pointer's current wall
    // point; the on-screen rect is its projection.
    _updateLasso() {
      if (!this._lassoWorldStart) return
      const current = this._lassoWallPoint()
      if (!current) return
      const start = this._lassoWorldStart
      if (!this.lassoRect && !this._lassoThresholdReached(current)) return

      const world = {
        minX: Math.min(start.x, current.x),
        maxX: Math.max(start.x, current.x),
        minY: Math.min(start.y, current.y),
        maxY: Math.max(start.y, current.y),
      }
      const a = this._projectWallPointToClient(start.x, start.y)
      const b = this._projectWallPointToClient(current.x, current.y)
      if (a && b) {
        this.lassoRect = {
          x: Math.min(a.x, b.x),
          y: Math.min(a.y, b.y),
          w: Math.abs(b.x - a.x),
          h: Math.abs(b.y - a.y),
        }
      }
      const hits = (this._lassoItemWorldRects || [])
        .filter(r => r.minX < world.maxX && r.maxX > world.minX && r.minY < world.maxY && r.maxY > world.minY)
        .map(r => r.itemId)
      this._emitLassoSelection(hits)
    },

    // Drag-vs-click threshold: 5px of cursor travel with a mouse, 1 wall-inch
    // of crosshair travel under pointer lock (there is no cursor to measure).
    _lassoThresholdReached(current) {
      if (this.freeLookActive) {
        const start = this._lassoWorldStart
        return Math.hypot(current.x - start.x, current.y - start.y) >= 1
      }
      const from = this._lassoPointerStart
      const to = this._lassoPointer
      return Boolean(from && to && Math.hypot(to.clientX - from.clientX, to.clientY - from.clientY) >= 5)
    },

    _emitLassoSelection(hits) {
      let ids = hits
      if (this._lassoAdditive && this._lassoBase.length) {
        const seen = new Set(this._lassoBase.map(String))
        ids = [...this._lassoBase, ...hits.filter(id => !seen.has(String(id)))]
      }
      // Live selection: emit on every change so outlines track the rect
      const key = ids.map(String).join(',')
      if (key !== this._lassoLastEmit) {
        this._lassoLastEmit = key
        this._lassoLastIds = ids
        this.$emit('selection-change', { ids })
      }
    },

    // Geometry is frame-driven; mouse events only refresh pointer + modifiers
    _onLassoMove(e) {
      this._lassoAdditive = Boolean(e.metaKey || e.ctrlKey || e.shiftKey)
      if (!this.freeLookActive) {
        this._lassoPointer = { clientX: e.clientX, clientY: e.clientY }
      }
    },

    _endLasso() {
      document.removeEventListener('mousemove', this._lassoMove)
      document.removeEventListener('mouseup', this._lassoUp)
      this._lassoMove = null
      this._lassoUp = null
      const wasActive = !!this.lassoRect
      const ids = wasActive ? this._lassoLastIds : []
      this._lassoWorldStart = null
      this._lassoPointer = null
      this._lassoPointerStart = null
      this.lassoRect = null
      this.lassoOrigin = null
      this._lassoBase = []
      this._lassoAdditive = false
      this._lassoItemWorldRects = null
      this._lassoLastEmit = null
      this._lassoLastIds = []
      // Final emit lets the parent normalize (collapse 0/1); a plain click on
      // empty space clears the selection, matching the projects grid.
      this.$emit('selection-change', { ids, final: true })
    },

    onMouseUp() {
      // The turntable's own press ends on a document listener, so that a
      // release outside the canvas still resolves the click.
      if (this.turntablePhase !== 'idle') return
      this._dropGrab()
      this.endResizeDrag()
    },

    endResizeDrag() {
      if (!this.isDragging) return
      const endedResizeDrag = this.dragMode === 'resize'
      const endedSourceDrag = this.dragMode === 'source-move' || this.dragMode === 'source-scale'
      if (endedResizeDrag) this.tooltipVisible = false

      document.removeEventListener('mousemove', this.boundHandleDrag)
      document.removeEventListener('mouseup', this.boundEndResizeDrag)
      this._shiftLockedAspect = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      this.isDragging = false
      this.dragMode = null
      this.dragHandle = null
      this.dragStartPos = null
      this.dragStartDims = null
      this.lastDragEvent = null
      this._dragGrabAimPoint = null
      if (endedResizeDrag && this.resizeSnapAnimation) {
        this.dragWidth = this.resizeSnapAnimation.toWidth
        this.dragHeight = this.resizeSnapAnimation.toHeight
        this.applyResizeVisual(this.dragWidth, this.dragHeight)
      }
      this.resizeSnapAnimation = null
      this.activeResizeSnap = null
      // Flush the final queued drag frame before clearing constraint UI.
      // Otherwise commitResizeDims can emit one last constraint state after
      // cleanup and leave its banner visible until another interaction.
      if (this.dragRafId != null) {
        cancelAnimationFrame(this.dragRafId)
        this.applyPendingDragDims()
      }
      if (this.sourceDragRafId != null) {
        cancelAnimationFrame(this.sourceDragRafId)
        this.applyPendingSourceTransform()
      }
      if (endedResizeDrag) {
        this.setActiveResizeConstraint(null)
        this.clearAllResizeConstraintMessages()
      }
      this.dragWidth = null
      this.dragHeight = null
      if (endedResizeDrag && this.collisionEnabled) {
        const positions = this._collectMovedRenderItemPositions()
        if (positions.length) this.$emit('resize-collision-positions', { positions })
      }
      this._hideSourceGhost()
      this.dragSourceStart = null
      this.lastEmittedDims = null
      this.syncOrbitInteraction()
      if (endedResizeDrag || endedSourceDrag) {
        if (endedResizeDrag) this.$emit('resize-drag-state', false)
        if (endedSourceDrag) this.$emit('source-transform-drag-state', false)
      }
      // The live geometry already shows the final gesture. The emitted values
      // update the owning props, whose watchers perform the one authoritative
      // reconciliation; scheduling another sync and rebuild here made release
      // traverse large project scenes two extra times.
    },

    onMouseLeave() {
      this._interactionAimPoint = null
      if (this.freeLookActive) return
      if (!this.isDragging || this.dragMode !== 'resize') this.tooltipVisible = false
      this.hoveredIntersectionPoint = null
      if (this.hoveredObject) {
        this.onUnhover(this.hoveredObject)
        this.hoveredObject = null
      }
      this.updateHandlesVisibility(null)
      this.syncOrbitInteraction()
      if (!this.isDragging) this.$refs.container.style.cursor = 'default'
    },

    handleDrag(event) {
      if (!this.dragStartPos) return
      this.lastDragEvent = event
      if (this.freeLookActive) return // render loop handles free-look drag via _updateFreeLookDrag
      if (!this._dragGrabAimPoint || !this.threeRaycaster || !this.threeCamera) return

      const ndc = this.getMouseNDC({ clientX: event.clientX, clientY: event.clientY })
      this.threeRaycaster.setFromCamera(ndc, this.threeCamera)
      const { origin, direction } = this.threeRaycaster.ray
      if (direction.z >= 0) return
      const t = -origin.z / direction.z
      const dxInches = (origin.x + direction.x * t) - this._dragGrabAimPoint.x
      // World Y is up; _applyDragDelta expects screen-down convention, so negate
      const dyInches = -((origin.y + direction.y * t) - this._dragGrabAimPoint.y)
      this._applyDragDelta(dxInches, dyInches, event)
    },

    _updateFreeLookDrag() {
      if (!this.isDragging || !this.freeLookActive || !this.threeCamera || !this._dragGrabAimPoint) return
      const aimPoint = this.getViewportCenterAimPoint()
      if (!aimPoint) return
      const ndc = this.getMouseNDC(aimPoint)
      this.threeRaycaster.setFromCamera(ndc, this.threeCamera)
      const { origin, direction } = this.threeRaycaster.ray
      if (direction.z >= 0) return
      const t = -origin.z / direction.z
      const dxInches = (origin.x + direction.x * t) - this._dragGrabAimPoint.x
      // World Y is up; _applyDragDelta expects screen-down convention, so negate
      const dyInches = -((origin.y + direction.y * t) - this._dragGrabAimPoint.y)
      this._applyDragDelta(dxInches, dyInches, null)
    },

    _applyDragDelta(dxInches, dyInches, event) {
      if (this.dragMode === 'resize') {
        if (!this.dragStartDims) return

        let newWidth = this.dragStartDims.width
        let newHeight = this.dragStartDims.height

        const corner = this.dragHandle
        const resizeFromCenterFactor = 2

        if (corner === 'top-right' || corner === 'bottom-right') {
          newWidth += dxInches * resizeFromCenterFactor
        } else {
          newWidth -= dxInches * resizeFromCenterFactor
        }
        if (corner === 'bottom-left' || corner === 'bottom-right') {
          newHeight += dyInches * resizeFromCenterFactor
        } else {
          newHeight -= dyInches * resizeFromCenterFactor
        }

        const isAspectMode = this.resizeMode === RESIZE_MODE_ASPECT || this.resizeMode === RESIZE_MODE_ASPECT_FREE
        if (isAspectMode) {
          if (this._shiftLockedAspect == null) {
            const w = this.dragWidth ?? this.dragStartDims?.width
            const h = this.dragHeight ?? this.dragStartDims?.height
            if (w > 0 && h > 0) this._shiftLockedAspect = w / h
          }
        } else {
          this._shiftLockedAspect = null
        }
        const effectiveAspect = this._shiftLockedAspect ?? this.resizeAspectRatio
        const usesSnap = this.resizeMode === RESIZE_MODE_SNAP || this.resizeMode === RESIZE_MODE_ASPECT
        let snapCheckWidth = newWidth
        let snapCheckHeight = newHeight
        if (this.resizeMode === RESIZE_MODE_ASPECT) {
          const constrained = resolveResizeDimensions({
            width: newWidth,
            height: newHeight,
            mode: RESIZE_MODE_ASPECT,
            aspectRatio: effectiveAspect,
          })
          snapCheckWidth = constrained.width
          snapCheckHeight = constrained.height
        }
        const snapTarget = usesSnap ? this.getResizeSnapTarget(snapCheckWidth, snapCheckHeight) : null

        let resolved
        if (snapTarget) {
          resolved = snapTarget
        } else {
          const freeMode = (this.resizeMode === RESIZE_MODE_ASPECT || this.resizeMode === RESIZE_MODE_ASPECT_FREE)
            ? RESIZE_MODE_ASPECT
            : RESIZE_MODE_FREE
          resolved = resolveResizeDimensions({
            width: newWidth,
            height: newHeight,
            mode: freeMode,
            aspectRatio: effectiveAspect,
            snapCandidates: this.resizeSnapCandidates,
          })
        }
        const clampResult = clampResizeDimensionsToConstraints({
          width: resolved.width,
          height: resolved.height,
          constraints: this.resizeConstraints,
          preserveAspectRatio: isAspectMode,
        })
        this.pendingResizeIntent = {
          intentWidth: resolved.width,
          intentHeight: resolved.height,
          clamped: clampResult.clamped,
          constraint: clampResult.constraint,
        }
        newWidth = clampResult.width
        newHeight = clampResult.height
        this.refreshSizeSnapOverlayVisibility()

        const tooltipPoint = event ? this.getTooltipAnchorPoint(event) : this.getViewportCenterAimPoint()
        this.setTooltipPositionFromClientPoint(tooltipPoint)
        this.tooltipText = this._resizeDimTooltip(newWidth, newHeight)
        this.tooltipVisible = true

        if (this.dimGroup) this.dimGroup.visible = false

        if (snapTarget) {
          this.startResizeSnapAnimation(newWidth, newHeight)
        } else {
          this.dragWidth = newWidth
          this.dragHeight = newHeight
          this.pendingDragDims = { width: newWidth, height: newHeight }
          if (this.dragRafId == null) {
            this.dragRafId = requestAnimationFrame(() => this.applyPendingDragDims())
          }
        }
      } else if (this.dragMode === 'source-move') {
        this.handleSourceMoveDrag(dxInches, dyInches)
      } else if (this.dragMode === 'source-scale') {
        this.handleSourceScaleDrag(dxInches)
      }
    },

    applyPendingDragDims() {
      this.dragRafId = null
      const d = this.pendingDragDims
      this.pendingDragDims = null
      if (!d) return

      this.commitResizeDims(d.width, d.height)
    },

    // ------------------------------------------------------------------
    // Resize handling
    // ------------------------------------------------------------------

    onResize() {
      this.markNeedsRender()  // viewport changed
      if (this.freeLookActive) return
      // Under orbit the camera belongs to the user once the item has loaded, so
      // a resize re-fits the projection and leaves the viewpoint where it is.
      // Before the first fit there is no viewpoint to protect, and the normal
      // path below is what establishes one.
      if (this.orbitEnabled && this.hasInitialFit) {
        this.updateCameraViewport()
        return
      }
      // Resizing reflows the overlay, and the browser fires `scroll` when it
      // clamps the offset to the new extent. That is this resize's own doing,
      // not a gesture — acted on, it cancels the fit a threshold crossing just
      // started and snaps the camera to wherever the overlay happens to sit.
      // Claim it here rather than in the layout refresh below, which is skipped
      // for the rest of a drag once that fit is animating.
      // A selection scroll in flight is aimed at snap positions this resize has
      // just moved, and the settle that would end it is about to be dropped
      // along with the reflow scrolls below — leaving a latch set that disables
      // auto framing entirely, with no timer left to clear it. End the handoff
      // here instead: after a viewport change the camera belongs to auto
      // framing again, not to a scroll measured against the old layout.
      if (this._scrollSnapSelectionFitPending) this.finishScrollSnapSelectionHandoff()
      this.suppressScrollSnapOverlayScroll()
      if (this.cameraAnimRafId != null) {
        // Both the fit below and auto framing sit out a camera animation, so a
        // drag that crosses a second threshold while one is in flight would
        // land on a mode the canvas has already outgrown. Note that the
        // viewport moved and re-check when the animation lands. Only a resize
        // sets this, so choosing a mode by hand is never overridden.
        this._autoFramingRecheckPending = true
      }
      if (this.cameraAnimRafId == null) this.fitCamera()
      this.applyAutoFramingForCurrentScene({ instant: true })
      this.$nextTick(() => this.refreshScrollSnapOverlayLayout())
    },

    // ------------------------------------------------------------------
    // Free-look controls
    // ------------------------------------------------------------------

    enableFreeLook() {
      // Walking around the room and turning one piece in place are two answers
      // to the same question; entering one leaves the other.
      this.exitTurntable({ instant: true })
      if (!this._pointerControls || this.freeLookActive) return
      this.freeLookActive = true
      this.syncOrbitInteraction()
      this._freeLookKeys.clear()
      this._freeLookFloorY = this.threeCamera.position.y
      this._freeLookVelocity = { forward: 0, right: 0, vertical: 0 }
      this._freeLookOnGround = true
      this._freeLookBobTime = 0
      this._freeLookBobAmplitude = 0
      this._freeLookJumpConsumed = false
      this._freeLookLandDip = 0
      this._freeLookLandDipVel = 0
      this._freeLookPhysicsY = this._freeLookFloorY
      this._freeLookCrouchOffset = 0
      this._initFreeLookAudio()
      document.addEventListener('keydown', this._boundFreeLookKeyDown)
      document.addEventListener('keyup', this._boundFreeLookKeyUp)
      this._pointerControls.addEventListener('unlock', this._boundPointerUnlock)
      this._pointerControls.lock()
      this.$emit('enter-free-look')
    },

    _beginItemGrab(instance, data, event) {
      if (this.debouncedRebuild?.cancel) this.debouncedRebuild.cancel()
      let grabOffset = { x: 0, y: 0 }
      const aimPoint = this.freeLookActive
        ? this.getViewportCenterAimPoint()
        : { clientX: event.clientX, clientY: event.clientY }
      if (instance?.group && aimPoint && this.threeCamera) {
        const ndc = this.getMouseNDC(aimPoint)
        this.threeRaycaster.setFromCamera(ndc, this.threeCamera)
        const { origin, direction } = this.threeRaycaster.ray
        if (direction.z < 0) {
          const t = -origin.z / direction.z
          grabOffset = {
            x: instance.group.position.x - (origin.x + direction.x * t),
            y: instance.group.position.y - (origin.y + direction.y * t),
          }
        }
      }
      this.updateHandlesVisibility(null)
      // Dragging a multi-selected item moves the whole selection: the other
      // selected items ride along as rigid companions (excluded from collision
      // pushing, pinned in the live solver).
      const companions = this._collectGrabCompanions(data.renderItemId)
      this._grabbed = {
        key: String(data.renderItemKey),
        itemId: data.renderItemId,
        grabOffset,
        companions,
        modifiers: {
          ctrlKey: Boolean(event?.ctrlKey),
          metaKey: Boolean(event?.metaKey),
          shiftKey: Boolean(event?.shiftKey),
        },
        startPos: instance?.group
          ? { x: instance.group.position.x, y: instance.group.position.y }
          : null,
        collision: this.collisionEnabled
          ? this._snapshotCollisionNeighbors(String(data.renderItemKey), companions.map(c => c.key))
          : null,
      }
      this.syncOrbitInteraction()
      this.activeWallSnap = { x: false, y: false }
      this.wallSnapOverlayOpacity = 0
      this.wallSnapOverlayTargetOpacity = 1
      this.rebuildWallSnapOverlay()
      document.addEventListener('mouseup', this._boundDropGrab, { once: true })
      document.body.style.cursor = 'grabbing'
      document.body.style.userSelect = 'none'
    },

    // Half footprint for an item. Prefers the live finished size recorded on the instance during
    // a resize (builtDims/specs lag), then the resolved spec's finished size.
    _collisionHalfExtent(instance, spec) {
      const live = instance?.liveFootprint
      if (live && live.w > 0 && live.h > 0) return { halfW: live.w / 2, halfH: live.h / 2 }
      const props = spec?.rendererProps || {}
      const w = Number(props.targetWidth) || Number(props.artWidth) || 16
      const h = Number(props.targetHeight) || Number(props.artHeight) || 16
      return { halfW: w / 2, halfH: h / 2 }
    },
    // The other multi-selected items when a grab starts on a selected item —
    // they move rigidly with the drag. Empty for single drags.
    _collectGrabCompanions(grabbedItemId) {
      const multiIds = (this.selectedItemIds?.length || 0) >= 2
        ? this.selectedItemIds.map(String)
        : []
      if (!multiIds.includes(String(grabbedItemId))) return []
      const companions = []
      this.getRenderSpecs().forEach(spec => {
        if (spec?.itemId == null) return
        if (String(spec.itemId) === String(grabbedItemId)) return
        if (!multiIds.includes(String(spec.itemId))) return
        const instance = this._renderItemInstances?.get(String(spec.key))
        if (!instance?.group) return
        const half = this._collisionHalfExtent(instance, spec)
        companions.push({
          key: String(spec.key),
          itemId: spec.itemId,
          instance,
          startPos: { x: instance.group.position.x, y: instance.group.position.y },
          halfW: half.halfW,
          halfH: half.halfH,
        })
      })
      return companions
    },
    // Capture each non-dragged item's rest position (anchor) and footprint at grab start so the
    // live solver can push them aside and let them gravitate home as the dragged item moves away.
    // `excludedKeys` (drag companions) move with the drag and must not be treated as pushable.
    _snapshotCollisionNeighbors(draggedKey, excludedKeys = []) {
      if (!this._renderItemInstances) return null
      const excluded = new Set(excludedKeys.map(String))
      const specByKey = new Map(this.getRenderSpecs().map(spec => [String(spec.key), spec]))
      const draggedInstance = this._renderItemInstances.get(String(draggedKey))
      const dragged = this._collisionHalfExtent(draggedInstance, specByKey.get(String(draggedKey)))
      const neighbors = []
      this._renderItemInstances.forEach((instance, key) => {
        if (String(key) === String(draggedKey) || excluded.has(String(key)) || !instance?.group) return
        const half = this._collisionHalfExtent(instance, specByKey.get(String(key)))
        neighbors.push({
          instance,
          halfW: half.halfW,
          halfH: half.halfH,
          anchorX: instance.group.position.x,
          anchorY: instance.group.position.y,
        })
      })
      return { dragged, neighbors }
    },
    // Compute the full expected layout for this frame: seed every neighbor from its rest position
    // (anchor) and settle with the pinned item held at (x, y). Re-seeding from anchors each frame
    // (rather than nudging current positions) is what makes the live preview match the committed
    // result — items slot to their minimal-displacement spots instead of riding along the drag.
    _solveLiveCollision(snap, x, y, halfW, halfH, extraPinned = []) {
      if (!snap || !snap.neighbors.length) return
      const boxes = [
        { x, y, halfW, halfH, pinned: true, anchorX: x, anchorY: y },
        ...extraPinned.map(p => ({
          x: p.x, y: p.y, halfW: p.halfW, halfH: p.halfH,
          pinned: true, anchorX: p.x, anchorY: p.y,
        })),
      ]
      const neighborOffset = boxes.length
      snap.neighbors.forEach((n) => {
        boxes.push({
          x: n.anchorX,
          y: n.anchorY,
          halfW: n.halfW,
          halfH: n.halfH,
          pinned: false,
          anchorX: n.anchorX,
          anchorY: n.anchorY,
        })
      })
      resolveWallCollisions(boxes) // settle (same as commit): full expected target positions
      // Ease the on-screen positions toward the (drift-free) target rather than snapping, so
      // neighbors glide and arc smoothly as the dragged item passes through them.
      snap.neighbors.forEach((n, i) => {
        const g = n.instance.group.position
        g.x += (boxes[neighborOffset + i].x - g.x) * WALL_COLLISION_DISPLAY_EASE
        g.y += (boxes[neighborOffset + i].y - g.y) * WALL_COLLISION_DISPLAY_EASE
      })
    },
    // Push neighbors out of the dragged item's way (and spring them back) for the current frame.
    // Drag companions (multi-selection) ride along pinned so they shove neighbors too.
    _applyLiveCollision(x, y) {
      const snap = this._grabbed?.collision
      if (!snap) return
      const extraPinned = (this._grabbed.companions || [])
        .filter(c => c.instance?.group)
        .map(c => ({
          x: c.instance.group.position.x,
          y: c.instance.group.position.y,
          halfW: c.halfW,
          halfH: c.halfH,
        }))
      this._solveLiveCollision(snap, x, y, snap.dragged.halfW, snap.dragged.halfH, extraPinned)
    },
    // Push neighbors away from the item being resized. Static scene resolution preserves saved
    // positions; resize preview opts into collision solving for the current edit gesture only.
    // Unlike a move, the resize target moves smoothly with the growing edge, so we track it
    // directly (no easing); easing here would just make neighbors lag behind the edge.
    _applyResizeCollision() {
      if (!this._renderItemInstances) return
      const selectedKey = String(this.getSelectedRenderSpec()?.key ?? '')
      const specs = this.getRenderSpecs({
        collisionEnabled: true,
        pinnedItemId: this.selectedItemId,
      })
      const layout = this.getDynamicWallLayout(specs)
      specs.forEach((spec) => {
        const key = String(spec.key)
        if (key === selectedKey) return
        const inst = this._renderItemInstances.get(key)
        if (!inst?.group) return
        const target = this.normalizeRenderSpec(spec, layout).position
        inst.group.position.x = target.x
        inst.group.position.y = target.y
      })
    },
    _collectMovedRenderItemPositions() {
      if (!this._renderItemInstances) return []
      const selectedKey = String(this.getSelectedRenderSpec()?.key ?? '')
      const layout = this._dynamicWallLayout || this.getDynamicWallLayout()
      const wallLeftX = Number.isFinite(Number(layout?.wallLeftX))
        ? Number(layout.wallLeftX)
        : ((Number(layout?.wallCenterX) || 0) - ((Number(layout?.wallWidth) || WALL_WIDTH) / 2))
      const positions = []
      this._renderItemInstances.forEach((instance, key) => {
        const itemId = instance?.itemId ?? instance?.group?.userData?.itemId
        if (String(key) === selectedKey || !instance?.group || itemId == null) return
        const start = instance.basePosition
        const x = instance.group.position.x
        const y = instance.group.position.y
        if (start && Math.hypot(x - start.x, y - start.y) <= 0.01) return
        positions.push({ itemId, x: x - wallLeftX, y })
      })
      return positions
    },
    // Snap pushed neighbors back to their rest positions (used when a drag ends without a move).
    _restoreCollisionNeighbors(snap) {
      if (!snap) return
      snap.neighbors.forEach((n) => {
        if (!n.instance?.group) return
        n.instance.group.position.x = n.anchorX
        n.instance.group.position.y = n.anchorY
      })
    },
    _updateGrabbedItem() {
      if (!this._grabbed || !this.threeCamera) return
      const instance = this._renderItemInstances?.get(this._grabbed.key)
      if (!instance?.group) return
      const aimPoint = this.freeLookActive
        ? this.getViewportCenterAimPoint()
        : this._interactionAimPoint
      if (!aimPoint) return
      const ndc = this.getMouseNDC(aimPoint)
      this.threeRaycaster.setFromCamera(ndc, this.threeCamera)
      const { origin, direction } = this.threeRaycaster.ray
      if (direction.z >= 0) return
      const t = -origin.z / direction.z
      const offset = this._grabbed.grabOffset
      let nextX = origin.x + direction.x * t + offset.x
      let nextY = origin.y + direction.y * t + offset.y
      const wallCenterX = this.getWallCenterX()
      const snapX = Math.abs(nextX - wallCenterX) <= WALL_DRAG_SNAP_THRESHOLD_INCHES
      const snapY = Math.abs(nextY - EYE_LEVEL) <= WALL_DRAG_SNAP_THRESHOLD_INCHES
      if (snapX) nextX = wallCenterX
      if (snapY) nextY = EYE_LEVEL
      this.activeWallSnap = { x: snapX, y: snapY }
      instance.group.position.x = nextX
      instance.group.position.y = nextY

      // Companions (multi-selection) follow rigidly with the drag delta
      const startPos = this._grabbed.startPos
      const companions = this._grabbed.companions || []
      if (startPos && companions.length) {
        const dx = nextX - startPos.x
        const dy = nextY - startPos.y
        companions.forEach((c) => {
          if (!c.instance?.group) return
          c.instance.group.position.x = c.startPos.x + dx
          c.instance.group.position.y = c.startPos.y + dy
        })
      }

      if (this.collisionEnabled && this._grabbed.collision) {
        this._applyLiveCollision(nextX, nextY)
      }
      this._syncSelectionHighlightPositions()

      const xOffset = instance.group.position.x - wallCenterX
      const yOffset = instance.group.position.y - EYE_LEVEL
      const xAbs = Math.round(Math.abs(xOffset))
      const yAbs = Math.round(Math.abs(yOffset))
      const xLabel = Math.abs(xOffset) < 1 ? 'center' : `${xAbs}" ${xOffset > 0 ? 'R' : 'L'}`
      const yLabel = Math.abs(yOffset) < 1 ? 'eye level' : `${yAbs}" ${yOffset > 0 ? 'up' : 'down'}`
      this.tooltipText = `${xLabel}, ${yLabel}`
      this.tooltipVisible = true
      this.setTooltipPositionFromClientPoint(aimPoint)
    },

    _dropGrab() {
      if (!this._grabbed) return
      const { key, itemId, startPos, collision, modifiers, companions = [] } = this._grabbed
      this._grabbed = null
      this.syncOrbitInteraction()
      this.wallSnapOverlayTargetOpacity = 0
      this.activeWallSnap = { x: false, y: false }
      this.tooltipVisible = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      const instance = this._renderItemInstances?.get(key)
      if (!instance?.group) {
        this._restoreCollisionNeighbors(collision)
        this.debouncedRebuild()
        return
      }
      const dx = instance.group.position.x - (startPos?.x ?? instance.group.position.x)
      const dy = instance.group.position.y - (startPos?.y ?? instance.group.position.y)
      if (Math.sqrt(dx * dx + dy * dy) > 1) {
        // Moved: the parent re-resolves and re-syncs every item's position (collision-free), so
        // the pushed neighbors persist — no need to restore them here.
        const layout = this._dynamicWallLayout || this.getDynamicWallLayout()
        const wallLeftX = Number.isFinite(Number(layout?.wallLeftX))
          ? Number(layout.wallLeftX)
          : ((Number(layout?.wallCenterX) || 0) - ((Number(layout?.wallWidth) || WALL_WIDTH) / 2))
        const items = [
          {
            itemId,
            x: instance.group.position.x - wallLeftX,
            y: instance.group.position.y,
          },
          ...companions
            .filter(c => c.instance?.group)
            .map(c => ({
              itemId: c.itemId,
              x: c.instance.group.position.x - wallLeftX,
              y: c.instance.group.position.y,
            })),
        ]
        this.$emit('move-render-item', { itemId, key, x: items[0].x, y: items[0].y, items })
      } else {
        if (startPos) {
          instance.group.position.x = startPos.x
          instance.group.position.y = startPos.y
        }
        companions.forEach((c) => {
          if (!c.instance?.group) return
          c.instance.group.position.x = c.startPos.x
          c.instance.group.position.y = c.startPos.y
        })
        this._syncSelectionHighlightPositions()
        // No move committed: spring any nudged neighbors back to their rest positions.
        this._restoreCollisionNeighbors(collision)
        const hasModifier = modifiers?.ctrlKey || modifiers?.metaKey || modifiers?.shiftKey
        if (!hasModifier && this.shouldScrollSnapToRenderItem({ itemId })) {
          this.requestScrollSnapToRenderItem({ itemId, key, behavior: 'smooth' })
          return
        }
        this.$emit('select-render-item', { itemId, key, ...(modifiers || {}) })
        this.debouncedRebuild()
      }
    },

    disableFreeLook() {
      if (!this.freeLookActive) return
      if (this._grabbed) this._dropGrab()
      if (this._lassoWorldStart) this._endLasso()
      this.freeLookActive = false
      this._freeLookKeys.clear()
      document.removeEventListener('keydown', this._boundFreeLookKeyDown)
      document.removeEventListener('keyup', this._boundFreeLookKeyUp)
      if (this._pointerControls) {
        this._pointerControls.removeEventListener('unlock', this._boundPointerUnlock)
        this._pointerControls.unlock()
      }
      // Reset roll applied by head bob
      if (this.threeCamera) {
        const e = new THREE.Euler().setFromQuaternion(this.threeCamera.quaternion, 'YXZ')
        e.z = 0
        this.threeCamera.quaternion.setFromEuler(e)
      }
      // Free-look walked the camera somewhere arbitrary. Orbit picks up from
      // wherever it was left rather than snapping back, so its pivot has to
      // move with it — the mode change that follows is what reframes.
      this.syncOrbitControlsToCamera()
      this.syncOrbitInteraction()
      this.$emit('exit-free-look')
    },

    _onFreeLookKeyDown(event) {
      if (event.code === 'Space') {
        event.preventDefault()
        this._freeLookKeys.add(' ')
        return
      }
      if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
        this._freeLookKeys.add('shift')
        return
      }
      const key = event.key.toLowerCase()
      if (!['w', 'a', 's', 'd'].includes(key)) return
      this._freeLookKeys.add(key)
    },

    _onFreeLookKeyUp(event) {
      if (event.code === 'Space') {
        this._freeLookKeys.delete(' ')
        this._freeLookJumpConsumed = false
        return
      }
      if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
        this._freeLookKeys.delete('shift')
        return
      }
      const key = event.key.toLowerCase()
      if (!['w', 'a', 's', 'd'].includes(key)) return
      this._freeLookKeys.delete(key)
    },

    _onPointerUnlock() {
      this.disableFreeLook()
    },

    _updateFreeLookMovement(deltaSeconds = 1 / 60) {
      if (!this.freeLookActive || !this._pointerControls) return

      const MAX_SPEED = 240       // in/s (~20 ft/s)
      const ACCEL = 1400          // in/s² — ground acceleration
      const AIR_ACCEL = 60        // in/s² — air acceleration (tweak to control mid-jump steering)
      const FRICTION = 10         // ground friction multiplier
      const GRAVITY = 900         // in/s²
      const JUMP_IMPULSE = 240    // in/s (~32-inch jump height)
      const BOB_FREQ = 24         // radians/s at full speed
      const BOB_AMP = 4.0         // inches of vertical bob
      const BOB_ROLL_AMP = 0.007  // radians of side-to-side roll at full speed

      const obj = this.threeCamera
      const vel = this._freeLookVelocity

      // --- Gravity ---
      if (!this._freeLookOnGround) {
        vel.vertical -= GRAVITY * deltaSeconds
      }

      // --- Jump (Space, only when grounded, requires re-press) ---
      if (this._freeLookKeys.has(' ') && this._freeLookOnGround && !this._freeLookJumpConsumed) {
        vel.vertical = JUMP_IMPULSE
        this._freeLookOnGround = false
        this._freeLookJumpConsumed = true
        this._freeLookLandDip = 0
        this._freeLookLandDipVel = 0
        this._playFreeLookSound('jump')
      }

      // --- Wish direction from WASD ---
      let wishF = 0, wishR = 0
      if (this._freeLookKeys.has('w')) wishF += 1
      if (this._freeLookKeys.has('s')) wishF -= 1
      if (this._freeLookKeys.has('a')) wishR -= 1
      if (this._freeLookKeys.has('d')) wishR += 1

      const wishLen = Math.sqrt(wishF * wishF + wishR * wishR)
      if (wishLen > 1) { wishF /= wishLen; wishR /= wishLen }
      const isMoving = wishLen > 0

      // --- Ground friction ---
      if (this._freeLookOnGround) {
        const spd = Math.sqrt(vel.forward * vel.forward + vel.right * vel.right)
        if (spd > 0) {
          const newSpd = Math.max(0, spd - spd * FRICTION * deltaSeconds)
          vel.forward *= newSpd / spd
          vel.right   *= newSpd / spd
        }
      }

      // --- Quake PM_Accelerate: add velocity in wish direction up to MAX_SPEED ---
      if (isMoving) {
        const accel = this._freeLookOnGround ? ACCEL : AIR_ACCEL
        const curInWish = vel.forward * wishF + vel.right * wishR
        const addSpd = Math.min(MAX_SPEED - curInWish, accel * deltaSeconds)
        if (addSpd > 0) {
          vel.forward += addSpd * wishF
          vel.right   += addSpd * wishR
        }
      }

      // --- Apply horizontal movement ---
      this._pointerControls.moveForward(vel.forward * deltaSeconds)
      this._pointerControls.moveRight(vel.right * deltaSeconds)

      // --- Apply vertical movement (physics Y is separate from visual Y) ---
      this._freeLookPhysicsY += vel.vertical * deltaSeconds

      // --- Ground detection ---
      if (this._freeLookPhysicsY <= this._freeLookFloorY) {
        const wasAirborne = !this._freeLookOnGround
        if (wasAirborne && vel.vertical < -100) {
          this._playFreeLookSound('land')
          // Kick the land-dip spring proportional to landing speed
          this._freeLookLandDipVel = -Math.min(Math.abs(vel.vertical) * 0.3, 70)
        }
        this._freeLookPhysicsY = this._freeLookFloorY
        vel.vertical = 0
        this._freeLookOnGround = true
      }

      // --- Land-dip spring (runs always so it can recover after landing) ---
      if (this._freeLookLandDip !== 0 || this._freeLookLandDipVel !== 0) {
        const SPRING_K = 140   // stiffness — higher = faster snap-back
        const SPRING_D = 16    // damping — lower = more bounce, higher = more dead-stop
        this._freeLookLandDipVel += (-this._freeLookLandDip * SPRING_K - this._freeLookLandDipVel * SPRING_D) * deltaSeconds
        this._freeLookLandDip += this._freeLookLandDipVel * deltaSeconds
        // Clamp out when effectively at rest
        if (Math.abs(this._freeLookLandDip) < 0.05 && Math.abs(this._freeLookLandDipVel) < 0.05) {
          this._freeLookLandDip = 0
          this._freeLookLandDipVel = 0
        }
      }

      // --- Camera bob ---
      const hSpeed = Math.sqrt(vel.forward * vel.forward + vel.right * vel.right)
      const targetAmp = Math.min(hSpeed / MAX_SPEED, 1)

      // Smoothly lerp amplitude: quick ramp-up, slow decay so bob settles naturally
      const lerpRate = targetAmp > this._freeLookBobAmplitude ? 10 : 3.5
      this._freeLookBobAmplitude += (targetAmp - this._freeLookBobAmplitude) * Math.min(1, deltaSeconds * lerpRate)
      const bobAmp = this._freeLookBobAmplitude

      if (bobAmp > 0.005) {
        const prevBobTime = this._freeLookBobTime
        // Keep time advancing even while settling so the sine cycle completes rather than freezing mid-wave
        this._freeLookBobTime += deltaSeconds * BOB_FREQ * Math.max(bobAmp, 0.15)

        // Trigger footstep at the trough (phase = π, foot hits floor) each full cycle
        if (bobAmp > 0.1) {
          const prevSteps = Math.floor((prevBobTime - Math.PI) / (Math.PI * 2))
          const currSteps = Math.floor((this._freeLookBobTime - Math.PI) / (Math.PI * 2))
          if (currSteps > prevSteps && this._freeLookOnGround) {
            this._playFreeLookSound('footstep')
          }
        }
      }

      // Crouch — smoothly lerp eye down 21" (57" standing → 36" crouching)
      const CROUCH_DROP = 21
      const crouchTarget = this._freeLookKeys.has('shift') ? -CROUCH_DROP : 0
      this._freeLookCrouchOffset += (crouchTarget - this._freeLookCrouchOffset) * Math.min(1, deltaSeconds * 10)

      // Apply visual offsets: bob + land dip when grounded, raw physics Y when airborne
      if (this._freeLookOnGround) {
        // Weighted bob: ease-in down (accelerates into impact), ease-in up (lifts slowly)
        // Phase [0, π]: descent 0 → -1 with quadratic ease-in
        // Phase [π, 2π]: ascent -1 → 0 with quadratic ease-in
        const phase = ((this._freeLookBobTime % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)
        const p = phase / Math.PI
        const shapedBob = phase < Math.PI ? -(p * p) : (-1 + (p - 1) * (p - 1))
        const bobY = shapedBob * BOB_AMP * bobAmp
        obj.position.y = this._freeLookFloorY + bobY + this._freeLookLandDip + this._freeLookCrouchOffset
      } else {
        obj.position.y = this._freeLookPhysicsY + this._freeLookCrouchOffset
      }

      // Apply roll bob via YXZ Euler so it doesn't corrupt PointerLockControls' yaw/pitch state
      const bobRoll = Math.sin(this._freeLookBobTime * 0.5) * BOB_ROLL_AMP * bobAmp
      const rollEuler = new THREE.Euler().setFromQuaternion(this.threeCamera.quaternion, 'YXZ')
      rollEuler.z = bobRoll
      this.threeCamera.quaternion.setFromEuler(rollEuler)
    },

    _playFloorImpactNoise(ctx, destination, now, gain) {
      const bufLen = Math.floor(ctx.sampleRate * 0.07)
      const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate)
      const data = buf.getChannelData(0)
      for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1
      const src = ctx.createBufferSource()
      src.buffer = buf
      const filter = ctx.createBiquadFilter()
      filter.type = 'bandpass'
      filter.frequency.value = 180
      filter.Q.value = 0.8
      const gainNode = ctx.createGain()
      gainNode.gain.setValueAtTime(gain, now)
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.07)
      src.connect(filter)
      filter.connect(gainNode)
      gainNode.connect(destination)
      src.start(now)
      src.stop(now + 0.08)
    },

    _initFreeLookAudio() {
      if (this._freeLookAudioCtx) return
      try {
        const AudioCtx = window.AudioContext || /** @type {any} */ (window).webkitAudioContext
        this._freeLookAudioCtx = new AudioCtx()
      } catch (_) { /* audio not available */ }
    },

    _playFreeLookSound(type) {
      const ctx = this._freeLookAudioCtx
      if (!ctx) return
      if (ctx.state === 'suspended') ctx.resume()

      const now = ctx.currentTime
      const masterGain = ctx.createGain()
      masterGain.connect(ctx.destination)

      if (type === 'footstep') {
        this._playFloorImpactNoise(ctx, masterGain, now, 0.18)
      } else if (type === 'jump') {
        // Grunt: low triangle (effort tone, no buzz) + breathy noise layer
        const dur = 0.18
        // Tonal layer — triangle falls from 110 Hz → 65 Hz (vocal effort)
        const osc = ctx.createOscillator()
        osc.type = 'triangle'
        osc.frequency.setValueAtTime(110, now)
        osc.frequency.exponentialRampToValueAtTime(65, now + dur)
        const oscGain = ctx.createGain()
        oscGain.gain.setValueAtTime(0.18, now)
        oscGain.gain.exponentialRampToValueAtTime(0.001, now + dur)
        osc.connect(oscGain)
        oscGain.connect(masterGain)
        osc.start(now)
        osc.stop(now + dur)
        // Breath layer — bandpass noise for breathiness
        const bLen = Math.floor(ctx.sampleRate * dur)
        const bBuf = ctx.createBuffer(1, bLen, ctx.sampleRate)
        const bData = bBuf.getChannelData(0)
        for (let i = 0; i < bLen; i++) bData[i] = Math.random() * 2 - 1
        const bSrc = ctx.createBufferSource()
        bSrc.buffer = bBuf
        const bFilter = ctx.createBiquadFilter()
        bFilter.type = 'bandpass'
        bFilter.frequency.value = 900
        bFilter.Q.value = 1.2
        const bGain = ctx.createGain()
        bGain.gain.setValueAtTime(0.08, now)
        bGain.gain.exponentialRampToValueAtTime(0.001, now + dur)
        bSrc.connect(bFilter)
        bFilter.connect(bGain)
        bGain.connect(masterGain)
        bSrc.start(now)
        bSrc.stop(now + dur)
        // Floor push-off layer
        this._playFloorImpactNoise(ctx, masterGain, now, 0.28)
        masterGain.gain.setValueAtTime(1, now)
      } else if (type === 'land') {
        // Sub-bass punch + noise body + louder footstep layer
        const dur = 0.25
        // Footstep layer (louder than normal)
        this._playFloorImpactNoise(ctx, masterGain, now, 0.38)
        // Sine drop: 130 Hz → 45 Hz
        const osc = ctx.createOscillator()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(130, now)
        osc.frequency.exponentialRampToValueAtTime(45, now + 0.08)
        const oscGain = ctx.createGain()
        oscGain.gain.setValueAtTime(0.22, now)
        oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12)
        osc.connect(oscGain)
        oscGain.connect(masterGain)
        osc.start(now)
        osc.stop(now + 0.13)
        // Noise body
        const nLen = Math.floor(ctx.sampleRate * dur)
        const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate)
        const nData = nBuf.getChannelData(0)
        for (let i = 0; i < nLen; i++) nData[i] = Math.random() * 2 - 1
        const nSrc = ctx.createBufferSource()
        nSrc.buffer = nBuf
        const nFilter = ctx.createBiquadFilter()
        nFilter.type = 'lowpass'
        nFilter.frequency.value = 130
        const nGain = ctx.createGain()
        nGain.gain.setValueAtTime(0.32, now)
        nGain.gain.exponentialRampToValueAtTime(0.001, now + dur)
        nSrc.connect(nFilter)
        nFilter.connect(nGain)
        nGain.connect(masterGain)
        nSrc.start(now)
        nSrc.stop(now + dur)
        masterGain.gain.setValueAtTime(1, now)
      }
    },

    // ------------------------------------------------------------------
    // Export
    // ------------------------------------------------------------------

    /**
     * Export a uniform thumbnail of the 3D canvas.
     * Uses the same aspect and perpendicular framing as live view, but may pull the camera
     * farther back so the full outer frame fits (live view can clip when standard scale is tight).
     * @param {number} [maxDim=800] - Maximum dimension in pixels
     * @returns {string} data URL
     */
    exportThumbnail(maxDim = 800) {
      if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return null

      const target = this.computeCameraTarget(this.cameraMode)
      if (!target) return null
      const exportAspect = THUMBNAIL_EXPORT_ASPECT

      const outerSize = this.getOuterSceneSize()
      const { sceneW: outerW, sceneH: outerH } = outerSize || {}
      const fov = this.threeCamera.fov
      const projectionFrontZ = this.getProjectionFrontZ()
      const minZForFullOuterFrame =
        Number.isFinite(outerW) && outerW > 0 && Number.isFinite(outerH) && outerH > 0
          ? computeFitDistance(outerW, outerH, fov, exportAspect, THUMBNAIL_EXPORT_OUTER_PADDING) + projectionFrontZ
          : target.targetZ
      const exportTargetZ = Math.max(target.targetZ, minZForFullOuterFrame)

      // Render at a fixed 4x internal resolution, then downscale the cropped
      // result back to ~maxDim (keeps thumbnails sharp).
      const renderScale = 4
      let thumbW, thumbH
      if (exportAspect > 1) {
        thumbW = maxDim * renderScale
        thumbH = Math.round((maxDim * renderScale) / exportAspect)
      } else {
        thumbH = maxDim * renderScale
        thumbW = Math.round((maxDim * renderScale) * exportAspect)
      }

      // Save current state
      const container = this.$refs.container
      const rect = container.getBoundingClientRect()
      const origW = rect.width || 400
      const origH = rect.height || 400
      const origAspect = this.threeCamera.aspect
      const origPosition = this.threeCamera.position.clone()
      const origQuaternion = this.threeCamera.quaternion.clone()
      const origPixelRatio = this.threeRenderer.getPixelRatio()
      const origRendererSize = new THREE.Vector2()
      this.threeRenderer.getSize(origRendererSize)
      const activePos = this.getActiveScenePosition()

      // Temporarily adjust camera and renderer for thumbnail
      this.threeCamera.aspect = exportAspect
      this.threeCamera.position.x = activePos.x
      this.threeCamera.position.y = activePos.y
      this.threeCamera.position.z = exportTargetZ
      this.threeCamera.lookAt(activePos.x, activePos.y, projectionFrontZ)
      this.threeCamera.updateProjectionMatrix()

      // Force pixel ratio to 1 so the output is exactly maxDim pixels
      this.threeRenderer.setPixelRatio(1)
      // Use false to prevent CSS layout shifts
      this.threeRenderer.setSize(thumbW, thumbH, false)

      // Hide decor plant for thumbnail exports (scale reference only; not part of product image).
      const decor = this._decorObject
      const decorWasVisible = decor ? decor.visible !== false : null
      if (decor) decor.visible = false

      this._suppressFlipForThumbnail = true
      this.applySelectedItemPose()
      this.syncInteractionGroupTransform()

      try {
        // Render and capture
        this.threeRenderer.render(this.threeScene, this.threeCamera)
      } finally {
        this._suppressFlipForThumbnail = false
        this.applySelectedItemPose()
        this.syncInteractionGroupTransform()
        if (decor) decor.visible = decorWasVisible
      }

      // Crop to the projected frame bounds so saved thumbnails don't include
      // excessive viewport background. Use export camera distance so the crop matches
      // the frame extents after pulling back to fit the full outer frame.
      let dataURL
      const displayTarget = {
        renderW: thumbW,
        renderH: thumbH,
        targetZ: exportTargetZ,
        aspect: exportAspect,
      }
      const displaySize = this.getDisplaySizeForTarget(displayTarget, outerSize, projectionFrontZ)
      if (displaySize && thumbW > 0 && thumbH > 0) {
        const projectedW = Math.max(1, Math.round(displaySize.width))
        const projectedH = Math.max(1, Math.round(displaySize.height))

        const cropW = Math.max(1, Math.min(thumbW, projectedW))
        const cropH = Math.max(1, Math.min(thumbH, projectedH))
        const cropX = Math.max(0, Math.round((thumbW - cropW) / 2))
        const cropY = Math.max(0, Math.round((thumbH - cropH) / 2))

        const cropLongest = Math.max(cropW, cropH)
        const downscale = cropLongest > maxDim ? (maxDim / cropLongest) : 1
        const outW = Math.max(1, Math.round(cropW * downscale))
        const outH = Math.max(1, Math.round(cropH * downscale))

        const outCanvas = document.createElement('canvas')
        outCanvas.width = outW
        outCanvas.height = outH
        const outCtx = outCanvas.getContext('2d')
        if (outCtx) {
          outCtx.drawImage(
            this.threeRenderer.domElement,
            cropX,
            cropY,
            cropW,
            cropH,
            0,
            0,
            outW,
            outH
          )
          dataURL = outCanvas.toDataURL('image/jpeg', 0.9)
        }
      }

      if (!dataURL) {
        dataURL = this.threeRenderer.domElement.toDataURL('image/jpeg', 0.9)
      }

      // Restore original state
      this.threeCamera.aspect = origAspect
      this.threeCamera.position.copy(origPosition)
      this.threeCamera.quaternion.copy(origQuaternion)
      this.threeCamera.updateProjectionMatrix()
      this.threeCamera.updateMatrixWorld()
      this.threeRenderer.setPixelRatio(origPixelRatio)
      this.threeRenderer.setSize(origRendererSize.x || target.renderW || origW, origRendererSize.y || target.renderH || origH, false)

      return dataURL
    },

    /**
     * Force an immediate rebuild and return a thumbnail data URL.
     * Designed for batch/headless use — cancels any pending debounced rebuild,
     * runs rebuildScene synchronously (silent), then calls exportThumbnail.
     * Call after setting all props and awaiting $nextTick in the parent.
     * @param {number} [maxDim=800]
     * @returns {Promise<string|null>} JPEG data URL
     */
    async generateThumbnail(maxDim = 800) {
      if (this.debouncedRebuild?.cancel) this.debouncedRebuild.cancel()
      await this.rebuildScene({ silent: true })
      return this.exportThumbnail(maxDim)
    },

    /**
     * Export the current render as a PNG data URL.
     * @param {number} [scale=2] - Resolution multiplier
     * @returns {string} data URL
     */
    exportDataURL(scale = 2) {
      if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return null

      const container = this.$refs.container
      const rect = container.getBoundingClientRect()
      const w = Math.round(rect.width * scale)
      const h = Math.round(rect.height * scale)

      // Temporarily resize renderer for high-res export
      this.threeRenderer.setSize(w, h)
      this.threeRenderer.render(this.threeScene, this.threeCamera)
      const dataURL = this.threeRenderer.domElement.toDataURL('image/png')

      // Restore original size
      this.threeRenderer.setSize(rect.width, rect.height)

      return dataURL
    },

    /**
     * Export as a Blob (for upload or download).
     * @param {number} [scale=2]
     * @returns {Promise<Blob>}
     */
    async exportBlob(scale = 2) {
      const dataURL = this.exportDataURL(scale)
      if (!dataURL) return null
      const res = await fetch(dataURL)
      return res.blob()
    },

    /**
     * Trigger a browser download of the rendered image.
     * @param {string} [filename='frame-render.png']
     * @param {number} [scale=2]
     */
    downloadImage(filename = 'frame-render.png', scale = 2) {
      const dataURL = this.exportDataURL(scale)
      if (!dataURL) return
      const link = document.createElement('a')
      link.href = dataURL
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.frame-renderer {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.frame-renderer__canvas-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  opacity: 1;
  transition: opacity 0.3s ease;
  pointer-events: auto;
}

.frame-renderer__canvas-wrapper--hidden {
  opacity: 0;
  pointer-events: none;
  /* Hide at once, reveal with the fade. Callers hide the canvas precisely
     because what it holds is about to stop being true — fading that out leaves
     a stale render on screen, and any camera move during the fade rescales it. */
  transition: none;
}

.frame-renderer canvas {
  display: block;
}

.frame-renderer__scroll-snap-overlay {
  position: absolute;
  inset: 0;
  z-index: 8;
  overflow-x: auto;
  overflow-y: auto;
  scroll-snap-type: both mandatory;
  overscroll-behavior: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

/* The turntable owns the pointer while it is up: spinning the piece and
   scrolling the camera between pieces are both drags over the same surface, and
   the overlay is on top. Inert rather than removed so the scroll position it is
   holding survives, and the camera is back where it was on the way out. */
.frame-renderer__scroll-snap-overlay--inert {
  pointer-events: none;
}

.frame-renderer__scroll-snap-overlay::-webkit-scrollbar {
  display: none;
}

.frame-renderer__scroll-snap-overlay--debug {
  background:
    linear-gradient(to right, rgba(0, 188, 212, 0.18) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(0, 188, 212, 0.10) 1px, transparent 1px);
  background-size: 40px 100%, 100% 40px;
  box-shadow: inset 0 0 0 1px rgba(0, 188, 212, 0.45);
}

.frame-renderer__scroll-snap-track {
  position: relative;
  min-width: 100%;
}

.frame-renderer__scroll-snap-grid {
  position: sticky;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 0;
  height: 100%;
  border-left: 2px solid rgba(255, 193, 7, 0.9);
  pointer-events: none;
  transform: translateX(-1px);
}

.frame-renderer__scroll-snap-cell {
  position: absolute;
  scroll-snap-align: center center;
  scroll-snap-stop: normal;
}

.frame-renderer__scroll-snap-overlay--debug .frame-renderer__scroll-snap-cell {
  background: rgba(0, 188, 212, 0.12);
  border-top: 1px solid rgba(0, 188, 212, 0.6);
  border-bottom: 1px solid rgba(0, 188, 212, 0.6);
  border-left: 1px solid rgba(0, 188, 212, 0.85);
  border-right: 1px solid rgba(0, 188, 212, 0.85);
}

.frame-renderer__scroll-snap-overlay--debug .frame-renderer__scroll-snap-cell::before,
.frame-renderer__scroll-snap-overlay--debug .frame-renderer__scroll-snap-cell::after {
  content: '';
  position: absolute;
}

.frame-renderer__scroll-snap-overlay--debug .frame-renderer__scroll-snap-cell::before {
  top: 0;
  bottom: 0;
  left: 50%;
  border-left: 2px solid rgba(0, 188, 212, 0.95);
  transform: translateX(-1px);
}

.frame-renderer__scroll-snap-overlay--debug .frame-renderer__scroll-snap-cell::after {
  left: 0;
  right: 0;
  top: 50%;
  border-top: 2px solid rgba(0, 188, 212, 0.65);
  transform: translateY(-1px);
}

.frame-renderer__scroll-snap-overlay--debug .frame-renderer__scroll-snap-cell--selected {
  background: rgba(255, 193, 7, 0.18);
  border-color: rgba(255, 193, 7, 0.95);
}

.frame-renderer__scroll-snap-overlay--debug .frame-renderer__scroll-snap-cell--active-mode {
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.55);
}

.frame-renderer__scroll-snap-label {
  position: absolute;
  top: 8px;
  left: 50%;
  max-width: calc(100% - 12px);
  padding: 2px 5px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 11px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
  transform: translateX(-50%);
}

.frame-renderer__lasso {
  position: absolute;
  pointer-events: none;
  border: 1px solid rgba(13, 110, 253, 0.6);
  background: rgba(13, 110, 253, 0.1);
  z-index: 10;
}

.frame-renderer__tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 0.75rem;
  padding: 3px 8px;
  border-radius: 3px;
  white-space: pre;
  text-align: right;
  z-index: 10;
  transform: translateY(-100%);
  font-variant-numeric: lining-nums tabular-nums;
}

.frame-renderer__crosshair {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 22px;
  height: 22px;
  z-index: 9;
  pointer-events: none;
  transform: translate(-50%, -50%);
  mix-blend-mode: difference;
}

.frame-renderer__crosshair::before,
.frame-renderer__crosshair::after {
  content: '';
  position: absolute;
  background: #fff;
}

.frame-renderer__crosshair::before {
  top: 50%;
  left: 3px;
  right: 3px;
  height: 1px;
  transform: translateY(-50%);
}

.frame-renderer__crosshair::after {
  top: 3px;
  bottom: 3px;
  left: 50%;
  width: 1px;
  transform: translateX(-50%);
}

.frame-renderer__loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  pointer-events: none;
}

.frame-renderer__render-stat {
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 12;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font: 500 11px/1.6 ui-monospace, SFMono-Regular, Menlo, monospace;
  pointer-events: none;
  white-space: nowrap;
}

.frame-renderer__render-stat .is-drawing { color: #6ee7a8; }
.frame-renderer__render-stat .is-idle { color: #f0b429; }

.frame-renderer__thumbnail {
  display: block;
}

/**
 * The overlay's two jobs want opposite stacking, which is what
 * `showTransitionOverlay`'s two terms already distinguish.
 *
 * Coming out of a view transition the canvas fades in *over* the thumbnail, so
 * the thumbnail has to sit under it — that fade is the effect. Covering an item
 * swap is the reverse: the canvas is live and mid-rebuild underneath, and the
 * thumbnail is the only thing standing in for the piece, so it has to be on
 * top. The canvas wrapper is z-index 2; raise the overlay past it, but only
 * while it is covering.
 */
.frame-renderer__thumbnail-overlay--covering {
  z-index: 3;
}

.canvas-fade-enter-active {
  transition: opacity 0.3s ease;
}

.canvas-fade-leave-active {
  transition: none;
}

.canvas-fade-enter,
.canvas-fade-leave-to {
  opacity: 0;
}
</style>
